import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { openPaddleCheckout, getPaddle } from "@/lib/paddle";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import LangSwitch from "@/components/LangSwitch";
import Logo from "@/components/Logo";
import { ArrowLeft, ArrowRight, Buildings, TreeEvergreen, Users, ShieldCheck, Sparkle, Lightning, Drop, Recycle, ArrowCircleLeft } from "@phosphor-icons/react";
import ImageUploader from "@/components/ImageUploader";
import GenerationProgress from "@/components/GenerationProgress";
import {
  SECTORS as SECTOR_DEFS,
  COUNTRIES as COUNTRY_DEFS,
  SIZES as SIZE_DEFS,
  getSectors, getCountries, getSizes, getCurrencies, getCeoRoles,
  DQ_OPTIONS,
} from "@/lib/presets";
import { toArabicDigits, formatNum, formatPct } from "@/lib/numFormat";

// Backend-facing English VALUE lists (unchanged for storage compatibility)
const SECTORS = SECTOR_DEFS.map(s => s.value);
const COUNTRIES = COUNTRY_DEFS.map(c => c.value);
const SIZES = SIZE_DEFS.map(s => s.value);
const GRIDS = { "UAE": 0.45, "Saudi Arabia": 0.52, "Qatar": 0.47, "Kuwait": 0.50, "Oman": 0.48, "Bahrain": 0.51, "Other Country": 0.48 };

const Field = ({ label, hint, children, full }) => (
  <div className={`flex flex-col gap-1.5 ${full ? "col-span-2" : ""}`}>
    <label className="font-['Outfit'] text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-400">
      {label}
      {hint && <span className="block text-[9px] font-normal tracking-normal normal-case text-slate-500 mt-0.5">{hint}</span>}
    </label>
    {children}
  </div>
);

// Chrome aggressively tries to classify fields and may offer to "Save driving
// licence" when it sees a numeric input near a category dropdown (e.g. year +
// sector). `autoComplete="off"` is explicitly ignored by Chrome. The only
// values Chrome respects for *not* autofilling are "new-password" and some
// non-standard strings — "new-password" is the documented one. We also set
// data-form-type="other" and data-lpignore="true" to shut up LastPass /
// 1Password heuristics.
const Input = ({ testId, ...props }) => (
  <input data-testid={testId} autoComplete="new-password" autoCorrect="off" autoCapitalize="off" spellCheck="false" data-form-type="other" data-lpignore="true" {...props}
    className="w-full bg-[#0B0C10] border border-slate-700 text-slate-100 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#01AF9B] focus:border-transparent outline-none transition-all placeholder:text-slate-600" />
);

const Select = ({ testId, children, ...props }) => (
  <select data-testid={testId} autoComplete="new-password" data-form-type="other" data-lpignore="true" {...props}
    className="w-full bg-[#0B0C10] border border-slate-700 text-slate-100 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#01AF9B] focus:border-transparent outline-none transition-all cursor-pointer"
    style={{ WebkitAppearance: "menulist", appearance: "auto" }}>
    {children}
  </select>
);

const TextArea = ({ testId, ...props }) => (
  <textarea data-testid={testId} autoComplete="new-password" autoCorrect="off" spellCheck="false" data-form-type="other" data-lpignore="true" {...props}
    className="w-full bg-[#0B0C10] border border-slate-700 text-slate-100 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#01AF9B] focus:border-transparent outline-none transition-all placeholder:text-slate-600 resize-y min-h-[80px]" />
);

// Shows a small AR-only hint under free-text TextAreas: tells the user they can
// type in Arabic because the backend auto-translates user-entered fields into
// the opposite language when the opposite-language PDF is generated.
const AnyLangHint = ({ lang }) => (
  lang === "ar" ? (
    <div className="mt-1 flex items-start gap-1.5 text-[11px] text-[#01AF9B]/80 leading-snug">
      <span>ℹ️</span>
      <span>يمكنك الكتابة بأي لغة. سيتم تكييف النص تلقائياً عند إنشاء التقرير باللغة الإنجليزية.</span>
    </div>
  ) : null
);

const DataQualityBlock = ({ value, onChange, sectionName, lang = "en" }) => {
  const T = (en, ar) => lang === "ar" ? ar : en;
  const localized = DQ_OPTIONS.map(opt => ({
    v: opt.v,
    label: T(opt.en, opt.ar),
    desc: T(opt.desc_en, opt.desc_ar),
  }));
  return (
  <div className="mt-6 p-4 rounded-xl border border-amber-500/30 bg-amber-500/5">
    <div className="flex items-start gap-2 mb-2">
      <span className="text-amber-400 text-sm font-bold">▶</span>
      <div>
        <div className="font-['Outfit'] text-sm font-bold text-amber-200">{T("Data Quality Attestation", "إقرار جودة البيانات")}, {sectionName}</div>
        <div className="text-xs text-slate-400 mt-0.5">{T("How confident are you in the figures you entered for this section? (GRI 2-5)", "ما مدى ثقتك في الأرقام التي أدخلتها لهذا القسم؟ (GRI 2-5)")}</div>
      </div>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3">
      {localized.map(opt => (
        <label key={opt.v} className={`flex flex-col cursor-pointer rounded-lg border px-3 py-2.5 text-xs transition-all ${value === opt.v ? "border-[#01AF9B] bg-[#01AF9B]/10" : "border-slate-700 bg-[#0B0C10] hover:border-slate-500"}`}>
          <input type="radio" className="sr-only" checked={value === opt.v} onChange={() => onChange(opt.v)} data-testid={`dq-${sectionName.toLowerCase()}-${opt.v}`} />
          <span className={`font-['Outfit'] font-bold text-sm ${value === opt.v ? "text-[#01AF9B]" : "text-slate-200"}`}>{opt.label}</span>
          <span className="text-[11px] text-slate-500 mt-0.5">{opt.desc}</span>
        </label>
      ))}
    </div>
  </div>
  );
};

function InjuryConflictAlert({ lang, soc, legacySum, onUseMonthly }) {
  const ar = lang === "ar";
  const vals = Object.values(soc.monthly_lti_count || {}).filter(v => v !== "" && v !== null);
  const formSum = vals.length ? vals.reduce((a, v) => a + (parseFloat(v) || 0), 0) : null;
  const monthly = formSum !== null ? formSum : legacySum;
  const annual = soc.lti_count === "" || soc.lti_count === undefined ? null : parseFloat(soc.lti_count);
  if (monthly === null || annual === null || Number.isNaN(annual) || monthly === annual) return null;
  return (
    <div data-testid="injury-conflict-alert" className="md:col-span-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      <p className="font-semibold">{ar ? "تعارض في بيانات الإصابات" : "Injury data conflict"}</p>
      <p className="mt-1">{ar
        ? `مجموع الإصابات الشهرية (${monthly}) لا يطابق الإجمالي السنوي (${annual}). سيستخدم التقرير المجموع الشهري (${monthly}). يرجى تحديث الإجمالي السنوي ليتطابق أو تصحيح القيم الشهرية.`
        : `Your monthly injuries add up to ${monthly}, but the annual total says ${annual}. The report uses the monthly total (${monthly}). Please align the annual figure or correct the monthly values.`}</p>
      <button type="button" data-testid="injury-conflict-use-monthly" onClick={() => onUseMonthly(monthly)}
        className="mt-2 rounded-md bg-amber-600 px-3 py-1 text-xs font-semibold text-white hover:bg-amber-700 transition-colors">
        {ar ? `استخدم المجموع الشهري (${monthly})` : `Use monthly total (${monthly})`}
      </button>
    </div>
  );
}

export default function ReportBuilder() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { lang, t } = useLang();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [reportId, setReportId] = useState(id || null);
  const [preflightBlocked, setPreflightBlocked] = useState(false);

  // Listen for CoherenceBadge's block/unblock signal (dispatched on the
  // Generate step whenever hard input contradictions are detected).
  useEffect(() => {
    const handler = (e) => setPreflightBlocked(!!e?.detail?.blocked);
    window.addEventListener("coherence:blocked", handler);
    return () => window.removeEventListener("coherence:blocked", handler);
  }, []);

  // Scroll to top whenever the wizard step changes so the new page starts
  // at the top instead of wherever the previous step left the scrollbar.
  // Feb 2026 fix — Next/Prev buttons used to leave the user staring at
  // the bottom of the fresh step.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  // Company Data
  const [company, setCompany] = useState({ name: "", sector: "", year: "2025", country: "UAE", size: "11-50", ceo: "", report_writer_name: "", website: "", description: "", leadership_statement: "", leadership_title: "CEO", contact_email: "", restatements: "", external_assurance: "", currency: "AED", cover_color: "#73324B" });
  // Environmental Data, Single Source of Truth (SSOT) model.
  // ALL downstream values (scope1/2/3, energy total, renewable %, intensity)
  // are AUTO-DERIVED from the primary meter inputs below. Users only enter
  // each figure ONCE. Manual override is available per-field via the
  // `<field>_manual` flag (true = respect user's typed value, don't overwrite).
  const [env, setEnv] = useState({
    // ── Primary meter inputs (SSOT) ─────────────────────────────
    // Scope 1 fuels
    diesel_litres: "", petrol_litres: "", natural_gas_m3: "", lpg_kg: "",
    // Scope 2 electricity + district cooling
    electricity_kwh: "", district_cooling_rt_hrs: "",
    // On-site renewable
    renewable_generated_kwh: "",
    // Optional commissioning date (month/year or ISO) — displayed under
    // the Energy Source Breakdown so partial-year yields read correctly.
    solar_commissioning_date: "",
    // GHG Protocol Scope 2 Guidance, market-based dual reporting.
    // If the user has procured green electricity (I-RECs, PPAs, on-site
    // solar retirements), enter the kWh amount claimed here. Leave blank
    // if not yet procured, the market-based Scope 2 figure will simply be
    // omitted from the PDF instead of fabricated.
    renewable_electricity_kwh: "",
    // Optional. Residual-mix emission factor for the non-green kWh
    // (kgCO₂e/kWh). If left blank, we fall back to the country grid
    // factor from company_data.grid_emission_factor.
    residual_mix_factor: "",
    // Water & waste
    water: "", water_recycled_pct: "", waste: "", landfill_pct: "",
    // Optional monthly breakdown panels (12-month {jan..dec}), if ANY
    // month is filled, ALL 12 must be filled (validated by gate).
    monthly_electricity_kwh: { jan:"", feb:"", mar:"", apr:"", may:"", jun:"", jul:"", aug:"", sep:"", oct:"", nov:"", dec:"" },
    monthly_water_m3:        { jan:"", feb:"", mar:"", apr:"", may:"", jun:"", jul:"", aug:"", sep:"", oct:"", nov:"", dec:"" },

    // ── Feb 2026 additive: optional kWh alternative for natural gas ─
    // Users may report gas in kWh (utility bill) instead of m³. When set,
    // backend converts kWh → m³ using 10.55 kWh/m³ (DEFRA net calorific
    // value) and treats natural_gas_m3 as authoritative once derived.
    natural_gas_kwh: "",

    // ── Auto-derived values (write-only from the derivation effects) ──
    // Kept as strings so the existing PDF/canonical code needs zero change.
    scope1: "", scope2: "", scope3: "", energy: "", renewable_pct: "", energy_intensity: "",

    // ── Manual override flags (default false → auto rules apply) ───────
    scope1_manual: false, scope2_manual: false, scope3_manual: false,
    energy_manual: false, renewable_pct_manual: false,
    electricity_kwh_manual: false, water_manual: false,

    // ── Narrative fields ───────────────────────────────────────
    reduction_target: "", climate_risk: "", initiatives: "",
    // ── Optional energy transition target — baseline year + value are
    // auto-derived server-side from prev_scope* / prev_energy fields
    // (single source of truth) to prevent user-typed duplicates from
    // disagreeing with the report data. Only target year + % are asked.
    energy_target_year: "", energy_target_pct: "",
    // Certifications
    env_certifications: "", biodiversity_statement: "",
    // Climate alignment (1.5°C / 2°C pathway), TCFD recommendation
    climate_alignment_status: "",
    // Data quality attestation for this section
    data_quality: "estimated",

    // ── Previous year (Y-1), annual figures only (per user request) ──
    prev_scope1: "", prev_scope2: "", prev_scope3: "", prev_energy: "", prev_water: "", prev_waste: "", prev_renewable_pct: "",
    // Two years ago (Y-2), optional annual figures
    prev2_scope1: "", prev2_scope2: "", prev2_scope3: "", prev2_energy: "",
    prev2_water: "", prev2_waste: "", prev2_renewable_pct: ""
  });
  // Social Data, same SSOT + monthly-panel model as Environmental.
  // Monthly panels auto-derive: average(headcount) → total_employees,
  // sum(lti_count) → lti_count, sum(working_hours) → total_working_hours,
  // and LTIR = lti × 1,000,000 / total_working_hours.
  const [legacyMonthlyInjuries, setLegacyMonthlyInjuries] = useState(null);
  const [soc, setSoc] = useState({
    total_employees: "", female_pct: "", local_pct: "", training_hours: "", injury_rate: "", csr_spend: "", supply_chain: "", wellbeing: "", hr_policies: "",
    // Workforce Demographics
    senior_mgmt_male: "", senior_mgmt_female: "", middle_mgmt_male: "", middle_mgmt_female: "",
    technical_male: "", technical_female: "", admin_male: "", admin_female: "",
    age_under30: "", age_30to50: "", age_over50: "",
    // Hiring & Turnover
    // ─── Feb 2026 UX simplification ────────────────────────────────
    // User feedback: the old 6-field breakdown (M/F hires × M/F leavers ×
    // current/prev turnover rate %) was confusing and left "turnover rate"
    // ambiguous. We now ask ONLY for two integer totals, `new_hires`
    // (total headcount hired this year) and `departures` (total who left)
    //, and auto-compute the turnover rate from GRI 401-1b:
    //   turnover_rate % = departures ÷ average_headcount × 100
    // The gender split fields remain for optional GRI 401-1a disclosure
    // (collapsed by default under "Add gender split (optional)").
    new_hires: "", departures: "",
    new_hires_male: "", new_hires_female: "", turnover_male: "", turnover_female: "", turnover_rate: "",
    // Parental Leave
    parental_taken_male: "", parental_taken_female: "", parental_returned_male: "", parental_returned_female: "",
    // Training & Reviews
    performance_review_pct: "", training_programs: "",
    // H&S expanded
    total_working_hours: "", fatalities: "", contractor_injuries: "", h_s_grievances: "",
    // NEW: LTI incident count (feeds LTIR calculation)
    lti_count: "",
    // ── Monthly breakdown panels (all optional; all-or-none per user rule) ──
    monthly_headcount:      { jan:"", feb:"", mar:"", apr:"", may:"", jun:"", jul:"", aug:"", sep:"", oct:"", nov:"", dec:"" },
    monthly_lti_count:      { jan:"", feb:"", mar:"", apr:"", may:"", jun:"", jul:"", aug:"", sep:"", oct:"", nov:"", dec:"" },
    monthly_working_hours:  { jan:"", feb:"", mar:"", apr:"", may:"", jun:"", jul:"", aug:"", sep:"", oct:"", nov:"", dec:"" },
    // ── Manual override flags for derived fields ─────────────────
    total_employees_manual: false, total_working_hours_manual: false,
    lti_count_manual: false, injury_rate_manual: false,
    // ── Previous-year LTIR is now DERIVED from LTI incidents & hours ─
    // Same SSOT logic as the current year, users enter incidents (which
    // they know), not a computed rate. Working hours defaults to
    // prev_total_employees × 2,080 h/yr unless overridden.
    prev_lti_count: "", prev_total_working_hours: "",
    prev_working_hours_manual: false, prev_injury_rate_manual: false,
    // Policies
    human_rights_policy: "", child_labor_policy: "", grievance_mechanism: "", gender_pay_ratio: "",
    // ── Feb 2026 additive: optional ethnicity / cultural-diversity ratio
    // for geographies where nationalization % is not the relevant lens.
    cultural_diversity_pct: "",
    // Optional contractor coverage disclosures (GRI 2-8).
    contractor_headcount: "", contractor_training_pct: "",
    // Previous Year
    prev_total_employees: "", prev_female_pct: "", prev_injury_rate: "", prev_training_hours: "", prev_turnover_rate: "",
    // Community
    community_projects: "",
    // Data quality attestation for this section
    data_quality: "estimated",
    // Key Suppliers
    key_suppliers: [
      { name: "", activity: "", esg_compliant: "" },
      { name: "", activity: "", esg_compliant: "" },
      { name: "", activity: "", esg_compliant: "" },
      { name: "", activity: "", esg_compliant: "" },
      { name: "", activity: "", esg_compliant: "" },
      { name: "", activity: "", esg_compliant: "" },
      { name: "", activity: "", esg_compliant: "" },
      { name: "", activity: "", esg_compliant: "" }
    ]
  });
  // Governance Data
  const [gov, setGov] = useState({
    board_size: "", board_female_pct: "", independent_pct: "", anti_corruption: "", whistleblower: "", data_protection: "", code_of_conduct: "", esg_committee: "", improvement_plan: "",
    // Board Details, structured (Phase 6 architectural). `board_members_structured`
    // is the source of truth (array of {name, role, gender, independent}). The
    // legacy free-text `board_members` string is auto-generated from it so the
    // backend / LLM prompt / older reports keep working unchanged.
    board_members_structured: [],
    board_members: "", ceo_chair_separation: "", board_committees: "", board_meeting_frequency: "",
    // Compliance
    abac_training_pct: "", compliance_incidents: "", legal_proceedings: "", supplier_code_of_conduct: "",
    // Feb 2026 audit fix — the free supplier % inputs (supplier_code_coverage,
    // total_critical_suppliers, pct_esg_assessed, pct_esg_compliant) were
    // removed. The Key Suppliers table (soc.key_suppliers[]) is the SSOT
    // for supplier ESG compliance %; the server derives it as
    // (compliant rows / filled rows × 100).
    // Risk & Ethics
    conflicts_of_interest_policy: "", third_party_due_diligence: "", industry_memberships: "", risk_categories: "",
    // Data quality attestation for this section
    data_quality: "estimated",
    // Previous Year
    prev_board_female_pct: "", prev_independent_pct: "", prev_board_size: ""
  });

  // Scope 3 converter helpers (only fields NOT already covered by the SSOT
  // env inputs, flights + commute). Landfill waste for Scope 3 is derived
  // from env.waste × env.landfill_pct, so no separate input needed here.
  const [s3FlyS, setS3FlyS] = useState("");
  const [s3FlyL, setS3FlyL] = useState("");
  const [s3Commute, setS3Commute] = useState("");
  const [eiRevenue, setEiRevenue] = useState("");

  // Load existing report
  useEffect(() => {
    if (id) {
      api.getReport(id).then(doc => {
        if (doc.company_data) setCompany(c => ({ ...c, ...doc.company_data }));
        if (doc.environmental_data) setEnv(e => ({ ...e, ...doc.environmental_data }));
        if (doc.social_data) setSoc(s => ({ ...s, ...doc.social_data }));
        const _legacyInj = doc.monthly_data?.injuries;
        if (Array.isArray(_legacyInj) && _legacyInj.some(v => v !== null && v !== "")) {
          setLegacyMonthlyInjuries(_legacyInj.reduce((a, v) => a + (parseFloat(v) || 0), 0));
        }
        if (doc.governance_data) setGov(g => ({ ...g, ...doc.governance_data }));
      }).catch(console.error);
    }
  }, [id]);

  // Prefill the GRI 2-3 contact email from the user's account email when blank.
  // SMEs rarely have a dedicated esg@ mailbox; using their real account email
  // is honest and editable (they can paste a dedicated address before generating).
  useEffect(() => {
    if (user?.email && !company.contact_email) {
      setCompany(c => c.contact_email ? c : { ...c, contact_email: user.email });
    }
  }, [user, company.contact_email]);

  // ─────────────────────────────────────────────────────────────────
  // Eagerly create a stub report on FIRST mount of the wizard when the
  // user landed via "New Report" (no `id` in the URL). Without this,
  // step 0 (Company Profile) renders BEFORE any auto-save has fired,
  // so `reportId` is still null and every ImageUploader on the page
  // shows "Save the report first to enable image uploads" — the user
  // has to navigate to step 1 and back for the upload buttons to
  // finally appear. Feb 2026 fix.
  // ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (id) return;         // editing existing draft, reportId already loaded
    if (reportId) return;   // stub already created (guards against double-mount in StrictMode)
    let cancelled = false;
    (async () => {
      try {
        const res = await api.createReport({
          company_data: company,
          environmental_data: env,
          social_data: soc,
          governance_data: gov,
          language: lang,
        });
        if (cancelled || !res || !res._id) return;
        setReportId(res._id);
        // Sticky URL so a browser refresh restores the form instead of
        // losing everything the user typed.
        navigate(`/report/${res._id}/edit`, { replace: true });
      } catch (e) {
        // If the create is blocked (e.g. freemium already_used), fall
        // back to the existing on-step-change save path — the user
        // will still see the paywall when they hit "Next".
        console.warn("Eager stub-report create skipped:", e?.response?.data || e?.message);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ═══════════════════════════════════════════════════════════════════
  // AUTO-DERIVATION (SSOT → downstream), respects `<field>_manual` flags
  // ═══════════════════════════════════════════════════════════════════

  // Board members structured table → derived KPIs.
  // The structured table is the SINGLE SOURCE OF TRUTH once populated:
  // board_size, female count, independent count, and the free-text
  // board_members string all flow from it. This kills the whole class of
  // "40% female claimed but board list shows only men" contradictions.
  useEffect(() => {
    const rows = Array.isArray(gov.board_members_structured) ? gov.board_members_structured : [];
    if (rows.length === 0) return; // free-text mode; leave manual fields untouched
    const valid = rows.filter(r => (r?.name || "").trim());
    const total = valid.length;
    const female = valid.filter(r => (r?.gender || "").toLowerCase() === "female").length;
    const independent = valid.filter(r => r?.independent === true || r?.independent === "true" || r?.independent === "yes").length;
    // Serialise back to the legacy free-text string so backend / LLM /
    // older PDF paths keep rendering unchanged.
    const serial = valid.map(r => {
      const bits = [r.name.trim()];
      const meta = [r.role, r.gender, r.independent ? "Independent" : "Executive"].filter(Boolean);
      if (meta.length) bits.push(`(${meta.join(", ")})`);
      return bits.join(" ");
    }).join(", ");
    setGov(g => {
      // Only update if values actually changed, avoid infinite loops.
      const sizeStr = String(total);
      const femStr = String(female);
      const indStr = String(independent);
      if (g.board_size === sizeStr && g.board_female_pct === femStr && g.independent_pct === indStr && g.board_members === serial) {
        return g;
      }
      return { ...g, board_size: sizeStr, board_female_pct: femStr, independent_pct: indStr, board_members: serial };
    });
  }, [gov.board_members_structured]);

  // Helper: sum a monthly breakdown object; returns null if EMPTY (no
  // months entered) so the caller can distinguish "not using monthly" from
  // "monthly total = 0".
  const _monthlySum = (mo) => {
    if (!mo || typeof mo !== "object") return null;
    const vals = Object.values(mo).map(v => parseFloat(v)).filter(v => !Number.isNaN(v));
    if (vals.length === 0) return null;
    return vals.reduce((a, b) => a + b, 0);
  };

  // Monthly → annual sync: if the 12-month panel has any values, we push
  // the sum into the annual field automatically (partial sums allowed
  // during typing; the gate ENFORCES all-12 filled before Next click).
  useEffect(() => {
    const sum = _monthlySum(env.monthly_electricity_kwh);
    if (sum !== null) {
      setEnv(e => ({ ...e, electricity_kwh: String(sum), electricity_kwh_manual: false }));
    }
  }, [env.monthly_electricity_kwh]);

  useEffect(() => {
    const sum = _monthlySum(env.monthly_water_m3);
    if (sum !== null) {
      setEnv(e => ({ ...e, water: String(sum), water_manual: false }));
    }
  }, [env.monthly_water_m3]);

  // Scope 1 = diesel×2.68 + petrol×2.31 + gas×2.04 + LPG×3.0  (kg → tonnes)
  // Emission factors: DEFRA 2024 UK GHG Conversion Factors.
  useEffect(() => {
    if (env.scope1_manual) return;
    const d = parseFloat(env.diesel_litres)   || 0;
    const p = parseFloat(env.petrol_litres)   || 0;
    const g = parseFloat(env.natural_gas_m3)  || 0;
    const l = parseFloat(env.lpg_kg)          || 0;
    if (d || p || g || l) {
      const total = ((d * 2.68) + (p * 2.31) + (g * 2.04) + (l * 3.0)) / 1000;
      setEnv(e => ({ ...e, scope1: total.toFixed(2) }));
    } else {
      setEnv(e => (e.scope1 === "" ? e : { ...e, scope1: "" }));
    }
  }, [env.diesel_litres, env.petrol_litres, env.natural_gas_m3, env.lpg_kg, env.scope1_manual]);

  // Scope 2 = electricity_kwh × grid_factor + district_cooling_RT × 0.6 × grid_factor
  //          (tonnes CO2e). District cooling: 0.6 kWh-elec per RT-hr
  //          @ modern chiller COP ≈ 5.8 (Empower DC Sustainability 2023).
  // FIXED (this round): district cooling now flows into Scope 2 from a
  // proper env field, not a disconnected local converter state.
  useEffect(() => {
    if (env.scope2_manual) return;
    const kwh  = parseFloat(env.electricity_kwh)         || 0;
    const cool = parseFloat(env.district_cooling_rt_hrs) || 0;
    const grid = GRIDS[company.country] || 0.45;
    if (kwh || cool) {
      const total = ((kwh * grid) + (cool * 0.6 * grid)) / 1000;
      setEnv(e => ({ ...e, scope2: total.toFixed(2) }));
    } else {
      setEnv(e => (e.scope2 === "" ? e : { ...e, scope2: "" }));
    }
  }, [env.electricity_kwh, env.district_cooling_rt_hrs, company.country, env.scope2_manual]);

  // Scope 3 = flights + landfill-waste + commute
  // Waste part is DERIVED from env.waste × landfill_pct (no separate input).
  useEffect(() => {
    if (env.scope3_manual) return;
    const fs = parseFloat(s3FlyS) || 0;
    const fl = parseFloat(s3FlyL) || 0;
    const wasteT = parseFloat(env.waste) || 0;
    const lfPct  = parseFloat(env.landfill_pct);
    const landfillWaste = (!Number.isNaN(lfPct) && lfPct > 0) ? wasteT * (lfPct / 100) : 0;
    const c = parseFloat(s3Commute) || 0;
    const emp = parseFloat(soc.total_employees) || 50;
    if (fs || fl || landfillWaste || c) {
      const total = (fs * 800 * 0.000255) + (fl * 900 * 0.000166) + (landfillWaste * 0.5) + (c * emp * 230 * 0.00017);
      setEnv(e => ({ ...e, scope3: total.toFixed(2) }));
    } else {
      setEnv(e => (e.scope3 === "" ? e : { ...e, scope3: "" }));
    }
  }, [s3FlyS, s3FlyL, s3Commute, env.waste, env.landfill_pct, soc.total_employees, env.scope3_manual]);

  // Total energy (kWh) = electricity + fuel-equivalents + on-site renewable
  // Rough kWh equivalents for fuel (LHV): diesel 10 kWh/L, petrol 9.7 kWh/L,
  // natural gas 10.6 kWh/m³, LPG 12.8 kWh/kg. Rounded to whole kWh.
  useEffect(() => {
    if (env.energy_manual) return;
    const kwh = parseFloat(env.electricity_kwh)         || 0;
    const d   = parseFloat(env.diesel_litres)           || 0;
    const p   = parseFloat(env.petrol_litres)           || 0;
    const g   = parseFloat(env.natural_gas_m3)          || 0;
    const l   = parseFloat(env.lpg_kg)                  || 0;
    const rgen= parseFloat(env.renewable_generated_kwh) || 0;
    const total = kwh + (d * 10) + (p * 9.7) + (g * 10.6) + (l * 12.8) + rgen;
    if (total > 0) {
      setEnv(e => ({ ...e, energy: String(Math.round(total)) }));
    } else {
      setEnv(e => (e.energy === "" ? e : { ...e, energy: "" }));
    }
  }, [env.electricity_kwh, env.diesel_litres, env.petrol_litres, env.natural_gas_m3, env.lpg_kg, env.renewable_generated_kwh, env.energy_manual]);

  // Renewable share % = renewable_generated / total_energy × 100
  useEffect(() => {
    if (env.renewable_pct_manual) return;
    const rgen = parseFloat(env.renewable_generated_kwh) || 0;
    const en   = parseFloat(env.energy)                  || 0;
    if (rgen > 0 && en > 0) {
      setEnv(e => ({ ...e, renewable_pct: ((rgen / en) * 100).toFixed(1) }));
    } else if (rgen === 0 && env.renewable_pct !== "" && !env.renewable_pct_manual) {
      setEnv(e => (e.renewable_pct === "0.0" || e.renewable_pct === "" ? e : { ...e, renewable_pct: "" }));
    }
  }, [env.renewable_generated_kwh, env.energy, env.renewable_pct_manual]);

  // Female Employees % — sync state to the value that the Female %
  // input DISPLAYS whenever the by-level Male/Female breakdown is
  // populated. Without this sync, the user sees "60%" in the field
  // (derived from the breakdown), but `soc.female_pct` still holds
  // the pre-breakdown typed value (e.g. "3"), which then trips a
  // false-positive coherence warning ("By-level ratio 60% vs
  // declared 3%") and corrupts downstream numeric prose. Feb 2026.
  useEffect(() => {
    const fem = (parseInt(soc.senior_mgmt_female || 0, 10) || 0)
              + (parseInt(soc.middle_mgmt_female || 0, 10) || 0)
              + (parseInt(soc.technical_female || 0, 10) || 0)
              + (parseInt(soc.admin_female || 0, 10) || 0);
    const tot = parseFloat(soc.total_employees || 0) || 0;
    if (fem > 0 && tot > 0) {
      const derived = String(Math.round((fem / tot) * 1000) / 10);
      setSoc(s => (s.female_pct === derived ? s : { ...s, female_pct: derived }));
    }
  }, [soc.senior_mgmt_female, soc.middle_mgmt_female, soc.technical_female, soc.admin_female, soc.total_employees]);

  // Energy Intensity (kWh per 1,000 of local currency revenue)
  useEffect(() => {
    const e = parseFloat(env.energy) || 0;
    const r = parseFloat(eiRevenue) || 0;
    if (e && r) {
      setEnv(ev => ({ ...ev, energy_intensity: (e / (r / 1000)).toFixed(2) }));
    }
  }, [env.energy, eiRevenue]);

  // ═══════════════════════════════════════════════════════════════════
  // SOCIAL, auto-derivation from monthly panels
  // ═══════════════════════════════════════════════════════════════════

  // Average monthly headcount → total_employees (rounded)
  // Headcount is a STOCK metric so we use the MEAN across the 12 months
  // (not the sum). GRI 2-7 recommends the average headcount over the
  // reporting period for FTE-based ratios.
  useEffect(() => {
    if (soc.total_employees_manual) return;
    const vals = _MONTHS.map(([k]) => parseFloat(soc.monthly_headcount?.[k])).filter(v => !Number.isNaN(v));
    if (vals.length === 0) return;
    const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
    setSoc(s => ({ ...s, total_employees: String(Math.round(avg)) }));
  }, [soc.monthly_headcount, soc.total_employees_manual]);

  // Sum monthly LTI incidents → lti_count
  useEffect(() => {
    if (soc.lti_count_manual) return;
    const vals = _MONTHS.map(([k]) => parseFloat(soc.monthly_lti_count?.[k])).filter(v => !Number.isNaN(v));
    if (vals.length === 0) return;
    const sum = vals.reduce((a, b) => a + b, 0);
    setSoc(s => ({ ...s, lti_count: String(sum) }));
  }, [soc.monthly_lti_count, soc.lti_count_manual]);

  // Sum monthly working hours → total_working_hours
  useEffect(() => {
    if (soc.total_working_hours_manual) return;
    const vals = _MONTHS.map(([k]) => parseFloat(soc.monthly_working_hours?.[k])).filter(v => !Number.isNaN(v));
    if (vals.length === 0) return;
    const sum = vals.reduce((a, b) => a + b, 0);
    setSoc(s => ({ ...s, total_working_hours: String(Math.round(sum)) }));
  }, [soc.monthly_working_hours, soc.total_working_hours_manual]);

  // LTIR (Lost-Time Injury Rate per million working hours)
  //   = LTI count × 1,000,000 / total working hours
  // Only auto-computes when BOTH inputs are present and the user has not
  // manually overridden LTIR.
  useEffect(() => {
    if (soc.injury_rate_manual) return;
    const lti = parseFloat(soc.lti_count);
    const twh = parseFloat(soc.total_working_hours);
    if (Number.isFinite(lti) && Number.isFinite(twh) && twh > 0) {
      const ltir = (lti * 1_000_000) / twh;
      setSoc(s => ({ ...s, injury_rate: ltir.toFixed(2) }));
    } else if (Number.isFinite(lti) && lti === 0 && Number.isFinite(twh) && twh > 0) {
      setSoc(s => ({ ...s, injury_rate: "0.00" }));
    }
  }, [soc.lti_count, soc.total_working_hours, soc.injury_rate_manual]);

  // ── PREVIOUS YEAR ────────────────────────────────────────────
  // Auto-suggest previous-year working hours from prev_total_employees × 2,080h.
  // Only sets when the field is empty AND the user hasn't overridden it.
  useEffect(() => {
    if (soc.prev_working_hours_manual) return;
    const emp = parseFloat(soc.prev_total_employees);
    if (Number.isFinite(emp) && emp > 0 && !soc.prev_total_working_hours) {
      setSoc(s => ({ ...s, prev_total_working_hours: String(Math.round(emp * 2080)) }));
    }
  }, [soc.prev_total_employees, soc.prev_working_hours_manual, soc.prev_total_working_hours]);

  // Previous-year LTIR (same formula as current year, derived from incidents).
  useEffect(() => {
    if (soc.prev_injury_rate_manual) return;
    const lti = parseFloat(soc.prev_lti_count);
    const twh = parseFloat(soc.prev_total_working_hours);
    if (Number.isFinite(lti) && Number.isFinite(twh) && twh > 0) {
      const ltir = (lti * 1_000_000) / twh;
      setSoc(s => ({ ...s, prev_injury_rate: ltir.toFixed(2) }));
    } else if (Number.isFinite(lti) && lti === 0 && Number.isFinite(twh) && twh > 0) {
      setSoc(s => ({ ...s, prev_injury_rate: "0.00" }));
    }
  }, [soc.prev_lti_count, soc.prev_total_working_hours, soc.prev_injury_rate_manual]);

  // saveReport({silent: true}), used by CoherenceBadge to persist the
  // latest form values before hitting /validate. Returns the report doc
  // (or { _id } for a fresh creation) so the caller can reuse the id
  // without waiting for the setReportId state update.
  const saveReport = async (opts = {}) => {
    const silent = !!opts.silent;
    if (!silent) setSaving(true);
    try {
      const data = { company_data: company, environmental_data: env, social_data: soc, governance_data: gov, language: lang };
      if (reportId) {
        const res = await api.updateReport(reportId, data);
        return res || { _id: reportId };
      }
      const res = await api.createReport(data);
      if (res && res._id) {
        setReportId(res._id);
        // Sticky URL so a refresh recovers the form instead of losing it.
        // Feb 2026 fix: /report/new left the URL unchanged after the first
        // save, and users who reloaded lost all their inputs.
        if (!id) navigate(`/report/${res._id}/edit`, { replace: true });
      }
      return res;
    } catch (err) {
      console.error("Save failed:", err);
      return null;
    } finally {
      if (!silent) setSaving(false);
    }
  };

  const [genStatus, setGenStatus] = useState("");
  const [genElapsed, setGenElapsed] = useState(0);
  const [genPhase, setGenPhase] = useState(null);
  const [preflightIssues, setPreflightIssues] = useState(null);
  const [lowCoverageWarning, setLowCoverageWarning] = useState(null);

  // Pre-generation data sanity check. Catches the most common
  // user-side data problems BEFORE we burn $0.50 on Claude.
  // Returns an array of human-readable issue strings.
  const runPreflight = () => {
    const issues = [];
    const num = (v) => {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : null;
    };
    if (!company.name?.trim()) issues.push("Company name is empty.");
    if (!company.sector?.trim()) issues.push("Sector is not selected.");
    if (company.sector?.trim() === "Other") issues.push("Industry is 'Other', please type your industry in the box under the Industry field.");
    const lp = num(soc.local_pct), te = num(soc.total_employees);
    if (lp && te && lp > 0 && lp <= 100) {
      const ppl = te * lp / 100;
      if (Math.abs(ppl - Math.round(ppl)) > 0.01) {
        const n = Math.max(1, Math.round(ppl));
        issues.push(`Nationalisation ${lp}% of ${te} employees is ${ppl.toFixed(1)} people. The report will show ${n} of ${te} (${(n / te * 100).toFixed(1).replace(/\.0$/, "")}%), update the % if that's wrong.`);
      }
    }
    const ps2 = num(env.prev_scope2), pe = num(env.prev_energy), s2e = num(env.scope2), kwhE = num(env.electricity_kwh);
    if (ps2 > 0 && pe > 0) {
      const ef = s2e > 0 && kwhE > 0 ? (s2e * 1000) / kwhE : 0.4;
      const implied = (ps2 * 1000) / ef;
      if (implied > pe * 1.1) issues.push(`Prior-year Scope 2 of ${ps2.toLocaleString()} t implies about ${Math.round(implied).toLocaleString()} kWh of grid electricity, but prior-year total energy is ${pe.toLocaleString()} kWh. Please check one of the two figures.`);
    }
    const w = num(env.water);
    if (w > 0 && te > 0 && w / te > 500) issues.push(`Water use of ${w.toLocaleString()} m³ is ${Math.round(w / te).toLocaleString()} m³ per employee per year, which is unusually high. Please confirm the figure (and that it is in m³, not litres).`);

    if (!company.year?.trim()) issues.push("Reporting year is missing.");
    const emp = num(soc.total_employees);
    if (!emp || emp < 1) issues.push("Total employees is 0 or missing, every section needs this number.");
    const fpct = num(soc.female_pct);
    if (fpct !== null && (fpct < 0 || fpct > 100)) issues.push(`Female workforce % (${fpct}) is outside 0–100.`);
    const bs = num(gov.board_size);
    const bf = num(gov.board_female_pct);
    if (bs && bf && bf > bs) issues.push(`Board has ${bf} female members but only ${bs} seats total, please reconcile.`);
    const indep = num(gov.independent_pct);
    if (bs && indep && indep > bs) issues.push(`Independent directors (${indep}) exceeds board size (${bs}).`);
    const s1 = num(env.scope1), s2 = num(env.scope2);
    if (!s1 && !s2) issues.push("Both Scope 1 and Scope 2 emissions are empty, at least one is needed for a credible GHG section.");
    const energy = num(env.energy);
    if (!energy) issues.push("Energy consumption (kWh) is missing.");
    const water = num(env.water);
    if (!water) issues.push("Water consumption (m³) is missing.");
    const recycled = num(env.recycled_pct);
    if (recycled !== null && (recycled < 0 || recycled > 100)) issues.push(`Recycled % (${recycled}) is outside 0–100.`);
    const train = num(soc.training_hours);
    if (!train) issues.push("Training hours per employee is missing, Training section will be thin.");
    return issues;
  };

  const handleGenerate = async (skipPreflight = false, skipCoverage = false) => {
    // Strip the transient "__OTHER__" placeholder from country before any
    // server call, it's a UI-only marker that keeps the "Other Country"
    // pill highlighted while the user is typing the custom country name.
    let effectiveCompany = company;
    if ((company.country || "").includes("__OTHER__")) {
      const cleaned = company.country.split(", ").filter(s => s && s !== "__OTHER__").join(", ");
      effectiveCompany = { ...company, country: cleaned };
      setCompany(effectiveCompany);
    }
    // Preflight: if there are issues, show modal and let user decide.
    if (company.sector?.trim() === "Other") {
      alert(lang === "ar" ? "يرجى كتابة نشاطك/قطاعك بدلاً من \"أخرى\" قبل إنشاء التقرير." : "Please type your industry instead of \"Other\" before generating the report.");
      return;
    }
    if (!skipPreflight) {
      const issues = runPreflight();
      if (issues.length > 0) {
        setPreflightIssues(issues);
        return;
      }
    }
    // Low-coverage gate: if any pillar is <40% filled, warn the user before
    // burning AI credits on a sparse report. User can force-continue.
    if (!skipCoverage) {
      const eScore = calcScore(env, 'e');
      const sScore = calcScore(soc, 's');
      const gScore = calcScore(gov, 'g');
      const thin = [];
      if (eScore < 40) thin.push({ pillar: "Environmental", score: eScore, step: 1 });
      if (sScore < 40) thin.push({ pillar: "Social", score: sScore, step: 2 });
      if (gScore < 40) thin.push({ pillar: "Governance", score: gScore, step: 3 });
      if (thin.length > 0) {
        setLowCoverageWarning(thin);
        return;
      }
    }
    // ─── Server-side coherence gate (Feb 2026, July 17 audit fix) ───
    // Before we spend a dime on an Anthropic call, ask the backend
    // if the current form inputs are internally coherent. The server
    // enforces the same rules on POST /generate, so this second call
    // just gives the user a nicer error surface than a raw 422.
    try {
      const data = { company_data: effectiveCompany, environmental_data: env, social_data: soc, governance_data: gov, language: lang };
      let rid = reportId;
      if (!rid) {
        const res = await api.createReport(data);
        rid = res._id;
        setReportId(rid);
        if (!id) navigate(`/report/${rid}/edit`, { replace: true });
      } else {
        await api.updateReport(rid, data);
      }
      const valRes = await api.validateReport(rid);
      const errIssues = (valRes?.issues || []).filter(i => i.severity === "error");
      if (errIssues.length > 0) {
        const list = errIssues.slice(0, 6).map(i => `• ${i.message}`).join("\n");
        alert(
          `Cannot generate, ${errIssues.length} contradictory field(s) in your form:\n\n` +
          `${list}\n\n` +
          `Please fix these in the form and try again.`
        );
        return;
      }
    } catch (e) {
      // If validation endpoint itself errors, fall through to generation.
      // The server-side gate on /generate is the source of truth anyway.
      console.warn("Pre-flight validation call failed, falling through:", e);
    }
    setPreflightIssues(null);
    setGenerating(true);
    setGenStatus("Saving data...");
    setGenPhase("init");
    setGenElapsed(0);
    // Track elapsed via a fixed start timestamp instead of a 1-per-second
    // counter, that way if the browser tab goes to background (throttling
    // the interval) or the user navigates away and comes back, the counter
    // shows the REAL elapsed time, not the wall-clock time the tab was active.
    const startTs = Date.now();
    const timer = setInterval(() => {
      setGenElapsed(Math.floor((Date.now() - startTs) / 1000));
    }, 1000);
    try {
      const data = { company_data: company, environmental_data: env, social_data: soc, governance_data: gov, language: lang };
      let rid = reportId;
      if (!rid) {
        const res = await api.createReport(data);
        rid = res._id;
        setReportId(rid);
        if (!id) navigate(`/report/${rid}/edit`, { replace: true });
      } else {
        await api.updateReport(rid, data);
      }
      setGenStatus("AI is writing your report...");
      const result = await api.pollUntilGenerated(rid, (status, _attempts, phaseLabel, phaseId) => {
        if (status === "generating") {
          // Phase label like "Writing environmental & social sections"
          // gives much better feedback than a generic spinner.
          setGenStatus(phaseLabel ? `${phaseLabel}…` : "AI is writing your report...");
          if (phaseId) setGenPhase(phaseId);
        } else if (status === "generated") {
          setGenStatus("Complete!");
          setGenPhase("done");
        }
      });
      clearInterval(timer);
      // If the 20-min poll window expired but the server is still working,
      // navigate to the report page anyway. ReportView will show the live
      // status (probably about to flip to 'generated' any second), much
      // friendlier than throwing a "Failed" error message.
      if (result && result._polling_timeout) {
        alert("Your report is taking longer than usual but is still being generated.\n\nWe'll take you to the report page now, refresh in a minute or two and your PDF will be ready to download. Your data is safe.");
      }
      navigate(`/report/${rid}`);
    } catch (err) {
      // Cross-tab session collision: another browser tab logged in as
      // a different user, overwriting the JWT in localStorage. The
      // polling loop detected this via a token mismatch. Show a
      // specific, actionable message instead of a confusing 404.
      if (err.session_changed || err.message === "session_changed") {
        alert(
          "Your session changed in another browser tab.\n\n" +
          "You logged in as a different account while this report was generating. " +
          "The report is still being generated on the server under the original account.\n\n" +
          "To see it: log back in as the account that owns this report, then refresh this page."
        );
        setGenStatus("");
        return;
      }
      // ─── Subscription payment gate (402 → send user to Billing page) ─
      const detail402 = err.response?.data?.detail;
      const err402 = err.response?.status === 402 && typeof detail402 === "object" ? detail402 : null;
      if (err402 && err402.error === "subscription_required") {
        setGenStatus("");
        clearInterval(timer);
        setGenerating(false);
        alert(err402.message || "This action requires an active subscription. Please subscribe from your Billing page.");
        window.location.href = "/billing";
        return;
      }
      // The Pattern-A ingestion gate returns HTTP 422 with a STRUCTURED
      // detail body: { error, message, errors[], warnings[] }.
      // Legacy 4xx/5xx errors return `detail` as a plain string.
      // Handle both so the user always sees an actionable message.
      const detail = err.response?.data?.detail;
      let msg;
      if (detail && typeof detail === "object") {
        if (detail.error === "input_reconciliation_failed") {
          const list = Array.isArray(detail.errors) ? detail.errors : [];
          const warns = Array.isArray(detail.warnings) ? detail.warnings : [];
          msg = (detail.message || "Please fix these input contradictions before generating:")
              + "\n\n• " + list.join("\n• ");
          if (warns.length) {
            msg += "\n\nAdvisory (non-blocking):\n• " + warns.join("\n• ");
          }
        } else {
          msg = detail.message || JSON.stringify(detail);
        }
      } else if (detail === "Report not found" && err.response?.status === 404) {
        // Most common non-Pattern-A cause: the token in localStorage
        // was overwritten by a second tab logging in as a different
        // user, or the URL contains a stale/deleted report id.
        msg = "This report can't be found under your current login.\n\n" +
              "This usually happens when you sign in with a different account " +
              "in another browser tab (which overwrites your session here). " +
              "Please sign back in as the account that owns this report.";
      } else {
        msg = detail || err.message || "Unknown error";
      }
      alert("Generation failed:\n\n" + msg);
      setGenStatus("");
    } finally {
      clearInterval(timer);
      setGenerating(false);
    }
  };

  const goNext = async () => {
    await saveReport();
    setStep(s => Math.min(s + 1, 4));
  };
  const goBack = () => setStep(s => Math.max(s - 1, 0));

  const steps = [t('step_company'), t('step_environment'), t('step_social'), t('step_governance'), t('step_generate')];
  const stepIcons = [Buildings, TreeEvergreen, Users, ShieldCheck, Sparkle];

  const progressPct = ((step + 1) / 5) * 100;

  return (
    <div className="min-h-screen" style={{ background: "#070709" }}>
      {/* Pre-generation data sanity-check modal */}
      {preflightIssues && !generating && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="preflight-modal">
          <div className="max-w-lg w-full bg-[#0F172A] border border-amber-500/40 rounded-2xl p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-2xl">⚠️</span>
              <h3 className="text-white font-bold text-lg">Heads up, {preflightIssues.length} data issue{preflightIssues.length>1?"s":""} detected</h3>
            </div>
            <p className="text-slate-300 text-sm mb-4">
              We noticed the following before generating your report. Fixing them now will produce a stronger, more defensible report. You can also continue and the AI will note the gaps honestly in the disclosures.
            </p>
            <ul className="space-y-2 mb-5 max-h-72 overflow-y-auto">
              {preflightIssues.map((issue, idx) => (
                <li key={idx} className="flex gap-2 text-sm text-slate-200 bg-amber-500/10 border-l-2 border-amber-500 pl-3 py-2 rounded-r" data-testid={`preflight-issue-${idx}`}>
                  <span className="text-amber-400">•</span> {issue}
                </li>
              ))}
            </ul>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setPreflightIssues(null)}
                className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm transition-colors"
                data-testid="preflight-fix-btn"
              >
                Let me fix them
              </button>
              <button
                onClick={() => { setPreflightIssues(null); setTimeout(() => handleGenerate(true), 50); }}
                className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-semibold text-sm transition-colors"
                data-testid="preflight-continue-btn"
              >
                Continue anyway
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Low-pillar-coverage warning modal */}
      {lowCoverageWarning && !generating && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="low-coverage-modal">
          <div className="max-w-lg w-full bg-[#0F172A] border border-orange-500/40 rounded-2xl p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-2xl">📉</span>
              <h3 className="text-white font-bold text-lg">
                {lowCoverageWarning.length} pillar{lowCoverageWarning.length>1?"s":""} below 40%
              </h3>
            </div>
            <p className="text-slate-300 text-sm mb-4">
              A comprehensive ESG report expects meaningful data across all three pillars.
              The following section{lowCoverageWarning.length>1?"s are":" is"} sparsely filled, so the AI will have very
              little to work with and the resulting chapter{lowCoverageWarning.length>1?"s":""} will look thin to a reader.
            </p>
            <ul className="space-y-2 mb-5">
              {lowCoverageWarning.map((w, idx) => (
                <li key={idx}
                    className="flex items-center justify-between text-sm text-slate-200 bg-orange-500/10 border-l-2 border-orange-500 pl-3 pr-2 py-2 rounded-r"
                    data-testid={`low-coverage-issue-${idx}`}>
                  <span><span className="font-semibold">{w.pillar}</span> — only {w.score}% filled</span>
                  <button
                    onClick={() => { const t = w.step; setLowCoverageWarning(null); saveReport(); setStep(t); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    className="text-xs text-orange-300 hover:text-orange-200 underline"
                    data-testid={`low-coverage-jump-${idx}`}
                  >
                    Fix now →
                  </button>
                </li>
              ))}
            </ul>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setLowCoverageWarning(null)}
                className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm transition-colors"
                data-testid="low-coverage-fix-btn"
              >
                Let me fill more
              </button>
              <button
                onClick={() => { setLowCoverageWarning(null); setTimeout(() => handleGenerate(true, true), 50); }}
                className="px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-400 text-black font-semibold text-sm transition-colors"
                data-testid="low-coverage-continue-btn"
              >
                Generate anyway
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#070709]/70 border-b border-white/[0.06]">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm" data-testid="back-to-dashboard">
            <ArrowCircleLeft size={20} /> {t('dashboard')}
          </button>
          <div className="flex items-center gap-2">
            <LangSwitch />
            <Logo size="sm" />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-6">
        {/* Progress */}
        <div className="bg-[#111216] border border-slate-800/60 rounded-xl p-4 mb-5" data-testid="progress-bar">
          <div className="flex justify-between text-xs text-slate-400 mb-2">
            <span>{t('progress_step')} <span className="font-['Outfit'] font-bold text-[#01AF9B]">{formatNum(step + 1, lang)}</span> {t('progress_of')} {formatNum(5, lang)}, {steps[step]}</span>
            <span className="font-['Outfit'] font-bold text-[#01AF9B]">{formatPct(Math.round(progressPct), lang)}</span>
          </div>
          <div className="h-1 bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#01AF9B] to-[#34D399] rounded-full transition-all duration-500" style={{ width: `${progressPct}%` }} />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-4 bg-[#111216] border border-slate-800/60 rounded-xl p-1.5 overflow-x-auto" data-testid="step-tabs">
          {steps.map((s, i) => {
            const Icon = stepIcons[i];
            return (
              <button key={i} onClick={() => { if (i <= step) { saveReport(); setStep(i); } }}
                data-testid={`tab-${s.toLowerCase()}`}
                className={`flex-1 min-w-[80px] py-2 px-2 rounded-lg font-['Outfit'] text-xs font-semibold transition-all flex items-center justify-center gap-1.5
                  ${i === step ? "bg-[#01AF9B] text-white" : i < step ? "text-[#01AF9B] hover:bg-slate-800" : "text-slate-500"}`}>
                <Icon size={14} /> {s}
              </button>
            );
          })}
        </div>

        {/* Live pillar-completion meter, visible on every step */}
        <LiveCoverageMeter env={env} soc={soc} gov={gov} lang={lang} t={t} />
        {/* Smart nudges — up to 3 clickable chips per weak pillar, each
            jumps the user to the owning step and focuses the field.
            Only renders when at least one pillar is below 70%. */}
        <SmartNudges env={env} soc={soc} gov={gov} lang={lang} t={t} setStep={setStep} />

        {/* Step 0: Company */}
        {step === 0 && (
          <div className="animate-fade-in-up" data-testid="step-company">
            <div className="bg-[#01AF9B]/5 border-l-2 border-[#01AF9B] rounded-r-lg px-4 py-3 text-sm text-[#01AF9B] mb-5">
              {t('company_info_hint')}
            </div>
            <Card icon={<Buildings size={18} className="text-[#01AF9B]" />} title={t('company_information')} desc={t('basic_details')}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={`${t('company_name')} *`} full>
                  <Input testId="input-company-name" value={company.name} onChange={e => setCompany({ ...company, name: e.target.value })} placeholder={lang === "ar" ? "مثال: شركة النور للتجارة ذ.م.م" : "e.g. Al Noor Trading LLC"} />
                </Field>
                <Field label={`${t('industry_sector')} *`}>
                  <Select
                    testId="select-sector"
                    value={SECTORS.includes(company.sector) ? company.sector : (company.sector === "" ? "" : "Other")}
                    onChange={e => setCompany({ ...company, sector: e.target.value })}
                  >
                    <option value="">{t('select_sector')}</option>
                    {getSectors(lang).map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </Select>
                  {(company.sector === "Other" || (company.sector !== "" && !SECTORS.includes(company.sector))) && (
                    <div className="mt-2">
                      <Input
                        testId="input-sector-other"
                        value={company.sector === "Other" ? "" : company.sector}
                        onChange={e => setCompany({ ...company, sector: e.target.value || "Other" })}
                        placeholder={lang === "ar" ? "اكتب نشاطك / قطاعك (مثال: الفعاليات والترفيه الحي)" : "Type your industry / activity (e.g. Events & Live Entertainment)"}
                      />
                      <div className={`text-[10px] mt-1 ${company.sector === "Other" ? "text-red-600 font-semibold" : "text-slate-500"}`} data-testid="sector-other-hint">
                        {company.sector === "Other"
                          ? (lang === "ar" ? "مطلوب: اكتب نشاطك، وإلا سيظهر \"أخرى\" في التقرير." : "Required: type your industry, otherwise the report would print \"Other\".")
                          : (lang === "ar" ? "سيظهر النشاط الذي كتبته على الغلاف وفي السرد المُولَّد بالذكاء الاصطناعي." : "Your typed activity will appear on the cover and in the AI-generated narrative.")}
                      </div>
                    </div>
                  )}
                </Field>
                <Field label={t('reporting_year')} hint={lang === "ar" ? "اكتب أي سنة" : "Type any year"}>
                  <Input testId="input-year" type="text" inputMode="numeric" pattern="[0-9]*" value={company.year} onChange={e => setCompany({ ...company, year: e.target.value })} placeholder="2025" />
                </Field>
                <Field label={lang === "ar" ? "دول العمليات" : "Countries of Operation"} hint={lang === "ar" ? "اختر كل ما ينطبق" : "Select all that apply"}>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {getCountries(lang).map(c => {
                      const selected = (company.country || "").split(", ").filter(Boolean);
                      const isChecked = c.value === "Other Country"
                        ? selected.some(s => !["UAE","Saudi Arabia","Qatar","Kuwait","Oman","Bahrain"].includes(s))
                        : selected.includes(c.value);
                      return (
                        <button key={c.value} type="button"
                          data-testid={`country-btn-${c.value.replace(/\s+/g, '-').toLowerCase()}`}
                          className={`px-3 py-1.5 rounded-lg text-xs font-['Outfit'] font-semibold border transition-all ${isChecked ? "bg-[#01AF9B]/20 border-[#01AF9B] text-[#01AF9B]" : "bg-[#0B0C10] border-slate-700 text-slate-400 hover:border-slate-500"}`}
                          onClick={() => {
                            const curr = (company.country || "").split(", ").filter(Boolean);
                            if (c.value === "Other Country") {
                              const hasCustom = curr.some(s => !["UAE","Saudi Arabia","Qatar","Kuwait","Oman","Bahrain"].includes(s));
                              const nextList = hasCustom
                                ? curr.filter(s => ["UAE","Saudi Arabia","Qatar","Kuwait","Oman","Bahrain"].includes(s))
                                : [...curr, "__OTHER__"];
                              setCompany({ ...company, country: nextList.join(", ") });
                            } else {
                              const next = selected.includes(c.value) ? curr.filter(x => x !== c.value) : [...curr, c.value];
                              setCompany({ ...company, country: next.join(", ") });
                            }
                          }}>
                          {c.label}
                        </button>
                      );
                    })}
                  </div>
                  {(company.country || "").split(", ").some(s => s && !["UAE","Saudi Arabia","Qatar","Kuwait","Oman","Bahrain"].includes(s)) && (
                    <div className="mt-3">
                      <Input
                        testId="input-other-country"
                        value={((company.country || "").split(", ").find(s => s && !["UAE","Saudi Arabia","Qatar","Kuwait","Oman","Bahrain"].includes(s)) || "").replace("__OTHER__", "")}
                        onChange={e => {
                          const raw = e.target.value.trim();
                          const gccOnly = (company.country || "").split(", ").filter(s => ["UAE","Saudi Arabia","Qatar","Kuwait","Oman","Bahrain"].includes(s));
                          const nextList = raw ? [...gccOnly, raw] : [...gccOnly, "__OTHER__"];
                          setCompany({ ...company, country: nextList.join(", ") });
                        }}
                        placeholder={lang === "ar" ? "اكتب اسم الدولة (مثال: مصر، الأردن، الهند)" : "Type the country name (e.g. Egypt, Jordan, India)"}
                      />
                    </div>
                  )}
                </Field>
                <Field label={t('num_employees')}>
                  <Select testId="select-size" value={company.size} onChange={e => setCompany({ ...company, size: e.target.value })}>
                    {getSizes(lang).map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </Select>
                </Field>
                <Field label={t('ceo_director')}>
                  <Input testId="input-ceo" value={company.ceo} onChange={e => setCompany({ ...company, ceo: e.target.value })} placeholder={lang === "ar" ? "مثال: محمد الراشد" : "e.g. Mohammed Al Rashid"} />
                </Field>
                <Field label={lang === "ar" ? "مُعدّ التقرير (اسم الشخص الذي أعدّ هذا التقرير)" : "Report Writer (name of the person who prepared this report)"}>
                  <Input testId="input-report-writer" value={company.report_writer_name} onChange={e => setCompany({ ...company, report_writer_name: e.target.value })} placeholder={lang === "ar" ? "مثال: سارة المنصوري" : "e.g. Sarah Al Mansouri"} />
                </Field>
                <Field label={t('website')}>
                  <Input testId="input-website" value={company.website} onChange={e => setCompany({ ...company, website: e.target.value })} placeholder="www.company.ae" />
                </Field>
                <Field label={lang === "ar" ? "العملة" : "Currency"}>
                  <Select testId="select-currency" value={company.currency} onChange={e => setCompany({ ...company, currency: e.target.value })}>
                    {getCurrencies(lang).map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </Select>
                </Field>
                <Field label={t('company_description')} full>
                  <TextArea testId="input-description" value={company.description} onChange={e => setCompany({ ...company, description: e.target.value })} placeholder={t('describe_company')} />
                  <AnyLangHint lang={lang} />
                </Field>
                <Field label={t('leadership_statement')} full>
                  <div className="flex flex-col sm:flex-row gap-3 mb-3">
                    <Input
                      testId="input-leadership-title"
                      value={company.leadership_title}
                      onChange={e => setCompany({ ...company, leadership_title: e.target.value })}
                      className="sm:w-72"
                      list="leadership-title-presets"
                      placeholder={lang === "ar" ? "مثال: الرئيس التنفيذي، رئيس مجلس الإدارة، المؤسس..." : "e.g. CEO, Chairman, Founder, Partner..."}
                    />
                    <datalist id="leadership-title-presets">
                      {getCeoRoles(lang).map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </datalist>
                    <span className="text-xs text-gray-500 self-center">
                      {lang === "ar" ? "اكتب أي منصب أو اختر من القائمة المقترحة. سيُوقَّع هذا الاسم أسفل بيان ESG." : "Type any role, or pick from the suggested list. This name will be signed below the ESG statement."}
                    </span>
                  </div>
                  <TextArea testId="input-leadership" value={company.leadership_statement} onChange={e => setCompany({ ...company, leadership_statement: e.target.value })} placeholder={lang === "ar" ? `رسالة مختصرة من ${company.leadership_title || 'الرئيس التنفيذي'} حول الاستدامة...` : `A brief message from your ${company.leadership_title || 'CEO'} about sustainability...`} />
                  <AnyLangHint lang={lang} />
                </Field>
              </div>
            </Card>

            {/* Reporting & Disclosures (GRI 2-3, 2-4, 2-5) */}
            <Card icon={<Buildings size={18} className="text-[#01AF9B]" />} title={lang === "ar" ? "إعداد التقارير والإفصاحات" : "Reporting & Disclosures"} desc={lang === "ar" ? "إفصاحات اختيارية مطلوبة وفق GRI 2-3 و2-4 و2-5، وإعادة الإدراج، وحالة التدقيق، وجهة الاتصال. إبقاؤها فارغة يستخدم صياغة افتراضية شفّافة." : "Optional disclosures required by GRI 2-3, 2-4, 2-5, restatements, assurance status, and contact point. Leaving them blank uses transparent default wording."}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                <Field label="Contact email for ESG queries (GRI 2-3)" full>
                  <Input testId="input-contact-email" value={company.contact_email} onChange={e => setCompany({ ...company, contact_email: e.target.value })} placeholder="Pre-filled with your account email, replace with a dedicated address (e.g. esg@your-domain.com) if you have one." />
                </Field>
                <Field label="Restatements of prior-period information (GRI 2-4)" full>
                  <TextArea testId="input-restatements" value={company.restatements} onChange={e => setCompany({ ...company, restatements: e.target.value })} placeholder={lang === "ar" ? "صِف أي بيانات أعدنا احتسابها أو إدراجها منذ تقرير العام الماضي (مثل: تصحيح معامل انبعاث، إصلاح احتساب مزدوج). اتركها فارغة إن لم يكن هناك شيء." : "Describe any data we have recalculated or restated since last year's report (e.g. corrected emission factor, fixed double-count). Leave blank if none."} />
                </Field>
                <Field label="External assurance status (GRI 2-5)" full>
                  <TextArea testId="input-assurance" value={company.external_assurance} onChange={e => setCompany({ ...company, external_assurance: e.target.value })} placeholder={lang === "ar" ? "إذا كان هذا التقرير مُدقَّقاً خارجياً، اذكر مقدم الخدمة والنطاق. اتركه فارغاً، سيُفيد التقرير بأمانة بعدم الحصول على تدقيق خارجي." : "If this report was externally assured, name the provider and scope. Leave blank, the report will state honestly that no external assurance was obtained."} />
                </Field>
              </div>
            </Card>

            {/* Section Image Uploads */}
            <Card icon={<Buildings size={18} className="text-[#01AF9B]" />} title={lang === "ar" ? "صور التقرير" : "Report Images"} desc={lang === "ar" ? "ارفع صوراً لأقسام التقرير الرئيسية. تظهر في ملف PDF بإطارات خضراء احترافية." : "Upload images for key report sections. These appear in the PDF with professional green frames."}>
              <ImageUploader reportId={reportId} section="executive_summary" maxImages={1} accentColor="#01AF9B" />
              <ImageUploader reportId={reportId} section="ceo_letter" maxImages={1} accentColor="#01AF9B" />
              <ImageUploader reportId={reportId} section="company_overview" maxImages={2} accentColor="#01AF9B" />
              <ImageUploader reportId={reportId} section="esg_financial_report" maxImages={1} accentColor="#01AF9B" />
            </Card>

            <NavButtons onNext={goNext} saving={saving} lang={lang} />
          </div>
        )}

        {/* Step 1: Environment, SSOT (Single Source of Truth) design.
            Every value is entered ONCE below. Scope 1/2/3, total energy,
            renewable %, and energy intensity are all AUTO-DERIVED and shown
            as read-only chips (with an Override toggle if the user wants to
            input a different figure manually). */}
        {step === 1 && (
          <div className="animate-fade-in-up" data-testid="step-environment">
            <div className="bg-[#01AF9B]/5 border-l-2 border-[#01AF9B] rounded-r-lg px-4 py-3 text-sm text-[#01AF9B] mb-5">
              {lang === "ar" ? "أدخِل كل رقم مرّة واحدة. يُحدَّث النطاق 1 و2 و3 وإجمالي الطاقة ونسبة المتجدّدة وكثافة الطاقة تلقائياً. استخدم اللوحات الشهرية إذا كانت فواتيرك شهرية." : "Enter each figure once. Scope 1, Scope 2, Scope 3, total energy, renewable share and intensity update automatically. Use the monthly panels if your utility bills are per-month."}
            </div>

            {/* ── 1. ENERGY INPUTS (SSOT) ─────────────────────────── */}
            <Card icon={<Lightning size={18} className="text-[#01AF9B]" />} title={lang === "ar" ? "استهلاك الطاقة، أدخِل قراءات العدّاد" : (lang === "ar" ? "استهلاك الطاقة، أدخِل قراءات العدّاد" : "Energy Consumption, Enter meter readings")} desc={lang === "ar" ? "تُدخل جميع مُدخلات الوقود والكهرباء هنا مرّة واحدة. يُحتسب النطاق 1 والنطاق 2 مباشرةً أدناه." : (lang === "ar" ? "تُدخل جميع مُدخلات الوقود والكهرباء هنا مرّة واحدة. يُحتسب النطاق 1 والنطاق 2 مباشرةً أدناه." : "Every fuel and electricity input goes here, once. Scope 1 and Scope 2 recalculate live below.")}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={lang === "ar" ? "كهرباء الشبكة (ك.و.س/سنة)" : (lang === "ar" ? "كهرباء الشبكة (ك.و.س/سنة)" : "Grid Electricity (kWh/year)")} hint="Annual grid-supplied electricity. Use the monthly panel if you have per-month bills." full>
                  <div className="flex gap-2 items-start">
                    <Input testId="input-electricity-kwh" type="number" value={env.electricity_kwh}
                      onChange={e => setEnv({ ...env, electricity_kwh: e.target.value, electricity_kwh_manual: true, monthly_electricity_kwh: { jan:"", feb:"", mar:"", apr:"", may:"", jun:"", jul:"", aug:"", sep:"", oct:"", nov:"", dec:"" } })}
                      placeholder="e.g. 400000"
                      disabled={_monthlyAnyFilled(env.monthly_electricity_kwh)}
                    />
                  </div>
                </Field>
                <MonthlyPanel
                  label={lang === "ar" ? "كهرباء الشبكة الشهرية (ك.و.س)، اختياري" : "Monthly Grid Electricity (kWh), optional"}
                  color="#01AF9B"
                  value={env.monthly_electricity_kwh}
                  onChange={obj => setEnv({ ...env, monthly_electricity_kwh: obj })}
                  unit="kWh"
                  testIdPrefix="mo-elec"
                  whyMonthly="Unlocks a 12-month energy consumption trend chart in the PDF."
                />
                <Field label={lang === "ar" ? "تبريد المناطق (ساعة طن تبريد/سنة)" : (lang === "ar" ? "تبريد المناطق (ساعة طن تبريد/سنة)" : "District Cooling (RT-hrs/year)")} hint="Refrigeration-Ton hours from Empower/Tabreed/Emicool bills.">
                  <Input testId="input-district-cooling" type="number" value={env.district_cooling_rt_hrs}
                    onChange={e => setEnv({ ...env, district_cooling_rt_hrs: e.target.value })}
                    placeholder="e.g. 100000" />
                </Field>
                <Field label={lang === "ar" ? "التوليد المتجدّد الموقعي (ك.و.س/سنة)" : (lang === "ar" ? "التوليد المتجدّد الموقعي (ك.و.س/سنة)" : "On-site Renewable Generation (kWh/year)")} hint="Solar, wind, etc.">
                  <Input testId="input-renewable-generated" type="number" value={env.renewable_generated_kwh} onChange={e => setEnv({ ...env, renewable_generated_kwh: e.target.value })} placeholder="e.g. 50000" />
                </Field>
                <Field label={lang === "ar" ? "تاريخ تشغيل الطاقة الشمسية / المتجدّدة" : (lang === "ar" ? "تاريخ تشغيل الطاقة الشمسية / المتجدّدة" : "Solar / Renewable Commissioning Date")}
                       hint="Optional. If your on-site solar or renewable system was commissioned mid-year, entering the date here contextualises the reported partial-year generation.">
                  <Input testId="input-solar-commissioning-date" type="text"
                         value={env.solar_commissioning_date}
                         onChange={e => setEnv({ ...env, solar_commissioning_date: e.target.value })}
                         placeholder="e.g. May 2024 or 2024-05-15" />
                </Field>
                <Field label={lang === "ar" ? "الكهرباء المتجدّدة المطالَب بها عبر I-RECs / PPAs (ك.و.س/سنة)" : "Renewable Electricity Claimed via I-RECs / PPAs (kWh/year)"}
                       hint="Green kWh you've contractually procured, I-REC certificates retired, PPA volume, or on-site renewables you're claiming. Powers GHG Protocol Scope 2 market-based reporting. Leave blank if none.">
                  <Input testId="input-renewable-electricity-claimed" type="number"
                         value={env.renewable_electricity_kwh}
                         onChange={e => setEnv({ ...env, renewable_electricity_kwh: e.target.value })}
                         placeholder="e.g. 200000" />
                </Field>
                <Field label="Residual-Mix Factor (kgCO₂e/kWh, optional)"
                       hint="For market-based Scope 2 accounting. If your utility publishes a residual-mix factor (grid minus what other buyers claimed via I-RECs), enter it here. Leave blank to fall back to the country grid factor.">
                  <Input testId="input-residual-mix-factor" type="number" step="0.001"
                         value={env.residual_mix_factor}
                         onChange={e => setEnv({ ...env, residual_mix_factor: e.target.value })}
                         placeholder={`default: ${GRIDS[company.country] || 0.45}`} />
                </Field>
                <Field label={lang === "ar" ? "الديزل (لتر/سنة)" : (lang === "ar" ? "الديزل (لتر/سنة)" : "Diesel (litres/year)")} hint="Generators, company fleet, boilers.">
                  <Input type="number" value={env.diesel_litres} onChange={e => setEnv({ ...env, diesel_litres: e.target.value })} placeholder="e.g. 15000" />
                </Field>
                <Field label={lang === "ar" ? "البنزين (لتر/سنة)" : (lang === "ar" ? "البنزين (لتر/سنة)" : "Petrol (litres/year)")}>
                  <Input type="number" value={env.petrol_litres} onChange={e => setEnv({ ...env, petrol_litres: e.target.value })} placeholder="e.g. 5000" />
                </Field>
                <Field label={lang === "ar" ? "الغاز الطبيعي (م³/سنة)" : (lang === "ar" ? "الغاز الطبيعي (م³/سنة)" : "Natural Gas (m³/year)")} hint="Prefer kWh from your utility bill? Enter it in the next field, we convert automatically at 10.55 kWh/m³ (DEFRA).">
                  <Input type="number" value={env.natural_gas_m3} onChange={e => setEnv({ ...env, natural_gas_m3: e.target.value })} placeholder="e.g. 2000" />
                </Field>
                <Field label={lang === "ar" ? "الغاز الطبيعي (ك.و.س/سنة، اختياري)" : (lang === "ar" ? "الغاز الطبيعي (ك.و.س/سنة، اختياري)" : "Natural Gas (kWh/year, optional)")} hint="Alternative to m³. If you fill this, leave m³ blank. Auto-converted with DEFRA net calorific value.">
                  <Input type="number" value={env.natural_gas_kwh} onChange={e => setEnv({ ...env, natural_gas_kwh: e.target.value })} placeholder="e.g. 21100" />
                </Field>
                <Field label={lang === "ar" ? "غاز البترول المسال (كغ/سنة)" : (lang === "ar" ? "غاز البترول المسال (كغ/سنة)" : "LPG (kg/year)")}>
                  <Input type="number" value={env.lpg_kg} onChange={e => setEnv({ ...env, lpg_kg: e.target.value })} placeholder="e.g. 500" />
                </Field>
              </div>
              <div className="mt-3 text-[10px] text-slate-600 italic">
                Emission factors: DEFRA 2024 (diesel 2.68, petrol 2.31, gas 2.04, LPG 3.0 kg CO₂e/unit) · Grid factor {GRIDS[company.country] || 0.45} kg CO₂e/kWh ({company.country}) · District cooling 0.6 kWh-elec/RT-hr @ COP 5.8 (Empower DC 2023).
              </div>
            </Card>

            {/* ── 2. WATER & WASTE ────────────────────────────────── */}
            <Card icon={<Drop size={18} className="text-[#01AF9B]" />} title={lang === "ar" ? "المياه والنفايات" : (lang === "ar" ? "المياه والنفايات" : "Water & Waste")}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={lang === "ar" ? "استهلاك المياه (م³/سنة)" : (lang === "ar" ? "استهلاك المياه (م³/سنة)" : "Water Consumption (m³/year)")} hint="Use the monthly panel if you have per-month bills." full>
                  <Input testId="input-water" type="number" value={env.water}
                    onChange={e => setEnv({ ...env, water: e.target.value, water_manual: true, monthly_water_m3: { jan:"", feb:"", mar:"", apr:"", may:"", jun:"", jul:"", aug:"", sep:"", oct:"", nov:"", dec:"" } })}
                    placeholder="e.g. 2000"
                    disabled={_monthlyAnyFilled(env.monthly_water_m3)}
                  />
                </Field>
                <MonthlyPanel
                  label="Monthly Water (m³), optional"
                  color="#01AF9B"
                  value={env.monthly_water_m3}
                  onChange={obj => setEnv({ ...env, monthly_water_m3: obj })}
                  unit="m³"
                  testIdPrefix="mo-water"
                  whyMonthly="Unlocks a 12-month water consumption trend chart in the PDF."
                />
                <Field label={lang === "ar" ? "المياه المُعاد تدويرها (٪)" : (lang === "ar" ? "المياه المُعاد تدويرها (٪)" : "Water Recycled (%)")}>
                  <Input type="number" min="0" max="100" value={env.water_recycled_pct} onChange={e => setEnv({ ...env, water_recycled_pct: e.target.value })} placeholder="e.g. 10" />
                </Field>
                <Field label={lang === "ar" ? "إجمالي النفايات (طن/سنة)" : (lang === "ar" ? "إجمالي النفايات (طن/سنة)" : "Total Waste (tonnes/year)")}>
                  <Input testId="input-waste" type="number" value={env.waste} onChange={e => setEnv({ ...env, waste: e.target.value })} placeholder="e.g. 12" />
                </Field>
                <Field label={lang === "ar" ? "نسبة الطمر (٪)" : (lang === "ar" ? "نسبة الطمر (٪)" : "Landfill Rate (%)")} hint="Portion of waste sent to landfill vs recycled. Landfill waste feeds Scope 3 automatically.">
                  <Input type="number" min="0" max="100" value={env.landfill_pct} onChange={e => setEnv({ ...env, landfill_pct: e.target.value })} placeholder="e.g. 65" />
                </Field>
              </div>
              <EnvWasteCoherenceNote env={env} />
            </Card>

            {/* ── 3. AUTO-DERIVED SCOPES & ENERGY TOTALS ─────────── */}
            <Card icon={<Lightning size={18} className="text-[#01AF9B]" />} title={lang === "ar" ? "الانبعاثات والطاقة، محسوبة تلقائياً" : (lang === "ar" ? "الانبعاثات والطاقة، محسوبة تلقائياً" : "Emissions & Energy, Auto-computed")} desc={lang === "ar" ? "تُستمد هذه الأرقام مباشرةً من مُدخلات العدّاد أعلاه. فعِّل خيار التجاوز في أي بطاقة لإدخال قيمة مخصّصة (مثل تدقيق خارجي لغازات الدفيئة)." : (lang === "ar" ? "تُستمد هذه الأرقام مباشرةً من مُدخلات العدّاد أعلاه. فعِّل خيار التجاوز في أي بطاقة لإدخال قيمة مخصّصة (مثل تدقيق خارجي لغازات الدفيئة)." : "These figures are derived live from the meter inputs above. Toggle Override on any card to enter a custom value (e.g. from a third-party GHG audit).")}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <AutoChip
                  label="Scope 1 (tCO₂e/yr)"
                  color="#01AF9B"
                  value={env.scope1}
                  manual={env.scope1_manual}
                  onOverride={(v) => setEnv({ ...env, scope1: v, scope1_manual: true })}
                  onReset={() => setEnv({ ...env, scope1_manual: false })}
                  testId="chip-scope1"
                />
                <AutoChip
                  label="Scope 2 (tCO₂e/yr)"
                  color="#B94B05"
                  value={env.scope2}
                  manual={env.scope2_manual}
                  onOverride={(v) => setEnv({ ...env, scope2: v, scope2_manual: true })}
                  onReset={() => setEnv({ ...env, scope2_manual: false })}
                  testId="chip-scope2"
                />
                <AutoChip
                  label="Scope 3 (tCO₂e/yr)"
                  color="#B94B05"
                  value={env.scope3}
                  manual={env.scope3_manual}
                  onOverride={(v) => setEnv({ ...env, scope3: v, scope3_manual: true })}
                  onReset={() => setEnv({ ...env, scope3_manual: false })}
                  testId="chip-scope3"
                />
                <AutoChip
                  label={lang === "ar" ? "إجمالي الطلب على الطاقة (ك.و.س/سنة، جميع المصادر)" : "Total Energy Demand (kWh/yr, all carriers)"}
                  hint="Grid + on-site renewable + fuel-equivalent kWh from diesel, petrol, gas & LPG (DEFRA conversion). Not just electricity."
                  color="#01AF9B"
                  value={env.energy}
                  manual={env.energy_manual}
                  onOverride={(v) => setEnv({ ...env, energy: v, energy_manual: true })}
                  onReset={() => setEnv({ ...env, energy_manual: false })}
                  testId="chip-energy"
                />
                <AutoChip
                  label={lang === "ar" ? "نسبة الطاقة المتجدّدة (٪)" : "Renewable Share (%)"}
                  color="#01AF9B"
                  value={env.renewable_pct}
                  manual={env.renewable_pct_manual}
                  onOverride={(v) => setEnv({ ...env, renewable_pct: v, renewable_pct_manual: true })}
                  onReset={() => setEnv({ ...env, renewable_pct_manual: false })}
                  testId="chip-renewable"
                />
                <AutoChip
                  label={`Energy Intensity (kWh / ${company.currency} 1,000)`}
                  color="#01AF9B"
                  value={env.energy_intensity}
                  manual={false}
                  readOnly
                  testId="chip-intensity"
                />
              </div>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={`Annual Revenue (${company.currency})`} hint="For energy intensity calculation" full>
                  <Input type="number" value={eiRevenue} onChange={e => setEiRevenue(e.target.value)} placeholder="e.g. 5000000" />
                </Field>
              </div>
              <EnvScopeCoherenceNote env={env} />
            </Card>

            {/* ── 4. SCOPE 3 add-ons (flights + commute) ─────────── */}
            <Card icon={<Recycle size={18} className="text-[#B94B05]" />} title={lang === "ar" ? "إضافات النطاق 3، الرحلات والتنقّل (اختياري)" : (lang === "ar" ? "إضافات النطاق 3، الرحلات والتنقّل (اختياري)" : "Scope 3 Add-ons, Flights & Commute (optional)")} desc={lang === "ar" ? "تُحتسب نفايات المكبّ تلقائياً من مُدخلات النفايات أعلاه. أضِف الرحلات والتنقّل هنا لإكمال النطاق 3." : (lang === "ar" ? "تُحتسب نفايات المكبّ تلقائياً من مُدخلات النفايات أعلاه. أضِف الرحلات والتنقّل هنا لإكمال النطاق 3." : "Landfill waste is already picked up from your Waste inputs above. Add flights and commute here to complete Scope 3.")}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <CalcField label={lang === "ar" ? "رحلات قصيرة (ساعات/سنة)" : "Short-haul flights (hrs/yr)"}><CalcInput value={s3FlyS} onChange={setS3FlyS} placeholder="e.g. 40" /></CalcField>
                <CalcField label={lang === "ar" ? "رحلات طويلة (ساعات/سنة)" : "Long-haul flights (hrs/yr)"}><CalcInput value={s3FlyL} onChange={setS3FlyL} placeholder="e.g. 20" /></CalcField>
                <CalcField label={lang === "ar" ? "تنقّل الموظفين (كم ذهاباً وإياباً/يوم)" : "Employee commute (round-trip km/day)"}><CalcInput value={s3Commute} onChange={setS3Commute} placeholder="e.g. 40" /></CalcField>
              </div>
              <div className="mt-3 text-[10px] text-slate-500">
                Flights 0.255/0.166 kg/pkm (economy) · Commute 0.17 kg/km × 230 workdays · Landfill waste 0.5 tCO₂e/t. Source: DEFRA 2024.
              </div>
            </Card>

            {/* ── 5. TARGETS & CLIMATE RISK ──────────────────────── */}
            <Card icon={<TreeEvergreen size={18} className="text-[#01AF9B]" />} title={lang === "ar" ? "هدف التخفيض، مخاطر المناخ والمبادرات" : (lang === "ar" ? "هدف التخفيض، مخاطر المناخ والمبادرات" : "Reduction Target, Climate Risk & Initiatives")}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={lang === "ar" ? "هدف التخفيض" : (lang === "ar" ? "هدف التخفيض" : "Reduction Target")} full>
                  <Input testId="input-target" value={env.reduction_target} onChange={e => setEnv({ ...env, reduction_target: e.target.value })} placeholder="e.g. Reduce GHG by 25% by 2030" />
                </Field>
                <Field label={lang === "ar" ? "سنة الهدف — الموعد النهائي (اختياري)" : (lang === "ar" ? "سنة الهدف — الموعد النهائي (اختياري)" : "Target Year — deadline (optional)")} hint="Year by which you commit to reach the target. Example: 2030.">
                  <Input type="number" value={env.energy_target_year} onChange={e => setEnv({ ...env, energy_target_year: e.target.value })} placeholder="e.g. 2030" />
                </Field>
                <Field label={lang === "ar" ? "هدف نسبة الطاقة المتجدّدة ٪ (اختياري)" : (lang === "ar" ? "هدف نسبة الطاقة المتجدّدة ٪ (اختياري)" : "Renewable Share Target % (optional)")} hint="Your renewable-energy goal for the target year. Example: 50% of total energy from renewables by 2030.">
                  <Input type="number" min="0" max="100" value={env.energy_target_pct} onChange={e => setEnv({ ...env, energy_target_pct: e.target.value })} placeholder="e.g. 50" />
                </Field>
                <div className="sm:col-span-2 rounded-lg border border-teal-900/40 bg-teal-950/20 px-4 py-3 text-[11.5px] text-teal-100/80 leading-relaxed">
                  <span className="font-semibold text-teal-200">{lang === "ar" ? "خط الأساس مُستخرج تلقائياً." : "Baseline is auto-derived."}</span> Your target's reference year is set to <b>the previous reporting year</b> and the baseline emissions / energy are pulled directly from the Previous Year (Y-1) fields below. This removes duplicate entry and guarantees the target trajectory matches the trend chart.
                </div>
                <Field label={lang === "ar" ? "تقييم مخاطر المناخ (TCFD)" : (lang === "ar" ? "تقييم مخاطر المناخ (TCFD)" : "Climate Risk Assessment (TCFD)")} full>
                  <TextArea testId="input-climate" value={env.climate_risk} onChange={e => setEnv({ ...env, climate_risk: e.target.value })} placeholder={lang === "ar" ? "ما مخاطر المناخ التي قد تؤثر على عملك؟" : "What climate-related risks could affect your business?"} />
                  <AnyLangHint lang={lang} />
                </Field>
                <Field label={lang === "ar" ? "المبادرات البيئية الرئيسية" : (lang === "ar" ? "المبادرات البيئية الرئيسية" : "Key Environmental Initiatives")} full>
                  <TextArea testId="input-initiatives" value={env.initiatives} onChange={e => setEnv({ ...env, initiatives: e.target.value })} placeholder={lang === "ar" ? "مثل: تركيب ألواح شمسية، تحول إلى LED..." : "e.g. Installed rooftop solar, switched to LED..."} />
                  <AnyLangHint lang={lang} />
                </Field>
              </div>
            </Card>

            <Card icon={<TreeEvergreen size={18} className="text-[#01AF9B]" />} title={lang === "ar" ? "البيانات البيئية للسنة السابقة (س-1)" : (lang === "ar" ? "البيانات البيئية للسنة السابقة (س-1)" : "Previous Year Environmental Data (Y-1)")} desc={lang === "ar" ? "الإجماليات السنوية لتحليل الاتجاهات بين السنوات. إذا توفّر لديك س-2 أدناه، سيعرض ملف PDF جداول اتجاهات كاملة لثلاث سنوات." : (lang === "ar" ? "الإجماليات السنوية لتحليل الاتجاهات بين السنوات. إذا توفّر لديك س-2 أدناه، سيعرض ملف PDF جداول اتجاهات كاملة لثلاث سنوات." : "Annual totals for year-over-year trend analysis. If you also have Y-2 below, the PDF will render full 3-year trend tables.")}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={lang === "ar" ? "النطاق 1 (السنة السابقة، طن CO2e)" : "Scope 1 (previous year, tCO2e)"}>
                  <Input type="number" value={env.prev_scope1} onChange={e => setEnv({ ...env, prev_scope1: e.target.value })} placeholder="e.g. 25" />
                </Field>
                <Field label={lang === "ar" ? "النطاق 2 (السنة السابقة، طن CO2e)" : "Scope 2 (previous year, tCO2e)"}>
                  <Input type="number" value={env.prev_scope2} onChange={e => setEnv({ ...env, prev_scope2: e.target.value })} placeholder="e.g. 280" />
                </Field>
                <Field label={lang === "ar" ? "النطاق 3 (السنة السابقة، طن CO2e)" : "Scope 3 (previous year, tCO2e)"}>
                  <Input type="number" value={env.prev_scope3} onChange={e => setEnv({ ...env, prev_scope3: e.target.value })} placeholder="e.g. 90" />
                </Field>
                <Field label={lang === "ar" ? "إجمالي الطلب على الطاقة (السنة السابقة، ك.و.س، جميع المصادر)" : "Total Energy Demand (previous year, kWh, all carriers)"} hint="Sum of grid + on-site renewable + fuel-equivalent kWh for the prior year.">
                  <Input type="number" value={env.prev_energy} onChange={e => setEnv({ ...env, prev_energy: e.target.value })} placeholder="e.g. 480000" />
                </Field>
                <Field label={lang === "ar" ? "نسبة الطاقة المتجدّدة (السنة السابقة، ٪)" : "Renewable Share (previous year, %)"}>
                  <Input type="number" min="0" max="100" value={env.prev_renewable_pct} onChange={e => setEnv({ ...env, prev_renewable_pct: e.target.value })} placeholder="e.g. 10" />
                </Field>
                <Field label={lang === "ar" ? "المياه (السنة السابقة، م3)" : "Water (previous year, m3)"}>
                  <Input type="number" value={env.prev_water} onChange={e => setEnv({ ...env, prev_water: e.target.value })} placeholder="e.g. 2200" />
                </Field>
                <Field label={lang === "ar" ? "النفايات (السنة السابقة، طن)" : "Waste (previous year, tonnes)"}>
                  <Input type="number" value={env.prev_waste} onChange={e => setEnv({ ...env, prev_waste: e.target.value })} placeholder="e.g. 15" />
                </Field>
              </div>
            </Card>

            <Card icon={<TreeEvergreen size={18} className="text-[#01AF9B]" />} title={lang === "ar" ? "البيانات البيئية قبل سنتين (س-2، اختياري)" : (lang === "ar" ? "البيانات البيئية قبل سنتين (س-2، اختياري)" : "Two Years Ago Environmental Data (Y-2, optional)")} desc={lang === "ar" ? "اختياري. إذا توفّرت لديك بيانات من قبل سنتين، سيعرض ملف PDF جداول اتجاهات كاملة لثلاث سنوات. اتركها فارغة إذا كان لديك سنتان فقط من البيانات." : (lang === "ar" ? "اختياري. إذا توفّرت لديك بيانات من قبل سنتين، سيعرض ملف PDF جداول اتجاهات كاملة لثلاث سنوات. اتركها فارغة إذا كان لديك سنتان فقط من البيانات." : "Optional. If you have data from two years ago, your PDF will render full 3-year trend tables. Leave blank if you only have two years of history.")}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={lang === "ar" ? "النطاق 1 (س-2، طن CO2e)" : "Scope 1 (Y-2, tCO2e)"}>
                  <Input type="number" value={env.prev2_scope1} onChange={e => setEnv({ ...env, prev2_scope1: e.target.value })} placeholder="optional" />
                </Field>
                <Field label={lang === "ar" ? "النطاق 2 (س-2، طن CO2e)" : "Scope 2 (Y-2, tCO2e)"}>
                  <Input type="number" value={env.prev2_scope2} onChange={e => setEnv({ ...env, prev2_scope2: e.target.value })} placeholder="optional" />
                </Field>
                <Field label={lang === "ar" ? "النطاق 3 (س-2، طن CO2e)" : "Scope 3 (Y-2, tCO2e)"}>
                  <Input type="number" value={env.prev2_scope3} onChange={e => setEnv({ ...env, prev2_scope3: e.target.value })} placeholder="optional" />
                </Field>
                <Field label={lang === "ar" ? "إجمالي الطلب على الطاقة (س-2، ك.و.س، جميع المصادر)" : "Total Energy Demand (Y-2, kWh, all carriers)"} hint="Sum of grid + on-site renewable + fuel-equivalent kWh for two years ago.">
                  <Input type="number" value={env.prev2_energy} onChange={e => setEnv({ ...env, prev2_energy: e.target.value })} placeholder="optional" />
                </Field>
                <Field label={lang === "ar" ? "نسبة الطاقة المتجدّدة (س-2، ٪)" : "Renewable Share (Y-2, %)"}>
                  <Input type="number" min="0" max="100" value={env.prev2_renewable_pct} onChange={e => setEnv({ ...env, prev2_renewable_pct: e.target.value })} placeholder="optional" />
                </Field>
                <Field label={lang === "ar" ? "المياه (س-2، م3)" : "Water (Y-2, m3)"}>
                  <Input type="number" value={env.prev2_water} onChange={e => setEnv({ ...env, prev2_water: e.target.value })} placeholder="optional" />
                </Field>
                <Field label={lang === "ar" ? "النفايات (س-2، طن)" : "Waste (Y-2, tonnes)"}>
                  <Input type="number" value={env.prev2_waste} onChange={e => setEnv({ ...env, prev2_waste: e.target.value })} placeholder="optional" />
                </Field>
              </div>
            </Card>

            <Card icon={<Recycle size={18} className="text-[#01AF9B]" />} title={lang === "ar" ? "الشهادات والتنوّع الحيوي" : (lang === "ar" ? "الشهادات والتنوّع الحيوي" : "Certifications & Biodiversity")} desc={lang === "ar" ? "شهادات نظام الإدارة البيئية والتنوّع الحيوي" : (lang === "ar" ? "شهادات نظام الإدارة البيئية والتنوّع الحيوي" : "Environmental management system certifications and biodiversity")}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={lang === "ar" ? "الشهادات البيئية" : (lang === "ar" ? "الشهادات البيئية" : "Environmental Certifications")} full hint="e.g. ISO 14001, ISO 50001, EMS">
                  <TextArea value={env.env_certifications} onChange={e => setEnv({ ...env, env_certifications: e.target.value })} placeholder="e.g. ISO 14001:2015 Environmental Management System, LEED certified offices..." />
                </Field>
                <Field label={lang === "ar" ? "بيان التنوّع الحيوي" : (lang === "ar" ? "بيان التنوّع الحيوي" : "Biodiversity Statement")} full hint="Impact on ecosystems or sensitive areas">
                  <TextArea value={env.biodiversity_statement} onChange={e => setEnv({ ...env, biodiversity_statement: e.target.value })} placeholder="e.g. No operations in protected areas, wildlife corridor restoration..." />
                </Field>
                <Field label={lang === "ar" ? "مواءمة هدف المناخ (TCFD / اتفاقية باريس)" : (lang === "ar" ? "مواءمة هدف المناخ (TCFD / اتفاقية باريس)" : "Climate Target Alignment (TCFD / Paris Agreement)")} full hint="Are your emission reduction targets aligned with a scientific pathway?">
                  <Select value={env.climate_alignment_status} onChange={e => setEnv({ ...env, climate_alignment_status: e.target.value })} data-testid="select-climate-alignment">
                    <option value="">- Select -</option>
                    <option value="1.5C">Aligned with 1.5°C pathway (SBTi or equivalent)</option>
                    <option value="2C">Aligned with 2°C pathway (well-below 2°C)</option>
                    <option value="under_development">{lang === "ar" ? "قيد التطوير (التزام قائم، الأهداف تُحدَّد)" : "Under development (commitment made, target-setting in progress)"}</option>
                    <option value="not_yet">Not yet aligned / targets not set</option>
                  </Select>
                </Field>
              </div>
              <DataQualityBlock sectionName="Environmental" lang={lang} value={env.data_quality} onChange={v => setEnv({ ...env, data_quality: v })} />
            </Card>

            {/* Environmental Chapter Photos removed, the GHG Emissions and Waste
                Management pages are data-heavy and photos placed there caused
                downstream orphan pages in EN/AR. Chart pages remain photo-free. */}

            {/* Environmental section final gate, blocks Next if errors, warns on warnings, greenlights on clean. */}
            <EnvCompletionGate env={env} onNext={goNext} onBack={goBack} saving={saving} />
          </div>
        )}

        {/* Step 2: Social */}
        {step === 2 && (
          <div className="animate-fade-in-up" data-testid="step-social">
            <div className="bg-[#B94B05]/5 border-l-2 border-[#B94B05] rounded-r-lg px-4 py-3 text-sm text-[#B94B05] mb-5">
              {lang === "ar" ? "تركّز البيانات الاجتماعية على موظفيك، والسلامة، والرفاه، والتنوّع، والتطوير. وزنها كبير في تقارير دول الخليج و ADX." : "Social data focuses on your people, safety, wellbeing, diversity and development. Heavily weighted in GCC/ADX reporting."}
            </div>

            <Card icon={<Users size={18} className="text-[#B94B05]" />} title={lang === "ar" ? "نظرة عامة على القوى العاملة" : (lang === "ar" ? "نظرة عامة على القوى العاملة" : "Workforce Overview")} desc={lang === "ar" ? "إجمالي العدد ومؤشرات التنوّع العامة. استخدم اللوحة الشهرية إذا نمت القوى العاملة أو تقلّصت خلال السنة؛ سيُحتسب إجمالي الموظفين كمتوسط." : (lang === "ar" ? "إجمالي العدد ومؤشرات التنوّع العامة. استخدم اللوحة الشهرية إذا نمت القوى العاملة أو تقلّصت خلال السنة؛ سيُحتسب إجمالي الموظفين كمتوسط." : "Total headcount and overall diversity metrics. Use the monthly panel if your workforce grew or shrank across the year, total employees will be the AVERAGE.")}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={lang === "ar" ? "إجمالي الموظفين (السنة الحالية)" : (lang === "ar" ? "إجمالي الموظفين (السنة الحالية)" : "Total Employees (current year)")} hint={_monthlyAnyFilled(soc.monthly_headcount) ? "Auto-derived as the AVERAGE of the 12 monthly values." : "Or use the monthly panel below."}>
                  <Input testId="input-employees" type="number" value={soc.total_employees}
                    onChange={e => setSoc({ ...soc, total_employees: e.target.value, total_employees_manual: true, monthly_headcount: { jan:"", feb:"", mar:"", apr:"", may:"", jun:"", jul:"", aug:"", sep:"", oct:"", nov:"", dec:"" } })}
                    placeholder="e.g. 120"
                    disabled={_monthlyAnyFilled(soc.monthly_headcount)}
                  />
                </Field>
                <Field label={lang === "ar" ? "إجمالي الموظفين (السنة السابقة)" : (lang === "ar" ? "إجمالي الموظفين (السنة السابقة)" : "Total Employees (previous year)")} hint="For year-over-year comparison">
                  <Input type="number" value={soc.prev_total_employees} onChange={e => setSoc({ ...soc, prev_total_employees: e.target.value })} placeholder="e.g. 105" />
                </Field>
                <MonthlyPanel
                  label={lang === "ar" ? "عدد الموظفين الشهري، اختياري" : "Monthly Headcount, optional"}
                  color="#B94B05"
                  value={soc.monthly_headcount}
                  onChange={obj => setSoc({ ...soc, monthly_headcount: obj })}
                  unit="employees (avg)"
                  testIdPrefix="mo-headcount"
                  aggregation="avg"
                  whyMonthly="Unlocks a 12-month workforce headcount trend chart in the PDF."
                />
                <Field label={lang === "ar" ? "٪ الموظفات (السنة الحالية)" : (lang === "ar" ? "٪ الموظفات (السنة الحالية)" : "Female Employees % (current year)")} hint={((soc.senior_mgmt_female || soc.middle_mgmt_female || soc.technical_female || soc.admin_female) && soc.total_employees) ? "Auto-derived from the Workforce by Management Level breakdown below. Fill those cells to update this figure." : "Enter the % of female employees, or fill the Management Level breakdown below to auto-derive it."}>
                  <Input testId="input-female-pct" type="number" min="0" max="100"
                    value={(() => {
                      const fem = (parseInt(soc.senior_mgmt_female || 0, 10) || 0)
                                + (parseInt(soc.middle_mgmt_female || 0, 10) || 0)
                                + (parseInt(soc.technical_female || 0, 10) || 0)
                                + (parseInt(soc.admin_female || 0, 10) || 0);
                      const tot = parseFloat(soc.total_employees || 0) || 0;
                      if (fem > 0 && tot > 0) return Math.round((fem / tot) * 1000) / 10;
                      return soc.female_pct;
                    })()}
                    onChange={e => setSoc({ ...soc, female_pct: e.target.value })}
                    placeholder="e.g. 35"
                    readOnly={((parseInt(soc.senior_mgmt_female || 0, 10) || 0)
                              + (parseInt(soc.middle_mgmt_female || 0, 10) || 0)
                              + (parseInt(soc.technical_female || 0, 10) || 0)
                              + (parseInt(soc.admin_female || 0, 10) || 0)) > 0
                              && parseFloat(soc.total_employees || 0) > 0}
                  />
                </Field>
                <Field label={lang === "ar" ? "٪ الموظفات (السنة السابقة)" : (lang === "ar" ? "٪ الموظفات (السنة السابقة)" : "Female Employees % (previous year)")}>
                  <Input type="number" min="0" max="100" value={soc.prev_female_pct} onChange={e => setSoc({ ...soc, prev_female_pct: e.target.value })} placeholder="e.g. 30" />
                </Field>
                <Field label={lang === "ar" ? "التوطين / القوى العاملة المحلية (٪)" : (lang === "ar" ? "التوطين / القوى العاملة المحلية (٪)" : "Nationalization / Local Workforce (%)")}>
                  <Input testId="input-local-pct" type="number" min="0" max="100" value={soc.local_pct} onChange={e => setSoc({ ...soc, local_pct: e.target.value })} placeholder="e.g. 15" />
                  <LocalPctHint pct={soc.local_pct} total={soc.total_employees} lang={lang} />
                </Field>
                <Field label={lang === "ar" ? "التنوّع الثقافي / العرقي (٪، اختياري)" : (lang === "ar" ? "التنوّع الثقافي / العرقي (٪، اختياري)" : "Cultural / Ethnicity Diversity (%, optional)")} hint="For geographies where nationalization is not the relevant lens. Share of workforce from under-represented cultural or ethnic groups.">
                  <Input type="number" min="0" max="100" value={soc.cultural_diversity_pct} onChange={e => setSoc({ ...soc, cultural_diversity_pct: e.target.value })} placeholder="e.g. 42" />
                </Field>
                <Field label={lang === "ar" ? "نسبة الأجر بين الجنسين" : (lang === "ar" ? "نسبة الأجر بين الجنسين" : "Gender Pay Ratio")} hint="Ratio = female average pay ÷ male average pay across the workforce. 1.00 = perfect parity, 0.95 means women earn 95% of men on average. Enter as ratio (e.g. 0.95) or as X:Y (e.g. 1:1).">
                  <Input value={soc.gender_pay_ratio} onChange={e => setSoc({ ...soc, gender_pay_ratio: e.target.value })} placeholder="e.g. 1:1 (equal pay)" />
                </Field>
              </div>
            </Card>

            <Card icon={<Users size={18} className="text-[#B94B05]" />} title={lang === "ar" ? "القوى العاملة حسب المستوى الإداري" : (lang === "ar" ? "القوى العاملة حسب المستوى الإداري" : "Workforce by Management Level")} desc={lang === "ar" ? "التوزيع حسب الجنس عبر المستويات التنظيمية" : (lang === "ar" ? "التوزيع حسب الجنس عبر المستويات التنظيمية" : "Gender breakdown across organizational levels")}>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Field label={lang === "ar" ? "الإدارة العليا، ذكور" : (lang === "ar" ? "الإدارة العليا، ذكور" : "Senior Mgmt, Male")}>
                  <Input type="number" value={soc.senior_mgmt_male} onChange={e => setSoc({ ...soc, senior_mgmt_male: e.target.value })} placeholder="e.g. 6" />
                </Field>
                <Field label={lang === "ar" ? "الإدارة العليا، إناث" : (lang === "ar" ? "الإدارة العليا، إناث" : "Senior Mgmt, Female")}>
                  <Input type="number" value={soc.senior_mgmt_female} onChange={e => setSoc({ ...soc, senior_mgmt_female: e.target.value })} placeholder="e.g. 2" />
                </Field>
                <Field label={lang === "ar" ? "الإدارة الوسطى، ذكور" : (lang === "ar" ? "الإدارة الوسطى، ذكور" : "Middle Mgmt, Male")}>
                  <Input type="number" value={soc.middle_mgmt_male} onChange={e => setSoc({ ...soc, middle_mgmt_male: e.target.value })} placeholder="e.g. 12" />
                </Field>
                <Field label={lang === "ar" ? "الإدارة الوسطى، إناث" : (lang === "ar" ? "الإدارة الوسطى، إناث" : "Middle Mgmt, Female")}>
                  <Input type="number" value={soc.middle_mgmt_female} onChange={e => setSoc({ ...soc, middle_mgmt_female: e.target.value })} placeholder="e.g. 5" />
                </Field>
                <Field label={lang === "ar" ? "فنيون/مهرة، ذكور" : (lang === "ar" ? "فنيون/مهرة، ذكور" : "Technical/Skilled, Male")}>
                  <Input type="number" value={soc.technical_male} onChange={e => setSoc({ ...soc, technical_male: e.target.value })} placeholder="e.g. 30" />
                </Field>
                <Field label={lang === "ar" ? "فنيون/مهرة، إناث" : (lang === "ar" ? "فنيون/مهرة، إناث" : "Technical/Skilled, Female")}>
                  <Input type="number" value={soc.technical_female} onChange={e => setSoc({ ...soc, technical_female: e.target.value })} placeholder="e.g. 3" />
                </Field>
                <Field label={lang === "ar" ? "إداريون، ذكور" : (lang === "ar" ? "إداريون، ذكور" : "Administrative, Male")}>
                  <Input type="number" value={soc.admin_male} onChange={e => setSoc({ ...soc, admin_male: e.target.value })} placeholder="e.g. 4" />
                </Field>
                <Field label={lang === "ar" ? "إداريون، إناث" : (lang === "ar" ? "إداريون، إناث" : "Administrative, Female")}>
                  <Input type="number" value={soc.admin_female} onChange={e => setSoc({ ...soc, admin_female: e.target.value })} placeholder="e.g. 10" />
                </Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4 pt-4 border-t border-slate-800">
                <Field label={lang === "ar" ? "موظفون دون الثلاثين" : (lang === "ar" ? "موظفون دون الثلاثين" : "Employees Under 30")}>
                  <Input type="number" value={soc.age_under30} onChange={e => setSoc({ ...soc, age_under30: e.target.value })} placeholder="e.g. 25" />
                </Field>
                <Field label={lang === "ar" ? "موظفون 30-50" : (lang === "ar" ? "موظفون 30-50" : "Employees 30-50")}>
                  <Input type="number" value={soc.age_30to50} onChange={e => setSoc({ ...soc, age_30to50: e.target.value })} placeholder="e.g. 70" />
                </Field>
                <Field label={lang === "ar" ? "موظفون فوق الخمسين" : (lang === "ar" ? "موظفون فوق الخمسين" : "Employees Over 50")}>
                  <Input type="number" value={soc.age_over50} onChange={e => setSoc({ ...soc, age_over50: e.target.value })} placeholder="e.g. 25" />
                </Field>
              </div>
              <WorkforceCoherenceNote soc={soc} />
            </Card>

            <Card icon={<Users size={18} className="text-[#B94B05]" />} title={lang === "ar" ? "التوظيف ودوران العمالة" : (lang === "ar" ? "التوظيف ودوران العمالة" : "Hiring & Turnover")} desc={lang === "ar" ? "الإجماليان فقط، نحسب النسبة تلقائياً (GRI 401-1)" : (lang === "ar" ? "الإجماليان فقط، نحسب النسبة تلقائياً (GRI 401-1)" : "Just the two totals, we compute the % automatically (GRI 401-1)")}>
              <HiringTurnoverSection soc={soc} setSoc={setSoc} lang={lang} />
              <HiringTurnoverCoherenceNote soc={soc} />
            </Card>

            <Card icon={<Users size={18} className="text-[#B94B05]" />} title={lang === "ar" ? "التدريب والأداء" : (lang === "ar" ? "التدريب والأداء" : "Training & Performance")} desc={lang === "ar" ? "برامج التطوير، تغطية التقييم" : (lang === "ar" ? "برامج التطوير، تغطية التقييم" : "Development programs, review coverage")}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={lang === "ar" ? "ساعات التدريب لكل موظف (الحالية)" : (lang === "ar" ? "ساعات التدريب لكل موظف (الحالية)" : "Training Hours per Employee (current)")}>
                  <Input testId="input-training" type="number" value={soc.training_hours} onChange={e => setSoc({ ...soc, training_hours: e.target.value })} placeholder="e.g. 24" />
                </Field>
                <Field label={lang === "ar" ? "ساعات التدريب (السنة السابقة)" : (lang === "ar" ? "ساعات التدريب (السنة السابقة)" : "Training Hours (previous year)")}>
                  <Input type="number" value={soc.prev_training_hours} onChange={e => setSoc({ ...soc, prev_training_hours: e.target.value })} placeholder="e.g. 18" />
                </Field>
                <Field label={lang === "ar" ? "٪ تغطية تقييم الأداء" : (lang === "ar" ? "٪ تغطية تقييم الأداء" : "Performance Review Coverage %")} hint="% of employees who received annual reviews">
                  <Input type="number" min="0" max="100" value={soc.performance_review_pct} onChange={e => setSoc({ ...soc, performance_review_pct: e.target.value })} placeholder="e.g. 100" />
                </Field>
                <Field label={lang === "ar" ? "برامج التدريب" : (lang === "ar" ? "برامج التدريب" : "Training Programs")} hint="Key programs offered" full>
                  <TextArea value={soc.training_programs} onChange={e => setSoc({ ...soc, training_programs: e.target.value })} placeholder={lang === "ar" ? "مثل: تطوير القيادة، المهارات التقنية، التوعية بـ ESG، شهادات السلامة..." : "e.g. Leadership development, Technical skills, ESG awareness, Safety certifications..."} />
                  <AnyLangHint lang={lang} />
                </Field>
              </div>
            </Card>

            <Card icon={<Users size={18} className="text-[#B94B05]" />} title={lang === "ar" ? "إجازة الأبوة والأمومة" : (lang === "ar" ? "إجازة الأبوة والأمومة" : "Parental Leave")} desc={lang === "ar" ? "بيانات الاستحقاق والعودة إلى العمل" : (lang === "ar" ? "بيانات الاستحقاق والعودة إلى العمل" : "Entitlement and return-to-work data")}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={lang === "ar" ? "موظفون أخذوا إجازة الأبوة" : "Male employees who took parental leave"}>
                  <Input type="number" value={soc.parental_taken_male} onChange={e => setSoc({ ...soc, parental_taken_male: e.target.value })} placeholder="e.g. 5" />
                </Field>
                <Field label={lang === "ar" ? "موظفات أخذن إجازة الأبوة" : "Female employees who took parental leave"}>
                  <Input type="number" value={soc.parental_taken_female} onChange={e => setSoc({ ...soc, parental_taken_female: e.target.value })} placeholder="e.g. 3" />
                </Field>
                <Field label={lang === "ar" ? "عودة الذكور بعد الإجازة" : "Male returned after leave"}>
                  <Input type="number" value={soc.parental_returned_male} onChange={e => setSoc({ ...soc, parental_returned_male: e.target.value })} placeholder="e.g. 5" />
                </Field>
                <Field label={lang === "ar" ? "عودة الإناث بعد الإجازة" : "Female returned after leave"}>
                  <Input type="number" value={soc.parental_returned_female} onChange={e => setSoc({ ...soc, parental_returned_female: e.target.value })} placeholder="e.g. 3" />
                </Field>
              </div>
              <ParentalLeaveCoherenceNote soc={soc} />
            </Card>

            <Card icon={<Users size={18} className="text-[#B94B05]" />} title={lang === "ar" ? "الصحة والسلامة (الموسّعة)" : (lang === "ar" ? "الصحة والسلامة (الموسّعة)" : "Health & Safety (Expanded)")} desc={lang === "ar" ? "أدخِل حوادث الإصابات الموجبة للتغيّب الشهرية وساعات العمل، يُحتسب LTIR تلقائياً. يمكن تجاوز الحقول." : (lang === "ar" ? "أدخِل حوادث الإصابات الموجبة للتغيّب الشهرية وساعات العمل، يُحتسب LTIR تلقائياً. يمكن تجاوز الحقول." : "Enter monthly LTI incidents and working hours, LTIR is computed automatically. Fields can be overridden.")}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InjuryConflictAlert lang={lang} soc={soc} legacySum={legacyMonthlyInjuries}
                  onUseMonthly={(n) => setSoc({ ...soc, lti_count: String(n), lti_count_manual: true })} />
                <Field label={lang === "ar" ? "حوادث LTI (إجمالي هذا العام)" : "LTI incidents (total this year)"} hint={_monthlyAnyFilled(soc.monthly_lti_count) ? "Auto-derived as the SUM of the 12 monthly values." : "Number of lost-time injuries. Feeds LTIR."}>
                  <Input type="number" value={soc.lti_count}
                    onChange={e => setSoc({ ...soc, lti_count: e.target.value, lti_count_manual: true, monthly_lti_count: { jan:"", feb:"", mar:"", apr:"", may:"", jun:"", jul:"", aug:"", sep:"", oct:"", nov:"", dec:"" } })}
                    placeholder="e.g. 2"
                    disabled={_monthlyAnyFilled(soc.monthly_lti_count)}
                  />
                </Field>
                <Field label={lang === "ar" ? "إجمالي ساعات العمل" : "Total Working Hours"} hint={_monthlyAnyFilled(soc.monthly_working_hours) ? "Auto-derived as the SUM of the 12 monthly values." : "Total person-hours for LTIR. Auto-suggested from employees × 2,080 h/yr."}>
                  <div className="flex gap-2">
                    <Input type="number" value={soc.total_working_hours}
                      onChange={e => setSoc({ ...soc, total_working_hours: e.target.value, total_working_hours_manual: true, monthly_working_hours: { jan:"", feb:"", mar:"", apr:"", may:"", jun:"", jul:"", aug:"", sep:"", oct:"", nov:"", dec:"" } })}
                      placeholder="e.g. 250000"
                      disabled={_monthlyAnyFilled(soc.monthly_working_hours)}
                    />
                    {soc.total_employees && !_monthlyAnyFilled(soc.monthly_working_hours) && (
                      <button type="button" data-testid="auto-working-hours"
                        onClick={() => setSoc(s => ({ ...s, total_working_hours: String((parseInt(s.total_employees) || 0) * 2080), total_working_hours_manual: true }))}
                        className="flex-shrink-0 px-3 py-2 text-xs font-semibold rounded-lg border border-[#01AF9B]/40 text-[#01AF9B] hover:bg-[#01AF9B]/10 whitespace-nowrap">
                        Auto-fill
                      </button>
                    )}
                  </div>
                  {soc.total_employees && soc.total_working_hours && !_monthlyAnyFilled(soc.monthly_working_hours) && (
                    (() => {
                      const emp = parseInt(soc.total_employees) || 0;
                      const hrs = parseInt(soc.total_working_hours) || 0;
                      const expected = emp * 2080;
                      if (hrs < emp * 500 || hrs > emp * 3500) {
                        return <div className="text-xs text-amber-500 mt-1">⚠ This value seems unusual for {emp} employees. A typical value is ~{expected.toLocaleString()} (employees × 2,080h).</div>;
                      }
                      return null;
                    })()
                  )}
                </Field>
                <MonthlyPanel
                  label={lang === "ar" ? "حوادث LTI الشهرية، اختياري" : "Monthly LTI incidents, optional"}
                  color="#B94B05"
                  value={soc.monthly_lti_count}
                  onChange={obj => setSoc({ ...soc, monthly_lti_count: obj })}
                  unit="incidents"
                  testIdPrefix="mo-lti"
                  aggregation="sum"
                  whyMonthly="Unlocks a 12-month safety trend chart in the PDF."
                />
                <MonthlyPanel
                  label={lang === "ar" ? "ساعات العمل الشهرية، اختياري" : "Monthly Working Hours, optional"}
                  color="#B94B05"
                  value={soc.monthly_working_hours}
                  onChange={obj => setSoc({ ...soc, monthly_working_hours: obj })}
                  unit="hours"
                  testIdPrefix="mo-hours"
                  aggregation="sum"
                  whyMonthly="Unlocks a 12-month working-hours trend chart in the PDF."
                />
              </div>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                <AutoChip
                  label={lang === "ar" ? "LTIR (لكل مليون ساعة)" : "LTIR (per million hrs)"}
                  color="#B94B05"
                  value={soc.injury_rate}
                  manual={soc.injury_rate_manual}
                  onOverride={(v) => setSoc({ ...soc, injury_rate: v, injury_rate_manual: true })}
                  onReset={() => setSoc({ ...soc, injury_rate_manual: false })}
                  testId="chip-ltir"
                />
                {/* Plausibility warning: LTIR > 10 is very high (UAE SME
                    benchmark is 1–5). Usually caused by too-low working
                    hours. Only shows for the auto-derived value; manual
                    overrides are trusted. */}
                {(() => {
                  const ltir = parseFloat(soc.injury_rate);
                  const twh  = parseFloat(soc.total_working_hours);
                  const emp  = parseFloat(soc.total_employees);
                  if (!soc.injury_rate_manual && Number.isFinite(ltir) && ltir > 10) {
                    const expected = Number.isFinite(emp) ? emp * 2080 : null;
                    return (
                      <div className="md:col-span-2 rounded-xl border border-[#F59E0B]/30 bg-[#F59E0B]/5 p-3" data-testid="ltir-warning">
                        <div className="text-[10px] font-['Outfit'] font-semibold uppercase tracking-wider text-[#F59E0B] mb-1">
                          ⚠ Plausibility check
                        </div>
                        <div className="text-xs text-slate-200 leading-relaxed">
                          LTIR of <b>{ltir.toFixed(2)}</b> is very high, typical UAE SME benchmark is <b>1–5 per million hours</b>. Verify Total Working Hours (<b>{twh ? twh.toLocaleString() : "-"}</b>) reflects <b>ALL employees × ~2,080 h/year</b>{expected ? <>, expected ≈ <b>{expected.toLocaleString()}</b> for {emp} FTE</> : null}. If the figure is correct, please include a mitigation narrative under Wellbeing / HR Policies below.
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>

              {/* ── PREVIOUS YEAR H&S, DERIVED from incidents & hours ── */}
              <div className="mt-4 rounded-xl border border-slate-800 bg-[#0B0C10]/40 p-4">
                <div className="font-['Outfit'] text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-400 mb-3">
                  Previous year H&S (for YoY comparison)
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Field label={lang === "ar" ? "حوادث LTI (السنة السابقة)" : "LTI Incidents (previous year)"} hint="Number of lost-time injuries. LTIR is auto-computed below.">
                    <Input type="number" value={soc.prev_lti_count}
                      onChange={e => setSoc({ ...soc, prev_lti_count: e.target.value })}
                      data-testid="input-prev-lti-count"
                      placeholder="e.g. 2" />
                  </Field>
                  <Field label={lang === "ar" ? "ساعات العمل (السنة السابقة)" : "Working Hours (previous year)"} hint={`Auto-suggested from ${soc.prev_total_employees || "prev-year employees"} × 2,080h. Override if you know the exact figure.`}>
                    <div className="flex gap-2">
                      <Input type="number" value={soc.prev_total_working_hours}
                        onChange={e => setSoc({ ...soc, prev_total_working_hours: e.target.value, prev_working_hours_manual: true })}
                        data-testid="input-prev-working-hours"
                        placeholder="e.g. 218,000" />
                      {soc.prev_total_employees && (
                        <button type="button"
                          onClick={() => setSoc(s => ({ ...s, prev_total_working_hours: String((parseInt(s.prev_total_employees) || 0) * 2080), prev_working_hours_manual: false }))}
                          className="flex-shrink-0 px-3 py-2 text-xs font-semibold rounded-lg border border-[#01AF9B]/40 text-[#01AF9B] hover:bg-[#01AF9B]/10 whitespace-nowrap"
                          data-testid="btn-prev-hours-autofill">
                          Auto
                        </button>
                      )}
                    </div>
                  </Field>
                  <AutoChip
                    label={lang === "ar" ? "LTIR (السنة السابقة)" : "LTIR (previous year)"}
                    color="#B94B05"
                    value={soc.prev_injury_rate}
                    manual={soc.prev_injury_rate_manual}
                    onOverride={(v) => setSoc({ ...soc, prev_injury_rate: v, prev_injury_rate_manual: true })}
                    onReset={() => setSoc({ ...soc, prev_injury_rate_manual: false })}
                    testId="chip-prev-ltir"
                  />
                </div>
              </div>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-slate-800">
                <Field label={lang === "ar" ? "الوفيات" : "Fatalities"}>
                  <Input type="number" value={soc.fatalities} onChange={e => setSoc({ ...soc, fatalities: e.target.value })} placeholder="0" />
                </Field>
                <Field label={lang === "ar" ? "إصابات المقاولين" : "Contractor Injuries"} hint="Injuries among non-employees">
                  <Input type="number" value={soc.contractor_injuries} onChange={e => setSoc({ ...soc, contractor_injuries: e.target.value })} placeholder="e.g. 0" />
                </Field>
                <Field label={lang === "ar" ? "تظلّمات الصحة والسلامة المقدَّمة" : "H&S Grievances Filed"}>
                  <Input type="number" value={soc.h_s_grievances} onChange={e => setSoc({ ...soc, h_s_grievances: e.target.value })} placeholder="0" />
                </Field>
              </div>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-slate-800">
                <Field label={lang === "ar" ? "عدد المقاولين (اختياري)" : "Contractor Headcount (optional)"} hint="Total non-employee workers (subcontractors, agency staff, on-site contractors). Optional; feeds GRI 2-8 disclosure.">
                  <Input type="number" min="0" value={soc.contractor_headcount} onChange={e => setSoc({ ...soc, contractor_headcount: e.target.value })} placeholder="e.g. 15" />
                </Field>
                <Field label={lang === "ar" ? "٪ تغطية تدريب المقاولين (اختياري)" : "Contractor Training Coverage % (optional)"} hint="Share of contractors who completed mandatory H&S / ethics training this year.">
                  <Input type="number" min="0" max="100" value={soc.contractor_training_pct} onChange={e => setSoc({ ...soc, contractor_training_pct: e.target.value })} placeholder="e.g. 80" />
                </Field>
              </div>
            </Card>

            <Card icon={<Users size={18} className="text-[#B94B05]" />} title={lang === "ar" ? "السياسات وحقوق الإنسان" : (lang === "ar" ? "السياسات وحقوق الإنسان" : "Policies & Human Rights")} desc={lang === "ar" ? "تغطية السياسات لامتثال ESG" : (lang === "ar" ? "تغطية السياسات لامتثال ESG" : "Policy coverage for ESG compliance")}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={lang === "ar" ? "سياسة حقوق الإنسان" : (lang === "ar" ? "سياسة حقوق الإنسان" : "Human Rights Policy")}>
                  <Select value={soc.human_rights_policy} onChange={e => setSoc({ ...soc, human_rights_policy: e.target.value })}>
                    <option value="">{lang === "ar" ? "اختر..." : "Select..."}</option>
                    <option value="Yes, Formal policy in place">{lang === "ar" ? "نعم، سياسة رسمية" : "Yes, Formal policy"}</option>
                    <option value="In Development">{lang === "ar" ? "قيد التطوير" : "In Development"}</option>
                    <option value="No">{lang === "ar" ? "لا" : "No"}</option>
                  </Select>
                </Field>
                <Field label={lang === "ar" ? "سياسة عمالة الأطفال والعمل القسري" : (lang === "ar" ? "سياسة عمالة الأطفال والعمل القسري" : "Child & Forced Labor Policy")}>
                  <Select value={soc.child_labor_policy} onChange={e => setSoc({ ...soc, child_labor_policy: e.target.value })}>
                    <option value="">{lang === "ar" ? "اختر..." : "Select..."}</option>
                    <option value="Yes, Zero tolerance policy">{lang === "ar" ? "نعم، سياسة عدم تهاون" : "Yes, Zero tolerance"}</option>
                    <option value="In Development">{lang === "ar" ? "قيد التطوير" : "In Development"}</option>
                    <option value="No">{lang === "ar" ? "لا" : "No"}</option>
                  </Select>
                </Field>
                <Field label={lang === "ar" ? "آلية التظلّم" : (lang === "ar" ? "آلية التظلّم" : "Grievance Mechanism")} full hint="How employees can raise concerns">
                  <TextArea value={soc.grievance_mechanism} onChange={e => setSoc({ ...soc, grievance_mechanism: e.target.value })} placeholder={lang === "ar" ? "مثل: خط ساخن مجهول، جهة اتصال بالموارد البشرية، سياسة الباب المفتوح، منصة للمبلّغين..." : "e.g. Anonymous hotline, HR contact, open-door policy, whistleblower platform..."} />
                  <AnyLangHint lang={lang} />
                </Field>
              </div>
            </Card>

            <Card icon={<Users size={18} className="text-[#B94B05]" />} title={lang === "ar" ? "المجتمع والمسؤولية الاجتماعية" : (lang === "ar" ? "المجتمع والمسؤولية الاجتماعية" : "Community & CSR")} desc={lang === "ar" ? "الأثر الاجتماعي، المشاريع، الاستثمار" : (lang === "ar" ? "الأثر الاجتماعي، المشاريع، الاستثمار" : "Social impact, projects, investment")}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={`CSR Spend / Community Investment (${company.currency})`}>
                  <Input testId="input-csr" value={soc.csr_spend} onChange={e => setSoc({ ...soc, csr_spend: e.target.value })} placeholder="e.g. 250,000" />
                </Field>
                <Field label={lang === "ar" ? "المشاريع المجتمعية" : (lang === "ar" ? "المشاريع المجتمعية" : "Community Projects")} full hint="Describe specific CSR projects, beneficiaries, outcomes">
                  <TextArea value={soc.community_projects} onChange={e => setSoc({ ...soc, community_projects: e.target.value })} placeholder={lang === "ar" ? "مثل: برنامج تدريب الشباب (20 مشاركاً)، تجديد المدارس، تنظيف البيئة..." : "e.g. Youth training program (20 participants), school renovation, environmental clean-up..."} />
                  <AnyLangHint lang={lang} />
                </Field>
                <Field label={lang === "ar" ? "برامج الرفاه" : (lang === "ar" ? "برامج الرفاه" : "Wellbeing Programs")} full>
                  <TextArea value={soc.wellbeing} onChange={e => setSoc({ ...soc, wellbeing: e.target.value })} placeholder={lang === "ar" ? "الصحة النفسية، التوازن بين العمل والحياة..." : "Mental health, work-life balance initiatives..."} />
                  <AnyLangHint lang={lang} />
                </Field>
                <Field label={lang === "ar" ? "ممارسات سلسلة التوريد" : "Supply Chain Practices"} full>
                  <TextArea value={soc.supply_chain} onChange={e => setSoc({ ...soc, supply_chain: e.target.value })} placeholder={lang === "ar" ? "كيف تضمن المصادر المسؤولة؟" : "How do you ensure responsible sourcing?"} />
                </Field>
                <Field label={lang === "ar" ? "سياسات الموارد البشرية" : "HR Policies"} full>
                  <TextArea value={soc.hr_policies} onChange={e => setSoc({ ...soc, hr_policies: e.target.value })} placeholder={lang === "ar" ? "سياسات الموارد البشرية الرئيسية: تكافؤ الفرص، التظلّم، إلخ." : "Key HR policies: equal opportunity, grievance, etc."} />
                </Field>
              </div>
            </Card>

            <Card icon={<Users size={18} className="text-[#B94B05]" />} title="Key Suppliers (Top 8)" desc="List your main suppliers/providers and their ESG compliance status">
              <div className="space-y-3">
                <div className="grid grid-cols-[1fr_1fr_140px] gap-2 px-1">
                  <span className="font-['Outfit'] text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-400">{lang === "ar" ? "اسم المورّد" : "Supplier Name"}</span>
                  <span className="font-['Outfit'] text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-400">{lang === "ar" ? "النشاط / القطاع" : "Activity / Sector"}</span>
                  <span className="font-['Outfit'] text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-400">{lang === "ar" ? "متوافق مع ESG؟" : "ESG Compliant?"}</span>
                </div>
                {(soc.key_suppliers || []).map((sup, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_140px] gap-2 items-center" data-testid={`supplier-row-${i}`}>
                    <Input testId={`supplier-name-${i}`} value={sup.name} onChange={e => { const arr = [...soc.key_suppliers]; arr[i] = { ...arr[i], name: e.target.value }; setSoc({ ...soc, key_suppliers: arr }); }} placeholder={`Supplier ${i + 1}`} />
                    <Input testId={`supplier-activity-${i}`} value={sup.activity} onChange={e => { const arr = [...soc.key_suppliers]; arr[i] = { ...arr[i], activity: e.target.value }; setSoc({ ...soc, key_suppliers: arr }); }} placeholder="e.g. Raw Materials" />
                    <Select testId={`supplier-esg-${i}`} value={sup.esg_compliant} onChange={e => { const arr = [...soc.key_suppliers]; arr[i] = { ...arr[i], esg_compliant: e.target.value }; setSoc({ ...soc, key_suppliers: arr }); }}>
                      <option value="">--</option>
                      <option value="Yes">{lang === "ar" ? "نعم" : "Yes"}</option>
                      <option value="No">{lang === "ar" ? "لا" : "No"}</option>
                      <option value="In Progress">{lang === "ar" ? "قيد التنفيذ" : "In Progress"}</option>
                    </Select>
                  </div>
                ))}
              </div>
            </Card>

            <Card icon={<Users size={18} className="text-[#B94B05]" />} title={lang === "ar" ? "صور قسم الاجتماعي" : (lang === "ar" ? "صور قسم الاجتماعي" : "Social Section Images")} desc={lang === "ar" ? "ارفع صوراً لأقسام الاجتماعي في ملف PDF" : (lang === "ar" ? "ارفع صوراً لأقسام الاجتماعي في ملف PDF" : "Upload images for Social sections of the PDF")}>
              <ImageUploader reportId={reportId} section="training_development" maxImages={2} accentColor="#B94B05" />
              <ImageUploader reportId={reportId} section="community_engagement" maxImages={2} accentColor="#B94B05" />
              <DataQualityBlock sectionName="Social" lang={lang} value={soc.data_quality} onChange={v => setSoc({ ...soc, data_quality: v })} />
            </Card>

            <SocialCompletionGate soc={soc} onNext={goNext} onBack={goBack} saving={saving} />
          </div>
        )}

        {/* Step 3: Governance */}
        {step === 3 && (
          <div className="animate-fade-in-up" data-testid="step-governance">
            <div className="bg-[#3B82F6]/5 border-l-2 border-[#3B82F6] rounded-r-lg px-4 py-3 text-sm text-[#3B82F6] mb-5">
              {lang === "ar" ? "تغطّي بيانات الحوكمة مجلس الإدارة والأخلاقيات والامتثال والإشراف على ESG. حرجة لمتطلّبات تقارير ADX." : "Governance data covers your board, ethics, compliance and ESG oversight. Critical for ADX reporting requirements."}
            </div>

            <Card icon={<ShieldCheck size={18} className="text-[#1E3A5F]" />} title={lang === "ar" ? "مجلس الإدارة" : "Board of Directors"} desc="Board composition, structure and oversight">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={lang === "ar" ? "حجم المجلس" : "Board Size"} hint={(gov.board_members_structured || []).length > 0 ? "Auto-derived from the Board Members table below" : "Number of directors on the board"}>
                  <Input testId="input-board-size" type="number" value={gov.board_size} onChange={e => setGov({ ...gov, board_size: e.target.value })} placeholder="e.g. 7" readOnly={(gov.board_members_structured || []).length > 0} />
                </Field>
                <Field label={lang === "ar" ? "تواتر اجتماعات المجلس" : "Board Meeting Frequency"}>
                  <Select value={gov.board_meeting_frequency} onChange={e => setGov({ ...gov, board_meeting_frequency: e.target.value })}>
                    <option value="">{lang === "ar" ? "اختر..." : "Select..."}</option>
                    <option value="Monthly">{lang === "ar" ? "شهرياً" : "Monthly"}</option>
                    <option value="Quarterly">{lang === "ar" ? "ربع سنوياً" : "Quarterly"}</option>
                    <option value="Bi-annually">{lang === "ar" ? "نصف سنوياً" : "Bi-annually"}</option>
                    <option value="Annually">{lang === "ar" ? "سنوياً" : "Annually"}</option>
                  </Select>
                </Field>
                <Field label={lang === "ar" ? "عضوات مجلس الإدارة (العدد)" : "Female Board Members (count)"} hint={(gov.board_members_structured || []).length > 0 ? "Auto-derived from the Board Members table below" : "How many female directors are on the board. If 1 of 3 is female, enter 1."}>
                  <Input type="number" min="0" value={gov.board_female_pct} onChange={e => setGov({ ...gov, board_female_pct: e.target.value })} placeholder="e.g. 2" readOnly={(gov.board_members_structured || []).length > 0} />
                </Field>
                <Field label={lang === "ar" ? "عضوات المجلس (السنة السابقة، العدد)" : "Female Board Members (previous year, count)"} hint="For year-over-year comparison">
                  <Input type="number" min="0" value={gov.prev_board_female_pct} onChange={e => setGov({ ...gov, prev_board_female_pct: e.target.value })} placeholder="e.g. 1" />
                </Field>
                <Field label={lang === "ar" ? "حجم المجلس (السنة السابقة)" : "Board size (previous year)"}>
                  <Input testId="input-prev-board-size" type="number" min="0" value={gov.prev_board_size || ""} onChange={e => setGov({ ...gov, prev_board_size: e.target.value })} placeholder="e.g. 5" />
                </Field>
                <Field label={lang === "ar" ? "أعضاء المجلس المستقلون (العدد)" : "Independent Directors (count)"} hint={(gov.board_members_structured || []).length > 0 ? "Auto-derived from the Board Members table below" : "How many independent (non-executive) directors are on the board"}>
                  <Input type="number" min="0" value={gov.independent_pct} onChange={e => setGov({ ...gov, independent_pct: e.target.value })} placeholder="e.g. 2" readOnly={(gov.board_members_structured || []).length > 0} />
                </Field>
                <Field label={lang === "ar" ? "الأعضاء المستقلون (السنة السابقة، العدد)" : "Independent Directors (previous year, count)"} hint="For year-over-year comparison">
                  <Input type="number" min="0" value={gov.prev_independent_pct} onChange={e => setGov({ ...gov, prev_independent_pct: e.target.value })} placeholder="e.g. 1" />
                </Field>
                <Field label={lang === "ar" ? "فصل الرئيس التنفيذي عن رئاسة المجلس" : "CEO / Chair Separation"}>
                  <Select value={gov.ceo_chair_separation} onChange={e => setGov({ ...gov, ceo_chair_separation: e.target.value })}>
                    <option value="">{lang === "ar" ? "اختر..." : "Select..."}</option>
                    <option value="Yes, Separate roles">{lang === "ar" ? "نعم، أدوار منفصلة" : "Yes, Separate roles"}</option>
                    <option value="No, Same person">{lang === "ar" ? "لا، نفس الشخص" : "No, Same person"}</option>
                  </Select>
                </Field>
                <Field label={lang === "ar" ? "لجان المجلس" : "Board Committees"} hint="e.g. Audit, Remuneration, ESG" full>
                  <TextArea value={gov.board_committees} onChange={e => setGov({ ...gov, board_committees: e.target.value })} placeholder="e.g. Audit & Risk Committee, Remuneration Committee, Sustainability Committee..." />
                </Field>
                <div className="col-span-2">
                  <BoardMembersTable
                    rows={gov.board_members_structured || []}
                    onChange={rows => setGov(g => ({ ...g, board_members_structured: rows }))}
                    fallbackText={gov.board_members}
                    onFallbackChange={v => setGov(g => ({ ...g, board_members: v }))}
                    lang={lang}
                  />
                </div>
              </div>
              <BoardCoherenceNote gov={gov} />
            </Card>

            <Card icon={<ShieldCheck size={18} className="text-[#1E3A5F]" />} title={lang === "ar" ? "الأخلاقيات والامتثال" : "Ethics & Compliance"} desc="Anti-corruption, policies, training">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={lang === "ar" ? "سياسة مكافحة الفساد" : "Anti-Corruption Policy"}>
                  <Select value={gov.anti_corruption} onChange={e => setGov({ ...gov, anti_corruption: e.target.value })}>
                    <option value="">{lang === "ar" ? "اختر..." : "Select..."}</option>
                    <option value="Yes, Formal policy in place">{lang === "ar" ? "نعم، سياسة رسمية" : "Yes, Formal policy"}</option>
                    <option value="In Development">{lang === "ar" ? "قيد التطوير" : "In Development"}</option>
                    <option value="No">{lang === "ar" ? "لا" : "No"}</option>
                  </Select>
                </Field>
                <Field label={lang === "ar" ? "٪ تدريب مكافحة الرشوة" : "Anti-Bribery Training %"} hint="% of employees who completed training">
                  <Input type="number" min="0" max="100" value={gov.abac_training_pct} onChange={e => setGov({ ...gov, abac_training_pct: e.target.value })} placeholder="e.g. 100" />
                </Field>
                <Field label={lang === "ar" ? "آلية المبلّغين" : "Whistleblower Mechanism"}>
                  <Select value={gov.whistleblower} onChange={e => setGov({ ...gov, whistleblower: e.target.value })}>
                    <option value="">{lang === "ar" ? "اختر..." : "Select..."}</option>
                    <option value="Yes, Anonymous reporting channel available">{lang === "ar" ? "نعم، قناة مجهولة" : "Yes, Anonymous channel"}</option>
                    <option value="In Development">{lang === "ar" ? "قيد التطوير" : "In Development"}</option>
                    <option value="No">{lang === "ar" ? "لا" : "No"}</option>
                  </Select>
                </Field>
                <Field label={lang === "ar" ? "سياسة تضارب المصالح" : "Conflicts of Interest Policy"}>
                  <Select value={gov.conflicts_of_interest_policy} onChange={e => setGov({ ...gov, conflicts_of_interest_policy: e.target.value })}>
                    <option value="">{lang === "ar" ? "اختر..." : "Select..."}</option>
                    <option value="Yes, Formal policy in place">{lang === "ar" ? "نعم، سياسة رسمية" : "Yes, Formal policy"}</option>
                    <option value="In Development">{lang === "ar" ? "قيد التطوير" : "In Development"}</option>
                    <option value="No">{lang === "ar" ? "لا" : "No"}</option>
                  </Select>
                </Field>
                <Field label={lang === "ar" ? "حوادث الامتثال" : "Compliance Incidents"} hint="Number during reporting period">
                  <Input type="number" value={gov.compliance_incidents} onChange={e => setGov({ ...gov, compliance_incidents: e.target.value })} placeholder="0" />
                </Field>
                <Field label={lang === "ar" ? "الدعاوى القانونية" : "Legal Proceedings"} hint="Ongoing cases">
                  <Input type="number" value={gov.legal_proceedings} onChange={e => setGov({ ...gov, legal_proceedings: e.target.value })} placeholder="0" />
                </Field>
                <Field label={lang === "ar" ? "ميثاق السلوك" : "Code of Conduct"} full>
                  <TextArea value={gov.code_of_conduct} onChange={e => setGov({ ...gov, code_of_conduct: e.target.value })} placeholder={lang === "ar" ? "صِف ميثاق سلوك شركتك..." : "Describe your company's code of conduct..."} />
                </Field>
              </div>
            </Card>

            <Card icon={<ShieldCheck size={18} className="text-[#1E3A5F]" />} title={lang === "ar" ? "خصوصية البيانات وسلسلة التوريد" : "Data Privacy & Supply Chain"} desc="Data protection, supplier governance">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={lang === "ar" ? "سياسة حماية البيانات" : "Data Protection Policy"}>
                  <Select value={gov.data_protection} onChange={e => setGov({ ...gov, data_protection: e.target.value })}>
                    <option value="">{lang === "ar" ? "اختر..." : "Select..."}</option>
                    <option value="Yes, PDPL/GDPR compliant">{lang === "ar" ? "نعم، متوافق" : "Yes, Compliant"}</option>
                    <option value="In Development">{lang === "ar" ? "قيد التطوير" : "In Development"}</option>
                    <option value="No">{lang === "ar" ? "لا" : "No"}</option>
                  </Select>
                </Field>
                <Field label={lang === "ar" ? "العناية الواجبة للأطراف الخارجية" : "Third-Party Due Diligence"}>
                  <Select value={gov.third_party_due_diligence} onChange={e => setGov({ ...gov, third_party_due_diligence: e.target.value })}>
                    <option value="">{lang === "ar" ? "اختر..." : "Select..."}</option>
                    <option value="Yes, Formal screening process">{lang === "ar" ? "نعم، فحص رسمي" : "Yes, Formal screening"}</option>
                    <option value="In Development">{lang === "ar" ? "قيد التطوير" : "In Development"}</option>
                    <option value="No">{lang === "ar" ? "لا" : "No"}</option>
                  </Select>
                </Field>
                <Field label={lang === "ar" ? "ميثاق سلوك المورّدين" : "Supplier Code of Conduct"}>
                  <Select value={gov.supplier_code_of_conduct} onChange={e => setGov({ ...gov, supplier_code_of_conduct: e.target.value })}>
                    <option value="">{lang === "ar" ? "اختر..." : "Select..."}</option>
                    <option value="Yes, Required for all vendors">{lang === "ar" ? "نعم، لجميع الموردين" : "Yes, All vendors"}</option>
                    <option value="Yes, Major vendors only">{lang === "ar" ? "نعم، للموردين الرئيسيين" : "Yes, Major vendors"}</option>
                    <option value="In Development">{lang === "ar" ? "قيد التطوير" : "In Development"}</option>
                    <option value="No">{lang === "ar" ? "لا" : "No"}</option>
                  </Select>
                </Field>
              </div>
              <div className="mt-4 rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-3 text-[11.5px] text-slate-300 leading-relaxed">
                <span className="font-semibold text-slate-100">Supplier ESG compliance %</span> is auto-derived from the <span className="font-semibold">Key Suppliers (Top 8)</span> table in the Social step (compliant rows ÷ filled rows × 100). We removed the previous free-input fields (<i>Supplier Code Coverage %</i>, <i>Total Critical Suppliers</i>, <i>% ESG-Assessed</i>, <i>% ESG-Compliant</i>) because they used to disagree with the table and produced audit contradictions (e.g. "80% compliant" narrative next to a 4/8 = 50% table).
              </div>
            </Card>

            <Card icon={<ShieldCheck size={18} className="text-[#1E3A5F]" />} title={lang === "ar" ? "الإشراف على ESG والمخاطر" : "ESG Oversight & Risk"} desc="ESG committee, risk management, memberships">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={lang === "ar" ? "لجنة ESG / الشخص المسؤول" : "ESG Committee / Responsible Person"} full>
                  <TextArea value={gov.esg_committee} onChange={e => setGov({ ...gov, esg_committee: e.target.value })} placeholder="Do you have a dedicated ESG committee or person? Describe structure..." />
                </Field>
                <Field label={lang === "ar" ? "فئات إدارة المخاطر" : "Risk Management Categories"} full hint="Key risk areas the company monitors">
                  <TextArea value={gov.risk_categories} onChange={e => setGov({ ...gov, risk_categories: e.target.value })} placeholder="e.g. Regulatory, Operational, Environmental, Reputational, Cybersecurity..." />
                </Field>
                <Field label={lang === "ar" ? "العضويات والانتسابات الصناعية" : "Industry Memberships & Affiliations"} full>
                  <TextArea value={gov.industry_memberships} onChange={e => setGov({ ...gov, industry_memberships: e.target.value })} placeholder="e.g. UAE Business Council, GCC Sustainability Forum, MESIA..." />
                </Field>
                <Field label={lang === "ar" ? "خطة التحسين" : "Improvement Plan"} full>
                  <TextArea value={gov.improvement_plan} onChange={e => setGov({ ...gov, improvement_plan: e.target.value })} placeholder="What are your governance improvement plans?" />
                </Field>
              </div>
            </Card>

            <Card icon={<ShieldCheck size={18} className="text-[#1E3A5F]" />} title={lang === "ar" ? "جودة البيانات" : (lang === "ar" ? "جودة البيانات" : "Data Quality")} desc={lang === "ar" ? "قيِّم جودة البيانات لركيزة الحوكمة" : (lang === "ar" ? "قيِّم جودة البيانات لركيزة الحوكمة" : "Assess the data quality for the Governance pillar")}>
              <DataQualityBlock sectionName="Governance" lang={lang} value={gov.data_quality} onChange={v => setGov({ ...gov, data_quality: v })} />
            </Card>

            <NavButtons onBack={goBack} onNext={goNext} saving={saving} lang={lang} />
          </div>
        )}

        {/* Step 4: Generate */}
        {step === 4 && (
          <div className="animate-fade-in-up" data-testid="step-generate">
            <DataQualitySummaryCard
              env={env}
              soc={soc}
              gov={gov}
              onJumpToStep={(target) => { saveReport(); setStep(target); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            />

            <div className="bg-[#111216] border border-slate-800/60 rounded-xl p-6 mb-5">
              <h2 className="font-['Outfit'] text-xl font-bold text-white mb-4">{lang === "ar" ? "ملخّص درجات الاستدامة (ESG)" : "ESG Scores Summary"}</h2>
              <ScoreRow label={lang === "ar" ? "البيئي" : "Environmental"} score={calcScore(env, 'e')} color="#01AF9B" />
              <ScoreRow label={lang === "ar" ? "الاجتماعي" : "Social"} score={calcScore(soc, 's')} color="#B94B05" />
              <ScoreRow label={lang === "ar" ? "الحوكمة" : "Governance"} score={calcScore(gov, 'g')} color="#1E3A5F" />
              <div className="mt-4 pt-4 border-t border-slate-800 flex justify-between items-center">
                <span className="font-['Outfit'] text-sm font-semibold text-slate-300">{lang === "ar" ? "درجة اكتمال الإفصاح" : "Disclosure Completeness Score"}</span>
                <span className="font-['Outfit'] text-2xl font-bold text-white">
                  {Math.round((calcScore(env, 'e') + calcScore(soc, 's') + calcScore(gov, 'g')) / 3)}%
                </span>
              </div>
            </div>

            <div className="bg-[#01AF9B]/5 border border-[#01AF9B]/20 rounded-xl p-6 text-center mb-5">
              <Sparkle size={32} className="text-[#01AF9B] mx-auto mb-3" />
              <h3 className="font-['Outfit'] text-lg font-bold text-white mb-2">{lang === "ar" ? "تقريرك جاهز للإنشاء" : "Ready to Generate Your Report"}</h3>
              <p className="text-slate-400 text-sm mb-5">{lang === "ar"
                ? "سيقوم الذكاء الاصطناعي بكتابة تقرير ESG احترافي شامل بأكثر من 60 صفحة استنادًا إلى بياناتك، متوافق مع معايير GRI العالمية 2021، وتوصيات TCFD، وإرشادات الإفصاح ESG لبورصة أبوظبي، ودليل التقارير ESG لبورصة دبي المالية، وأهداف الأمم المتحدة للتنمية المستدامة (مُهيكل ليكون جاهزًا لـ ISSB وCSRD)."
                : "AI will write a comprehensive 60+ page professional ESG report based on your data, aligned with GRI Universal Standards 2021, TCFD Recommendations, ADX ESG Disclosure Guidelines, DFM ESG Reporting Guide, and the UN Sustainable Development Goals (structured to be forward-ready for ISSB and CSRD)."}</p>
              <CoherenceBadge
                env={env}
                soc={soc}
                gov={gov}
                reportId={reportId}
                saveReport={saveReport}
                onJumpToStep={(target) => { saveReport(); setStep(target); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              />
              <button data-testid="generate-report-button" onClick={handleGenerate} disabled={generating || !company.name || preflightBlocked}
                className="bg-gradient-to-r from-[#01AF9B] to-[#018F7E] text-white font-['Outfit'] font-bold text-base py-4 px-8 rounded-xl transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mx-auto">
                {generating ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>{lang === "ar" ? "جارٍ الإنشاء" : "Generating"}… ({Math.floor(genElapsed / 60)}:{String(genElapsed % 60).padStart(2, '0')})</span>
                  </>
                ) : (
                  <>
                    <Sparkle size={20} weight="fill" />
                    <span>{lang === "ar" ? "أنشئ تقرير ESG" : "Generate ESG Report"}</span>
                  </>
                )}
              </button>
            </div>

            {generating && (
              <GenerationProgress
                phase={genPhase}
                elapsed={genElapsed}
                language={lang}
                statusText={genStatus}
              />
            )}
            <NavButtons onBack={goBack} lang={lang} />
          </div>
        )}
      </main>
    </div>
  );
}

// Phase order used to render the staged progress tracker.
// Keep in sync with PHASES dict in backend/server.py.
// (The actual <GenerationProgress> component lives in /components/GenerationProgress.js)

function Card({ icon, title, desc, children }) {
  return (
    <div className="bg-[#111216] border border-slate-800/60 rounded-xl p-5 mb-4 hover:border-slate-700 transition-all">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-9 h-9 rounded-lg bg-[#0B0C10] border border-slate-700 flex items-center justify-center">{icon}</div>
        <span className="font-['Outfit'] text-sm font-bold text-white">{title}</span>
      </div>
      {desc && <p className="text-slate-500 text-xs mb-4 pl-12">{desc}</p>}
      <div className="mt-3">{children}</div>
    </div>
  );
}

// ─── PURE COHERENCE CHECKERS ──────────────────────────────────────
// Each returns { issues: string[], hasData: boolean }. Used both by the
// inline *CoherenceNote panels and by the DataQualitySummaryCard on step 4.
const _num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : NaN; };

function checkWorkforce(soc) {
  const total = _num(soc.total_employees);
  const femPct = _num(soc.female_pct);
  const issues = [];
  if (Number.isNaN(total) || total <= 0) return { issues, hasData: false };
  const blMale = (_num(soc.senior_mgmt_male)||0) + (_num(soc.middle_mgmt_male)||0)
                + (_num(soc.technical_male)||0) + (_num(soc.admin_male)||0);
  const blFem  = (_num(soc.senior_mgmt_female)||0) + (_num(soc.middle_mgmt_female)||0)
                + (_num(soc.technical_female)||0) + (_num(soc.admin_female)||0);
  const blTotal = blMale + blFem;
  const blFemPct = blTotal > 0 ? (blFem / blTotal * 100) : 0;
  const ageTotal = (_num(soc.age_under30)||0) + (_num(soc.age_30to50)||0) + (_num(soc.age_over50)||0);
  if (blTotal > 0 && blTotal !== total) {
    issues.push(`By-level breakdown sums to ${blTotal}, not ${total}. The published report will automatically rescale the per-level counts so they reconcile to ${total} total employees. You can also fix the inputs above now.`);
  }
  if (blTotal > 0 && !Number.isNaN(femPct) && femPct > 0 && Math.abs(blFemPct - femPct) > 2) {
    issues.push(`By-level female ratio is ${blFemPct.toFixed(1)}% but you declared ${femPct}% female workforce overall. The report uses ${femPct}% as the authoritative figure, per-level female counts will be recalculated to match.`);
  }
  if (ageTotal > 0 && ageTotal !== total) {
    issues.push(`Age groups sum to ${ageTotal}, not ${total}. Please reconcile so Under-30 + 30-50 + Over-50 equals total headcount.`);
  }
  return { issues, hasData: blTotal > 0 || ageTotal > 0 };
}

function checkEnvWaste(env) {
  const issues = []; let hasData = false;
  const lp = _num(env.landfill_pct), wr = _num(env.water_recycled_pct);
  const w  = _num(env.water), wa = _num(env.waste);
  if (!Number.isNaN(lp)) { hasData = true; if (lp < 0 || lp > 100) issues.push(`Landfill rate is ${lp}%. Percentages must be between 0 and 100.`); }
  if (!Number.isNaN(wr)) { hasData = true; if (wr < 0 || wr > 100) issues.push(`Water recycled % is ${wr}%. Percentages must be between 0 and 100.`); }
  if (!Number.isNaN(w) && w <= 0 && env.water !== "") issues.push("Water consumption must be greater than zero.");
  if (!Number.isNaN(wa) && wa <= 0 && env.waste !== "") issues.push("Total waste must be greater than zero.");
  return { issues, hasData };
}

function checkEnvScope(env) {
  const issues = []; let hasData = false;
  const s1=_num(env.scope1), s2=_num(env.scope2), s3=_num(env.scope3);
  const ps1=_num(env.prev_scope1), ps2=_num(env.prev_scope2), ps3=_num(env.prev_scope3);
  const en=_num(env.energy), pen=_num(env.prev_energy);
  const rp=_num(env.renewable_pct), rgen=_num(env.renewable_generated_kwh);
  if (!Number.isNaN(rp)) { hasData = true; if (rp < 0 || rp > 100) issues.push(`Renewable energy share is ${rp}%. Must be between 0 and 100.`); }
  const yoy = (cur, prev, label) => {
    if (Number.isNaN(cur) || Number.isNaN(prev) || prev <= 0 || cur < 0) return;
    hasData = true;
    const change = (cur - prev) / prev * 100;
    if (Math.abs(change) > 500) issues.push(`${label} changed by ${change.toFixed(0)}% year-over-year (from ${prev} to ${cur}). That magnitude is unusual, please double-check.`);
  };
  yoy(s1, ps1, "Scope 1 emissions"); yoy(s2, ps2, "Scope 2 emissions");
  yoy(s3, ps3, "Scope 3 emissions"); yoy(en, pen, "Total energy consumption");
  if (!Number.isNaN(rp) && rp > 0 && !Number.isNaN(rgen) && rgen === 0 && env.renewable_generated_kwh !== "") {
    issues.push(`You declared ${rp}% renewable energy but on-site renewable generation is 0 kWh. Either the share comes from a green-tariff supplier (please add a note in the Initiatives field) or one of these two numbers is incorrect.`);
  }
  if (!Number.isNaN(rgen) && !Number.isNaN(en) && en > 0 && rgen > en) {
    issues.push(`On-site renewable generation (${rgen.toLocaleString()} kWh) exceeds total energy consumption (${en.toLocaleString()} kWh). One of these figures needs review.`);
  }
  // Three-way contradiction: renewable_pct vs (kWh / total_energy)
  if (!Number.isNaN(rp) && rp > 0 && !Number.isNaN(rgen) && rgen > 0 && !Number.isNaN(en) && en > 0) {
    hasData = true;
    const impliedPct = (rgen / en) * 100;
    if (Math.abs(impliedPct - rp) > 1) {
      issues.push(`Your renewable share of ${rp}% does not match your on-site generation of ${rgen.toLocaleString()} kWh divided by ${en.toLocaleString()} kWh total energy (which gives ${impliedPct.toFixed(1)}%). The published report will use ${impliedPct.toFixed(1)}% (derived from the meter readings). Please reconcile one of the three inputs if you want to override.`);
    }
  }
  return { issues, hasData };
}

function checkHiringTurnover(soc) {
  // With the Feb 2026 UX rewrite, `new_hires` and `departures` are the
  // primary integer totals and `turnover_rate` is auto-computed from
  // them. This checker now only guards against physically impossible
  // values, departures exceeding headcount, or absurdly large hire
  // numbers, since the previous "declared vs implied rate" mismatch
  // is impossible by construction (the rate is derived, not typed).
  const issues = [];
  let hasData = false;
  const total = _num(soc.total_employees);

  let hires = _num(soc.new_hires);
  if (Number.isNaN(hires)) {
    const hm = _num(soc.new_hires_male);
    const hf = _num(soc.new_hires_female);
    if (!Number.isNaN(hm) || !Number.isNaN(hf)) {
      hires = (Number.isNaN(hm) ? 0 : hm) + (Number.isNaN(hf) ? 0 : hf);
    }
  }

  let departs = _num(soc.departures);
  if (Number.isNaN(departs)) {
    const tm = _num(soc.turnover_male);
    const tf = _num(soc.turnover_female);
    if (!Number.isNaN(tm) || !Number.isNaN(tf)) {
      departs = (Number.isNaN(tm) ? 0 : tm) + (Number.isNaN(tf) ? 0 : tf);
    }
  }

  if (!Number.isNaN(departs)) {
    hasData = true;
    if (!Number.isNaN(total) && total > 0 && departs > total) {
      issues.push(
        `Departures (${departs}) exceeds total employees (${total}). ` +
        `One of these figures needs review.`
      );
    }
  }
  if (!Number.isNaN(hires)) {
    hasData = true;
    if (!Number.isNaN(total) && total > 0 && hires > total * 5) {
      issues.push(
        `New hires (${hires}) is more than 5× your total headcount ` +
        `(${total}). Please double-check.`
      );
    }
  }
  return { issues, hasData };
}

function checkParentalLeave(soc) {
  const issues = []; let hasData = false;
  const tm = _num(soc.parental_taken_male), tf = _num(soc.parental_taken_female);
  const rm = _num(soc.parental_returned_male), rf = _num(soc.parental_returned_female);
  if (!Number.isNaN(tm) && !Number.isNaN(rm)) { hasData = true; if (rm > tm) issues.push(`Male employees returned after parental leave (${rm}) exceeds male employees who took leave (${tm}). Returns cannot exceed leaves taken.`); }
  if (!Number.isNaN(tf) && !Number.isNaN(rf)) { hasData = true; if (rf > tf) issues.push(`Female employees returned after parental leave (${rf}) exceeds female employees who took leave (${tf}). Returns cannot exceed leaves taken.`); }
  return { issues, hasData };
}

function checkBoard(gov) {
  const issues = []; let hasData = false;
  const size = _num(gov.board_size);
  const fem = _num(gov.board_female_pct), ind = _num(gov.independent_pct);
  const pfem = _num(gov.prev_board_female_pct), pind = _num(gov.prev_independent_pct);
  if (!Number.isNaN(size) && size > 0) {
    hasData = true;
    if (size < 3) issues.push(`Board size of ${size} is unusually small for a regulated entity. Typical boards have 3 or more directors.`);
    if (!Number.isNaN(fem) && fem > size) issues.push(`Female directors (${fem}) cannot exceed total board size (${size}).`);
    if (!Number.isNaN(ind) && ind > size) issues.push(`Independent directors (${ind}) cannot exceed total board size (${size}).`);
    if (!Number.isNaN(pfem) && pfem > size) issues.push(`Previous-year female directors (${pfem}) exceeds current board size (${size}). If your board shrank, current board size and prior-year counts should both be checked.`);
    if (!Number.isNaN(pind) && pind > size) issues.push(`Previous-year independent directors (${pind}) exceeds current board size (${size}). Please review.`);
  }
  return { issues, hasData };
}

function WorkforceCoherenceNote({ soc }) {
  const { issues, hasData } = checkWorkforce(soc);
  return <CoherencePanel issues={issues} okMessage={`All workforce numbers reconcile to your declared total of ${soc.total_employees} employees.`} hasData={hasData} testIdPrefix="workforce" />;
}

// ─────────────────────────────────────────────────────────────────
// Shared rendering helper used by every CoherenceNote component.
// Keeps the visual treatment identical across all form steps.
// ─────────────────────────────────────────────────────────────────
function CoherencePanel({ issues, okMessage, hasData, testIdPrefix = "coherence" }) {
  const { lang } = useLang();
  if (issues.length === 0 && hasData) {
    return (
      <div className="mt-4 flex items-start gap-2 text-xs text-[#01AF9B] bg-[#01AF9B]/5 border border-[#01AF9B]/20 rounded-lg px-3 py-2.5" data-testid={`${testIdPrefix}-coherence-ok`}>
        <span className="text-base leading-none">✓</span>
        <span>{okMessage}</span>
      </div>
    );
  }
  if (issues.length === 0) return null;
  return (
    <div className="mt-4 bg-[#F59E0B]/5 border border-[#F59E0B]/30 rounded-lg px-3 py-3" data-testid={`${testIdPrefix}-coherence-warnings`}>
      <div className="flex items-center gap-2 text-[#F59E0B] text-xs font-['Outfit'] font-bold uppercase tracking-wider mb-2">
        <span>⚠</span>
        <span>{lang === "ar" ? "اتّساق البيانات، يُرجى المراجعة" : "Data coherence, please review"}</span>
      </div>
      <ul className="space-y-1.5 text-xs text-slate-300 leading-relaxed">
        {issues.map((msg, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="text-[#F59E0B] mt-0.5">•</span>
            <span>{msg}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── ENVIRONMENTAL ────────────────────────────────────────────────
function EnvWasteCoherenceNote({ env }) {
  const { issues, hasData } = checkEnvWaste(env);
  return <CoherencePanel issues={issues} okMessage="Water & waste percentages look good." hasData={hasData} testIdPrefix="env-waste" />;
}

function EnvScopeCoherenceNote({ env }) {
  const { issues, hasData } = checkEnvScope(env);
  return <CoherencePanel issues={issues} okMessage="Energy & emissions figures look coherent." hasData={hasData} testIdPrefix="env-scope" />;
}

// ─── SOCIAL: HIRING & TURNOVER ────────────────────────────────────
// ─────────────────────────────────────────────────────────────────
// HiringTurnoverSection, simplified 2-field UI + auto-computed rates.
// ─────────────────────────────────────────────────────────────────
// The user asks for the number of new hires and the number of departures,
// nothing else. We derive the turnover rate deterministically using the
// GRI 401-1b formula:
//
//   turnover_rate % = departures ÷ average_headcount × 100
//   average_headcount = (opening_headcount + closing_headcount) ÷ 2
//
// If the previous-year headcount hasn't been entered yet we fall back to
// the current total_employees (worst case: no averaging, but the number
// is still coherent). Same logic for hiring rate.
//
// We keep `turnover_rate` in state so the backend RDM (which reads that
// field) receives the computed value and no coherence-gate rules fire.
// Gender split is optional, collapsed by default; expanding it lets
// users add M/F breakdown for GRI 401-1a disclosure and, if entered,
// the total is auto-summed from the split.
function HiringTurnoverSection({ soc, setSoc, lang = "en" }) {
  const [showSplit, setShowSplit] = React.useState(
    !!(soc.new_hires_male || soc.new_hires_female ||
       soc.turnover_male || soc.turnover_female)
  );

  const numOr = (v, fallback = null) => {
    if (v === "" || v === null || v === undefined) return fallback;
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };

  const closing = numOr(soc.total_employees);
  const opening = numOr(soc.prev_total_employees);
  const avgHc =
    opening !== null && closing !== null
      ? (opening + closing) / 2
      : (closing ?? opening);

  const hires = numOr(soc.new_hires);
  const departs = numOr(soc.departures);

  const turnoverRate =
    departs !== null && avgHc && avgHc > 0
      ? +((departs / avgHc) * 100).toFixed(1)
      : null;
  const hireRate =
    hires !== null && avgHc && avgHc > 0
      ? +((hires / avgHc) * 100).toFixed(1)
      : null;

  // Keep `turnover_rate` in soc state in sync with the derived value so
  // the backend RDM (which reads soc.turnover_rate) receives the exact
  // number we display, preventing the "declared vs implied" coherence-gate
  // failure that used to trip on the /generate call.
  React.useEffect(() => {
    if (turnoverRate !== null && String(turnoverRate) !== String(soc.turnover_rate)) {
      setSoc((prev) => ({ ...prev, turnover_rate: String(turnoverRate) }));
    }
    if (turnoverRate === null && soc.turnover_rate !== "") {
      setSoc((prev) => ({ ...prev, turnover_rate: "" }));
    }

  }, [turnoverRate]);

  // When the user enters a gender split, auto-sum into the totals.
  const onSplitChange = (patch) => {
    const merged = { ...soc, ...patch };
    const hm = numOr(merged.new_hires_male, 0);
    const hf = numOr(merged.new_hires_female, 0);
    const tm = numOr(merged.turnover_male, 0);
    const tf = numOr(merged.turnover_female, 0);
    if (patch.new_hires_male !== undefined || patch.new_hires_female !== undefined) {
      merged.new_hires = String(hm + hf);
    }
    if (patch.turnover_male !== undefined || patch.turnover_female !== undefined) {
      merged.departures = String(tm + tf);
    }
    setSoc(merged);
  };

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label={lang === "ar" ? "عدد التوظيفات الجديدة" : "Number of New Hires"} hint="Total headcount hired during the reporting year.">
          <Input
            testId="input-new-hires-total"
            type="number"
            min="0"
            value={soc.new_hires}
            onChange={(e) => setSoc({ ...soc, new_hires: e.target.value })}
            placeholder="e.g. 23"
          />
        </Field>
        <Field label={lang === "ar" ? "عدد المغادرين" : "Number of Departures"} hint="Total employees who LEFT during the reporting year (voluntary + involuntary). Does NOT include new hires.">
          <Input
            testId="input-departures-total"
            type="number"
            min="0"
            value={soc.departures}
            onChange={(e) => setSoc({ ...soc, departures: e.target.value })}
            placeholder="e.g. 15"
          />
        </Field>
      </div>

      {/* Auto-computed rates, read-only, GRI 401-1b formula. */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="rounded-lg border border-slate-800/60 bg-[#0B0C10] px-4 py-3" data-testid="computed-turnover-rate">
          <div className="text-[10px] font-['Outfit'] uppercase tracking-wider text-slate-500 mb-1">
            Turnover Rate (auto-calculated)
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-['Outfit'] font-bold text-white">
              {turnoverRate !== null ? `${turnoverRate.toFixed(1)}%` : "-"}
            </span>
            {avgHc ? (
              <span className="text-[10px] text-slate-500">
                = {departs ?? 0} ÷ avg headcount ({avgHc.toFixed(0)}) × 100
              </span>
            ) : (
              <span className="text-[10px] text-slate-500">
                fill in total employees to compute
              </span>
            )}
          </div>
        </div>
        <div className="rounded-lg border border-slate-800/60 bg-[#0B0C10] px-4 py-3" data-testid="computed-hire-rate">
          <div className="text-[10px] font-['Outfit'] uppercase tracking-wider text-slate-500 mb-1">
            Hire Rate (auto-calculated)
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-['Outfit'] font-bold text-white">
              {hireRate !== null ? `${hireRate.toFixed(1)}%` : "-"}
            </span>
            {avgHc ? (
              <span className="text-[10px] text-slate-500">
                = {hires ?? 0} ÷ avg headcount ({avgHc.toFixed(0)}) × 100
              </span>
            ) : (
              <span className="text-[10px] text-slate-500">
                fill in total employees to compute
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Optional gender split, only for users who want the GRI 401-1a
          disclosure. Collapsed by default so the primary UX stays clean. */}
      <button
        type="button"
        onClick={() => setShowSplit((v) => !v)}
        data-testid="toggle-hiring-turnover-gender-split"
        className="mt-3 text-xs text-[#01AF9B] hover:text-[#02c9b2] font-['Outfit'] font-semibold"
      >
        {showSplit ? "− Hide gender split" : "+ Add gender split (optional, for GRI 401-1a)"}
      </button>

      {showSplit && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-lg border border-slate-800/60 bg-[#0B0C10] p-3">
          <Field label={lang === "ar" ? "التوظيف الجديد، ذكور" : "New Hires, Male"} hint="Optional. If filled, the total above auto-updates.">
            <Input
              type="number"
              min="0"
              value={soc.new_hires_male}
              onChange={(e) => onSplitChange({ new_hires_male: e.target.value })}
              placeholder="e.g. 15"
            />
          </Field>
          <Field label={lang === "ar" ? "التوظيف الجديد، إناث" : "New Hires, Female"} hint="Optional.">
            <Input
              type="number"
              min="0"
              value={soc.new_hires_female}
              onChange={(e) => onSplitChange({ new_hires_female: e.target.value })}
              placeholder="e.g. 8"
            />
          </Field>
          <Field label={lang === "ar" ? "المغادرون، ذكور" : "Departures, Male"} hint="Employees who left.">
            <Input
              type="number"
              min="0"
              value={soc.turnover_male}
              onChange={(e) => onSplitChange({ turnover_male: e.target.value })}
              placeholder="e.g. 10"
            />
          </Field>
          <Field label={lang === "ar" ? "المغادرون، إناث" : "Departures, Female"} hint="Employees who left.">
            <Input
              type="number"
              min="0"
              value={soc.turnover_female}
              onChange={(e) => onSplitChange({ turnover_female: e.target.value })}
              placeholder="e.g. 5"
            />
          </Field>
        </div>
      )}
    </>
  );
}

function HiringTurnoverCoherenceNote({ soc }) {
  const { issues, hasData } = checkHiringTurnover(soc);
  return <CoherencePanel issues={issues} okMessage="Hiring & turnover figures look coherent." hasData={hasData} testIdPrefix="hiring" />;
}

// ─── SOCIAL: PARENTAL LEAVE ───────────────────────────────────────
function ParentalLeaveCoherenceNote({ soc }) {
  const { issues, hasData } = checkParentalLeave(soc);
  return <CoherencePanel issues={issues} okMessage="Parental-leave return rates look coherent." hasData={hasData} testIdPrefix="parental" />;
}

// ─── GOVERNANCE: BOARD COMPOSITION ────────────────────────────────
function BoardCoherenceNote({ gov }) {
  const { issues, hasData } = checkBoard(gov);
  return <CoherencePanel issues={issues} okMessage="Board composition counts are coherent." hasData={hasData} testIdPrefix="board" />;
}

// ─── GOVERNANCE: BOARD MEMBERS STRUCTURED TABLE ───────────────────
// Structured replacement for the legacy free-text `board_members` field
// (Phase 6 architectural fix). Each row captures Name, Role, Gender,
// Independent. Derived KPIs (board size, % female, % independent, chair
// detection) are computed from this table in the parent component's
// useEffect, killing the whole class of "gender-percentage-vs-board-list"
// contradictions at the source.
function BoardMembersTable({ rows, onChange, fallbackText, onFallbackChange, lang = "en" }) {
  const [showLegacy, setShowLegacy] = useState(false);
  const safeRows = Array.isArray(rows) ? rows : [];

  const updateRow = (i, patch) => {
    const next = safeRows.map((r, idx) => idx === i ? { ...r, ...patch } : r);
    onChange(next);
  };
  const addRow = () => {
    onChange([...safeRows, { name: "", role: "", gender: "", independent: false }]);
  };
  const removeRow = (i) => {
    onChange(safeRows.filter((_, idx) => idx !== i));
  };

  const chairPresent = safeRows.some(r => (r?.role || "").toLowerCase().includes("chair"));

  return (
    <div className="col-span-2">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-slate-200 text-sm font-semibold font-['Outfit']">{lang === "ar" ? "أعضاء مجلس الإدارة" : "Board Members"}</div>
          <div className="text-slate-500 text-xs font-['Outfit'] mt-0.5">Add one row per director. Board size, % female, and % independent are auto-computed from this table.</div>
        </div>
        <button type="button" onClick={addRow} data-testid="board-add-member"
          className="px-3 py-1.5 rounded-lg text-xs font-['Outfit'] font-semibold bg-[#01AF9B]/20 border border-[#01AF9B] text-[#01AF9B] hover:bg-[#01AF9B]/30 transition-all">
          + Add board member
        </button>
      </div>

      {safeRows.length === 0 ? (
        <div className="bg-[#0B0C10] border border-dashed border-slate-700 rounded-lg p-6 text-center">
          <div className="text-slate-400 text-sm font-['Outfit'] mb-3">{lang === "ar" ? "لم تتم إضافة أعضاء مجلس الإدارة بعد." : "No board members added yet."}</div>
          <button type="button" onClick={addRow} data-testid="board-add-first"
            className="px-4 py-2 rounded-lg text-sm font-['Outfit'] font-semibold bg-[#01AF9B]/20 border border-[#01AF9B] text-[#01AF9B]">
            + Add your first board member
          </button>
          <div className="mt-4 text-xs text-slate-500 font-['Outfit']">
            or <button type="button" onClick={() => setShowLegacy(v => !v)} className="underline text-slate-400 hover:text-slate-200">use legacy free-text field</button>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-slate-700 overflow-hidden">
          <table className="w-full text-sm font-['Outfit']">
            <thead className="bg-[#1E3A5F]/60">
              <tr className="text-slate-300 text-xs uppercase tracking-wider">
                <th className="text-left px-3 py-2 font-semibold">Name</th>
                <th className="text-left px-3 py-2 font-semibold">Role / Title</th>
                <th className="text-left px-3 py-2 font-semibold w-32">Gender</th>
                <th className="text-center px-3 py-2 font-semibold w-24">{lang === "ar" ? "مستقل" : "Independent"}</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {safeRows.map((r, i) => (
                <tr key={i} className="border-t border-slate-800 hover:bg-slate-800/20">
                  <td className="px-2 py-1.5">
                    <input data-testid={`board-row-${i}-name`} type="text" value={r?.name || ""}
                      onChange={e => updateRow(i, { name: e.target.value })}
                      placeholder="e.g. Jane Smith"
                      className="w-full bg-[#0B0C10] border border-slate-700 text-slate-100 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#01AF9B] focus:border-transparent outline-none" />
                  </td>
                  <td className="px-2 py-1.5">
                    <input data-testid={`board-row-${i}-role`} type="text" value={r?.role || ""}
                      onChange={e => updateRow(i, { role: e.target.value })}
                      placeholder="e.g. Chair, CEO, CFO"
                      className="w-full bg-[#0B0C10] border border-slate-700 text-slate-100 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#01AF9B] focus:border-transparent outline-none" />
                  </td>
                  <td className="px-2 py-1.5">
                    <select data-testid={`board-row-${i}-gender`} value={r?.gender || ""}
                      onChange={e => updateRow(i, { gender: e.target.value })}
                      className="w-full bg-[#0B0C10] border border-slate-700 text-slate-100 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#01AF9B] focus:border-transparent outline-none">
                      <option value="">-</option>
                      <option value="Female">{lang === "ar" ? "أنثى" : "Female"}</option>
                      <option value="Male">{lang === "ar" ? "ذكر" : "Male"}</option>
                      <option value="Prefer not to say">{lang === "ar" ? "أفضّل عدم الإفصاح" : "Prefer not to say"}</option>
                    </select>
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    <input data-testid={`board-row-${i}-independent`} type="checkbox"
                      checked={!!r?.independent}
                      onChange={e => updateRow(i, { independent: e.target.checked })}
                      className="w-4 h-4 accent-[#01AF9B]" />
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    <button type="button" onClick={() => removeRow(i)} data-testid={`board-row-${i}-remove`}
                      className="text-slate-500 hover:text-red-400 text-lg leading-none px-1" aria-label={lang === "ar" ? "حذف الصف" : "Remove row"}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="bg-[#1E3A5F]/30 border-t border-slate-800 px-3 py-2 text-xs text-slate-400 font-['Outfit']">
            {safeRows.filter(r => (r?.name || "").trim()).length} member(s) · {safeRows.filter(r => (r?.gender || "").toLowerCase() === "female").length} female · {safeRows.filter(r => !!r?.independent).length} independent · Chair: <span className={chairPresent ? "text-[#01AF9B] font-semibold" : "text-amber-400 font-semibold"}>{chairPresent ? "Yes" : "Not named"}</span>
          </div>
        </div>
      )}

      {showLegacy && safeRows.length === 0 && (
        <div className="mt-3">
          <div className="text-slate-500 text-xs font-['Outfit'] mb-1">{lang === "ar" ? "وضع النص الحر (أضف صفوفاً هيكلية أعلاه لاستخراج المؤشرات تلقائياً)" : "Legacy free-text mode (add structured rows above to auto-derive KPIs)"}</div>
          <textarea value={fallbackText || ""} onChange={e => onFallbackChange(e.target.value)}
            placeholder="e.g. Jane Smith (Chair, Independent, Female), John Doe (CEO, Executive, Male)..."
            className="w-full min-h-[80px] bg-[#0B0C10] border border-slate-700 text-slate-100 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#01AF9B] focus:border-transparent outline-none placeholder:text-slate-600" />
        </div>
      )}
    </div>
  );
}

// ─── DATA QUALITY SUMMARY (Final Review step) ─────────────────────
// Aggregates every coherence check across all 3 sections. Click a pill to
// jump back to the offending step to fix.
function DataQualitySummaryCard({ env, soc, gov, onJumpToStep }) {
  const { lang } = useLang();
  // Environmental: combine waste + scope
  const envWaste = checkEnvWaste(env);
  const envScope = checkEnvScope(env);
  const envIssues = [...envWaste.issues, ...envScope.issues];
  const envHasData = envWaste.hasData || envScope.hasData;

  // Social: combine workforce + hiring + parental
  const wf = checkWorkforce(soc);
  const ht = checkHiringTurnover(soc);
  const pl = checkParentalLeave(soc);
  const socIssues = [...wf.issues, ...ht.issues, ...pl.issues];
  const socHasData = wf.hasData || ht.hasData || pl.hasData;

  // Governance
  const bd = checkBoard(gov);
  const govIssues = [...bd.issues];

  // ── Ticket 4 v5 (Feb 2026) — Policy-honesty note ──
  // Show the user which yes/no policies they marked as "no" so they know
  // the generated report will honestly disclose those as "not yet in
  // place" instead of overstating. This turns the audit-round contradictions
  // into an intentional, visible design choice for the user.
  const _isNo = (v) => {
    const s = String(v || "").trim().toLowerCase();
    return s.startsWith("no") || s.includes("not implemented") || s.includes("not in place") || s === "n/a";
  };
  const _noPolicies = [
    ["third_party_due_diligence", "Third-Party Due Diligence"],
    ["supplier_code_of_conduct", "Supplier Code of Conduct"],
    ["data_protection", "Data Protection Policy"],
    ["anti_corruption", "Anti-Corruption Policy"],
    ["whistleblower", "Whistleblower Mechanism"],
    ["code_of_conduct", "Code of Conduct"],
  ].filter(([k]) => _isNo(gov[k])).map(([, label]) => label);
  const govHasData = bd.hasData;

  const sections = [
    { id: "env", step: 1, label: lang === "ar" ? "البيئي" : "Environmental", color: "#01AF9B", issues: envIssues, hasData: envHasData },
    { id: "soc", step: 2, label: lang === "ar" ? "الاجتماعي" : "Social",     color: "#B94B05", issues: socIssues, hasData: socHasData },
    { id: "gov", step: 3, label: lang === "ar" ? "الحوكمة" : "Governance",   color: "#1E3A5F", issues: govIssues, hasData: govHasData },
  ];

  const totalIssues = envIssues.length + socIssues.length + govIssues.length;
  const allCoherent = totalIssues === 0;

  return (
    <div className="bg-[#111216] border border-slate-800/60 rounded-xl p-6 mb-5" data-testid="data-quality-summary">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-['Outfit'] text-xl font-bold text-white">{lang === "ar" ? "فحص جودة البيانات" : "Data Quality Check"}</h2>
        {allCoherent ? (
          <span className="font-['Outfit'] text-[11px] font-bold tracking-[0.08em] uppercase text-[#01AF9B] bg-[#01AF9B]/10 border border-[#01AF9B]/30 rounded-full px-3 py-1" data-testid="dq-all-clean">{lang === "ar" ? "سليم بالكامل" : "All clean"}</span>
        ) : (
          <span className="font-['Outfit'] text-[11px] font-bold tracking-[0.08em] uppercase text-[#F59E0B] bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-full px-3 py-1" data-testid="dq-has-warnings">{lang === "ar" ? `${totalIssues} تحذير` : `${totalIssues} warning${totalIssues > 1 ? "s" : ""}`}</span>
        )}
      </div>
      <p className="text-slate-500 text-xs mb-5">
        {lang === "ar"
          ? "شبكة الأمان الأخيرة قبل بدء إنشاء التقرير بالذكاء الاصطناعي. اضغط على أيّ قسم يحتوي تحذيرات للرجوع ومراجعة المدخلات."
          : "Final safety net before AI generation kicks in. Click any section with warnings to jump back and review the inputs."}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {sections.map(sec => {
          const clean = sec.issues.length === 0;
          const hasAny = sec.hasData;
          return (
            <button
              key={sec.id}
              type="button"
              onClick={() => onJumpToStep(sec.step)}
              data-testid={`dq-pill-${sec.id}`}
              className={`text-left rounded-lg px-4 py-3 transition-all border ${
                clean
                  ? "bg-[#01AF9B]/5 border-[#01AF9B]/20 hover:border-[#01AF9B]/40"
                  : "bg-[#F59E0B]/5 border-[#F59E0B]/30 hover:border-[#F59E0B]/60"
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-['Outfit'] text-xs font-bold tracking-[0.08em] uppercase" style={{ color: sec.color }}>{sec.label}</span>
                <span className={`text-base ${clean ? "text-[#01AF9B]" : "text-[#F59E0B]"}`}>{clean ? "✓" : "⚠"}</span>
              </div>
              <div className={`text-xs ${clean ? "text-slate-300" : "text-[#F59E0B]"} font-semibold`}>
                {clean
                  ? (hasAny ? (lang === "ar" ? "متسق" : "Coherent") : (lang === "ar" ? "لا بيانات بعد" : "No data yet"))
                  : (lang === "ar" ? `${sec.issues.length} تحذير` : `${sec.issues.length} warning${sec.issues.length > 1 ? "s" : ""}`)}
              </div>
              {!clean && (
                <div className="text-[11px] text-slate-400 mt-1.5 truncate">
                  {sec.issues[0]}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {totalIssues > 0 && (
        <div className="mt-5 pt-5 border-t border-slate-800">
          <div className="text-[10px] font-['Outfit'] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-2">{lang === "ar" ? "جميع التحذيرات" : "All warnings"}</div>
          <ul className="space-y-1.5 text-xs text-slate-300 leading-relaxed" data-testid="dq-all-issues">
            {[
              ...envIssues.map(t => ({ t, step: 1, color: "#01AF9B" })),
              ...socIssues.map(t => ({ t, step: 2, color: "#B94B05" })),
              ...govIssues.map(t => ({ t, step: 3, color: "#1E3A5F" })),
            ].map((it, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-[#F59E0B] mt-0.5">•</span>
                <span className="flex-1">{it.t}</span>
                <button
                  type="button"
                  onClick={() => onJumpToStep(it.step)}
                  className="flex-shrink-0 text-[10px] font-['Outfit'] font-bold tracking-wider uppercase rounded px-2 py-0.5 transition-colors"
                  style={{ color: it.color, border: `1px solid ${it.color}40` }}
                  data-testid={`dq-jump-${i}`}
                >
                  {lang === "ar" ? "إصلاح" : "Fix"}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {_noPolicies.length > 0 && (
        <div className="mt-5 pt-5 border-t border-slate-800">
          <div className="flex items-start gap-2 text-xs text-[#F4C21A]">
            <span className="text-base leading-none">ⓘ</span>
            <div className="flex-1">
              <div className="font-semibold mb-1">
                {lang === "ar"
                  ? "ملاحظة الشفافية: السياسات التالية غير مطبّقة بعد"
                  : "Transparency note: the following policies are marked as not yet in place"}
              </div>
              <div className="text-[11px] text-slate-400 leading-relaxed">
                {_noPolicies.join(" · ")}
              </div>
              <div className="mt-2 text-[11px] text-slate-500 leading-relaxed">
                {lang === "ar"
                  ? "سيُصرّح تقريرك بذلك بأمانة كنقطة انطلاق وخارطة طريق للدورة المقبلة، بدل الادعاء بوجود ما ليس قائماً. إن كان أيّ من هذه الحقول مضبوطاً بالخطأ، ارجع إلى خطوة الحوكمة لتصحيحه."
                  : "Your report will honestly disclose these as a starting point plus a forward roadmap, rather than overstating what isn't yet in place. If any of these was set by mistake, jump back to the Governance step to correct it."}
              </div>
              <button
                type="button"
                onClick={() => onJumpToStep(3)}
                className="mt-2 text-[10px] font-['Outfit'] font-bold tracking-wider uppercase rounded px-2 py-0.5 transition-colors"
                style={{ color: "#F4C21A", border: "1px solid #F4C21A40" }}
                data-testid="dq-policy-honesty-fix"
              >
                {lang === "ar" ? "مراجعة السياسات" : "Review policies"}
              </button>
            </div>
          </div>
        </div>
      )}

      {allCoherent && (
        <div className="mt-5 pt-5 border-t border-slate-800 flex items-start gap-2 text-xs text-[#01AF9B]">
          <span className="text-base leading-none">✓</span>
          <span>{lang === "ar" ? "اجتازت جميع الأقسام فحوصات الاتساق. يمكنك المتابعة إلى الإنشاء بثقة." : "All sections passed coherence checks. You can proceed to generation with confidence."}</span>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Environmental step: SSOT helpers, monthly panel, auto-chip, and gate.
// ═══════════════════════════════════════════════════════════════════
const _MONTHS = [
  ["jan","Jan"], ["feb","Feb"], ["mar","Mar"], ["apr","Apr"],
  ["may","May"], ["jun","Jun"], ["jul","Jul"], ["aug","Aug"],
  ["sep","Sep"], ["oct","Oct"], ["nov","Nov"], ["dec","Dec"],
];

// Returns true if ANY of the 12 keys in a monthly-panel object has a
// non-empty value. Used to lock/unlock the paired annual input.
function _monthlyAnyFilled(obj) {
  if (!obj || typeof obj !== "object") return false;
  return _MONTHS.some(([k]) => obj[k] !== undefined && obj[k] !== null && String(obj[k]).trim() !== "");
}

// Returns true only if ALL 12 months are non-empty. This is the "OK to
// submit" state for the monthly panel per user's Q2 choice (c: all-or-none).
function _monthlyAllFilled(obj) {
  if (!obj || typeof obj !== "object") return false;
  return _MONTHS.every(([k]) => obj[k] !== undefined && obj[k] !== null && String(obj[k]).trim() !== "");
}

// Optional 12-month input matrix. Renders in a compact 4×3 grid, aggregates
// (sum by default; avg for stock metrics like headcount), and shows a
// coloured status pill. Persists as {jan,feb,...} directly on env/soc, so
// no schema change on the backend is required.
//
// UX:
//  · While unused (no month filled), the panel COLLAPSES into a single
//    "Add monthly breakdown" chip, this keeps the form compact and makes
//    the intent obvious.
//  · The 12 inputs use "-" as placeholder (not "0"), so empty ≠ zero.
//  · `whyMonthly` renders as a subtitle under the panel title explaining
//    what filling it in unlocks in the PDF.
function MonthlyPanel({ label, color = "#01AF9B", value, onChange, unit = "", testIdPrefix, aggregation = "sum", whyMonthly }) {
  const { lang } = useLang();
  const any = _monthlyAnyFilled(value);
  const all = _monthlyAllFilled(value);
  const [expanded, setExpanded] = useState(false);
  const isOpen = any || expanded;
  const vals = _MONTHS
    .map(([k]) => parseFloat(value?.[k]))
    .filter(v => !Number.isNaN(v));
  const sum = vals.reduce((a, b) => a + b, 0);
  const avg = vals.length > 0 ? sum / vals.length : 0;
  const agg = aggregation === "avg" ? avg : sum;
  const aggLabel = aggregation === "avg" ? "Average" : "Annual total";
  const setMonth = (k, v) => onChange({ ...(value || {}), [k]: v });
  const clearAll = () => { onChange(_MONTHS.reduce((acc, [k]) => ({ ...acc, [k]: "" }), {})); setExpanded(false); };

  // ── Collapsed state: single-row prompt, saves ~180px of vertical space ──
  if (!isOpen) {
    return (
      <div className="col-span-2 rounded-xl border border-dashed border-slate-700 bg-[#0B0C10]/40 p-3 flex items-center justify-between hover:border-slate-500 transition-colors">
        <div className="flex flex-col">
          <span className="font-['Outfit'] text-[10px] font-semibold tracking-[0.08em] uppercase" style={{ color }}>{label}</span>
          {whyMonthly && <span className="text-[10px] text-slate-500 mt-0.5">{whyMonthly}</span>}
        </div>
        <button type="button" onClick={() => setExpanded(true)}
          data-testid={`${testIdPrefix}-expand`}
          className="text-[10px] font-['Outfit'] font-semibold uppercase tracking-wider text-slate-300 hover:text-white px-3 py-1.5 rounded-md border border-slate-700 hover:border-slate-500 transition-colors">
          + Add 12-month breakdown
        </button>
      </div>
    );
  }

  return (
    <div className="col-span-2 rounded-xl border border-slate-800/80 bg-[#0B0C10] p-4">
      <div className="flex items-start justify-between mb-3 gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-['Outfit'] text-[10px] font-semibold tracking-[0.08em] uppercase" style={{ color }}>{label}</div>
          {whyMonthly && <div className="text-[10px] text-slate-500 mt-0.5">{whyMonthly}</div>}
          <div className="text-[10px] text-slate-500 mt-1">Enter all 12 months, or clear and use the annual field above.</div>
        </div>
        <button type="button" onClick={clearAll}
          data-testid={`${testIdPrefix}-clear`}
          className="text-[10px] font-['Outfit'] font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-200 transition-colors shrink-0">
          {any ? "Clear" : "Close"}
        </button>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {_MONTHS.map(([k, l]) => (
          <label key={k} className="flex flex-col gap-1">
            <span className="text-[9px] font-['Outfit'] uppercase text-slate-500 tracking-wider">{l}</span>
            <input
              type="number"
              min="0"
              value={value?.[k] || ""}
              onChange={e => setMonth(k, e.target.value)}
              data-testid={`${testIdPrefix}-${k}`}
              className="w-full bg-[#111216] border border-slate-700 rounded-md text-slate-100 text-xs px-2 py-1.5 focus:ring-1 focus:ring-[--fc] outline-none"
              style={{ "--fc": color }}
              placeholder="-"
            />
          </label>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between text-xs">
        {!any && (
          <span className="text-slate-500 italic">{lang === "ar" ? "فارغ، القيمة السنوية أعلاه هي المرجع." : "Empty, annual value above is authoritative."}</span>
        )}
        {any && !all && (
          <span className="text-[#F59E0B] font-['Outfit'] font-semibold" data-testid={`${testIdPrefix}-partial`}>
            ⚠ {12 - _MONTHS.filter(([k]) => String(value?.[k] || "").trim() !== "").length} month(s) missing, fill all 12 before continuing.
          </span>
        )}
        {all && (
          <span className="font-['Outfit'] font-semibold" style={{ color }} data-testid={`${testIdPrefix}-complete`}>
            ✓ {aggLabel} = {(aggregation === "avg" ? Math.round(agg) : agg).toLocaleString()} {unit}
          </span>
        )}
      </div>
    </div>
  );
}

// Auto-derived chip. Shows the current value with an "Override" toggle
// that flips the user into manual entry (respected by the derivation
// useEffects via the `<field>_manual` flag).
function AutoChip({ label, color = "#01AF9B", value, manual, onOverride, onReset, readOnly, testId, hint }) {
  const displayed = (value === "" || value === null || value === undefined) ? "-" : value;
  return (
    <div className="rounded-xl border p-3 flex flex-col gap-1"
      style={{ borderColor: `${color}30`, background: `${color}08` }}
      data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-['Outfit'] text-[9px] font-bold tracking-[0.08em] uppercase" style={{ color }}>{label}</span>
        {!readOnly && (
          manual ? (
            <button type="button" onClick={onReset}
              className="text-[9px] font-['Outfit'] font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-200"
              data-testid={`${testId}-auto`}>
              Auto
            </button>
          ) : (
            <span className="text-[9px] font-['Outfit'] font-semibold uppercase tracking-wider text-slate-500">Derived</span>
          )
        )}
      </div>
      {hint && (
        <span className="text-[10px] leading-snug text-slate-400 font-['Outfit'] normal-case" data-testid={`${testId}-hint`}>
          {hint}
        </span>
      )}
      {manual && !readOnly ? (
        <input
          type="number"
          value={value || ""}
          onChange={e => onOverride(e.target.value)}
          className="w-full bg-[#0B0C10] border border-slate-700 rounded-md text-slate-100 text-sm px-2.5 py-1.5 focus:ring-1 outline-none"
          style={{ "--fc": color }}
          data-testid={`${testId}-input`}
        />
      ) : (
        <div className="flex items-baseline justify-between">
          <span className="font-['Outfit'] text-lg font-bold text-white" data-testid={`${testId}-value`}>{displayed}</span>
          {!readOnly && (
            <button type="button" onClick={() => onOverride(value || "")}
              className="text-[9px] font-['Outfit'] font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-200"
              data-testid={`${testId}-override`}>
              Override
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Environmental Completion Gate, the "bulletproof" final check.
//
// Splits findings into ERRORS (must-fix) and WARNINGS (advisory).
// Errors block Next; warnings show a confirm dialog. When both are
// empty the user sees an explicit green "complete & coherent" panel.
// ─────────────────────────────────────────────────────────────────
function checkEnvComplete(env) {
  const errors = [];
  const warnings = [];
  const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  };

  // ── Required: at least one Scope must be present ──
  const s1 = num(env.scope1);
  const s2 = num(env.scope2);
  const s3 = num(env.scope3);
  if ((s1 === null || s1 <= 0) && (s2 === null || s2 <= 0)) {
    errors.push("You must provide Scope 1 or Scope 2 data (from fuel, electricity, or district cooling). Both are currently empty.");
  }

  // ── Required: energy inputs behind Scope 2 ──
  const kwh = num(env.electricity_kwh);
  const cool = num(env.district_cooling_rt_hrs);
  if ((kwh === null || kwh <= 0) && (cool === null || cool <= 0)) {
    errors.push("Enter Grid Electricity kWh OR District Cooling RT-hrs (or both), Scope 2 cannot be computed without one of them.");
  }

  // ── Required: water & waste ──
  if (num(env.water) === null || num(env.water) <= 0) {
    errors.push("Water Consumption (m³) is missing or zero.");
  }
  if (num(env.waste) === null || num(env.waste) <= 0) {
    errors.push("Total Waste (tonnes) is missing or zero.");
  }
  if (num(env.landfill_pct) === null) {
    warnings.push("Landfill Rate (%) is empty, Scope 3 waste emissions cannot be estimated.");
  }

  // ── Monthly panels: if any month filled, all 12 must be filled ──
  if (_monthlyAnyFilled(env.monthly_electricity_kwh) && !_monthlyAllFilled(env.monthly_electricity_kwh)) {
    errors.push("Monthly Grid Electricity panel is partially filled. Enter all 12 months, or clear the panel and use the annual field.");
  }
  if (_monthlyAnyFilled(env.monthly_water_m3) && !_monthlyAllFilled(env.monthly_water_m3)) {
    errors.push("Monthly Water panel is partially filled. Enter all 12 months, or clear the panel and use the annual field.");
  }

  // ── Warnings from existing checkers (range + YoY) ──
  const w = checkEnvWaste(env).issues || [];
  const s = checkEnvScope(env).issues || [];
  warnings.push(...w, ...s);

  // ── Narrative fields (soft warnings only) ──
  if (!String(env.reduction_target || "").trim()) {
    warnings.push("No Reduction Target set, this is a strong recommendation for GRI 305-4 alignment.");
  }
  if (!String(env.climate_risk || "").trim()) {
    warnings.push("Climate Risk (TCFD) narrative is blank, the report will use a generic template.");
  }
  if (!String(env.initiatives || "").trim()) {
    warnings.push("Key Environmental Initiatives are blank, the report will be thin on qualitative narrative.");
  }

  return { errors, warnings };
}

function EnvCompletionGate({ env, onNext, onBack, saving }) {
  const { lang } = useLang();
  const [showBlock, setShowBlock] = useState(false);
  const [showWarn, setShowWarn] = useState(false);
  const { errors, warnings } = checkEnvComplete(env);
  const clean = errors.length === 0 && warnings.length === 0;

  const handleClick = () => {
    if (errors.length > 0) { setShowBlock(true); return; }
    if (warnings.length > 0) { setShowWarn(true); return; }
    onNext();
  };

  return (
    <>
      {/* Persistent status panel above the nav buttons */}
      <div className="mt-6" data-testid="env-completion-status">
        {clean && (
          <div className="rounded-xl border border-[#01AF9B]/40 bg-[#01AF9B]/10 p-4 flex items-start gap-3" data-testid="env-status-clean">
            <span className="text-[#01AF9B] text-lg font-bold">✓</span>
            <div>
              <div className="font-['Outfit'] font-bold text-[#01AF9B] text-sm">{lang === "ar" ? "قسم البيئة مكتمل ومتّسق" : "Environmental section complete & coherent"}</div>
              <div className="text-xs text-slate-300 mt-0.5">All meter inputs are provided, Scope 1/2/3 have been computed, and no coherence issues were detected. Safe to proceed.</div>
            </div>
          </div>
        )}
        {!clean && errors.length > 0 && (
          <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-4" data-testid="env-status-errors">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-red-400 text-lg font-bold">⚠</span>
              <div className="font-['Outfit'] font-bold text-red-300 text-sm">Fix these before continuing ({errors.length})</div>
            </div>
            <ul className="text-xs text-slate-200 space-y-1 pl-6 list-disc">
              {errors.map((m, i) => <li key={i} data-testid={`env-error-${i}`}>{m}</li>)}
            </ul>
          </div>
        )}
        {!clean && errors.length === 0 && warnings.length > 0 && (
          <div className="rounded-xl border border-[#F59E0B]/30 bg-[#F59E0B]/5 p-4" data-testid="env-status-warnings">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[#F59E0B] text-lg font-bold">⚠</span>
              <div className="font-['Outfit'] font-bold text-[#F59E0B] text-sm">{warnings.length} coherence note(s), please review</div>
            </div>
            <ul className="text-xs text-slate-300 space-y-1 pl-6 list-disc">
              {warnings.map((m, i) => <li key={i} data-testid={`env-warn-${i}`}>{m}</li>)}
            </ul>
          </div>
        )}
      </div>

      <div className="flex justify-between items-center mt-6 mb-4">
        <button onClick={onBack} className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm font-['Outfit'] font-medium" data-testid="nav-back">
          <ArrowLeft size={16} /> Back
        </button>
        <button onClick={handleClick} disabled={saving}
          className={`font-['Outfit'] font-semibold py-2.5 px-5 rounded-lg transition-all flex items-center gap-2 disabled:opacity-50 text-sm text-white ${errors.length > 0 ? "bg-red-500/80 hover:bg-red-500" : "bg-[#01AF9B] hover:bg-[#018F7E]"}`}
          data-testid="nav-next">
          {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <>{errors.length > 0 ? "Review issues" : "Next"} <ArrowRight size={16} /></>}
        </button>
      </div>

      {/* Blocking modal, hard errors */}
      {showBlock && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="env-block-modal">
          <div className="bg-[#111216] border border-red-500/40 rounded-2xl max-w-lg w-full p-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-red-400 text-2xl">⚠</span>
              <h3 className="font-['Outfit'] font-bold text-white text-lg">{lang === "ar" ? "قسم البيئة غير مكتمل" : "Environmental section incomplete"}</h3>
            </div>
            <p className="text-sm text-slate-400 mb-4">A credible ESG report cannot be generated without the following. Please fix and try again:</p>
            <ul className="text-sm text-slate-200 space-y-2 mb-5 pl-5 list-disc">
              {errors.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
            <button type="button" onClick={() => setShowBlock(false)}
              className="w-full bg-[#01AF9B] hover:bg-[#018F7E] text-white font-['Outfit'] font-semibold py-2.5 rounded-lg transition-all"
              data-testid="env-block-ok">
              OK, take me back
            </button>
          </div>
        </div>
      )}

      {/* Warnings modal, soft; allow user to proceed */}
      {showWarn && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="env-warn-modal">
          <div className="bg-[#111216] border border-[#F59E0B]/40 rounded-2xl max-w-lg w-full p-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[#F59E0B] text-2xl">⚠</span>
              <h3 className="font-['Outfit'] font-bold text-white text-lg">Coherence notes ({warnings.length})</h3>
            </div>
            <p className="text-sm text-slate-400 mb-4">These are not blockers, but the report will be stronger if you address them:</p>
            <ul className="text-sm text-slate-200 space-y-2 mb-5 pl-5 list-disc max-h-64 overflow-y-auto">
              {warnings.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
            <div className="flex gap-3">
              <button type="button" onClick={() => setShowWarn(false)}
                className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-100 font-['Outfit'] font-semibold py-2.5 rounded-lg transition-all"
                data-testid="env-warn-back">
                Let me fix them
              </button>
              <button type="button" onClick={() => { setShowWarn(false); onNext(); }}
                className="flex-1 bg-[#01AF9B] hover:bg-[#018F7E] text-white font-['Outfit'] font-semibold py-2.5 rounded-lg transition-all"
                data-testid="env-warn-continue">
                Continue anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────
// Social Completion Gate, same "bulletproof" pattern as Env.
// Splits findings into ERRORS (must-fix) and WARNINGS (advisory).
// Reuses the existing checkers (checkWorkforce, checkHiringTurnover,
// checkParentalLeave) and adds monthly-panel validation for headcount /
// LTI / working hours.
// ─────────────────────────────────────────────────────────────────
function checkSocialComplete(soc) {
  const errors = [];
  const warnings = [];
  const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  };

  // ── Required ──
  const emp = num(soc.total_employees);
  if (emp === null || emp <= 0) {
    errors.push("Total Employees is missing or zero, the entire Social section depends on this figure.");
  }
  const female = num(soc.female_pct);
  if (female === null) {
    warnings.push("Female Employees % is empty, diversity metrics will be incomplete.");
  }
  const training = num(soc.training_hours);
  if (training === null || training <= 0) {
    warnings.push("Training Hours per Employee is missing, the Training section will be thin.");
  }
  const twh = num(soc.total_working_hours);
  if (twh === null || twh <= 0) {
    errors.push("Total Working Hours is missing, LTIR cannot be computed without it.");
  }
  const lti = num(soc.lti_count);
  if (lti === null) {
    warnings.push("LTI incident count is empty, LTIR defaults to zero, which may misrepresent your safety record.");
  }

  // ── Monthly panels: all-or-none ──
  if (_monthlyAnyFilled(soc.monthly_headcount) && !_monthlyAllFilled(soc.monthly_headcount)) {
    errors.push("Monthly Headcount panel is partially filled. Enter all 12 months, or clear the panel and use the annual field.");
  }
  if (_monthlyAnyFilled(soc.monthly_lti_count) && !_monthlyAllFilled(soc.monthly_lti_count)) {
    errors.push("Monthly LTI incidents panel is partially filled. Enter all 12 months, or clear the panel and use the annual field.");
  }
  if (_monthlyAnyFilled(soc.monthly_working_hours) && !_monthlyAllFilled(soc.monthly_working_hours)) {
    errors.push("Monthly Working Hours panel is partially filled. Enter all 12 months, or clear the panel and use the annual field.");
  }

  // ── Warnings from existing checkers ──
  warnings.push(...(checkWorkforce(soc).issues || []));
  warnings.push(...(checkHiringTurnover(soc).issues || []));
  warnings.push(...(checkParentalLeave(soc).issues || []));

  // ── LTIR sanity: if lti > 0 and twh > 0, verify auto-derived matches manual override within tolerance ──
  if (Number.isFinite(lti) && Number.isFinite(twh) && twh > 0 && lti >= 0) {
    const expected = (lti * 1_000_000) / twh;
    const declared = num(soc.injury_rate);
    if (declared !== null && Math.abs(declared - expected) > Math.max(0.5, expected * 0.15)) {
      warnings.push(`Declared LTIR (${declared}) does not match the formula ${lti} incidents × 1,000,000 / ${twh.toLocaleString()} hours = ${expected.toFixed(2)}. Toggle Auto on the LTIR chip if this is unintentional.`);
    }
  }

  // ── Narrative fields (soft) ──
  if (!String(soc.wellbeing || "").trim()) warnings.push("Wellbeing Programs description is blank, the report will use a generic template.");
  if (!String(soc.hr_policies || "").trim()) warnings.push("HR Policies description is blank, the report will use a generic template.");

  return { errors, warnings };
}

function SocialCompletionGate({ soc, onNext, onBack, saving }) {
  const { lang } = useLang();
  const [showBlock, setShowBlock] = useState(false);
  const [showWarn, setShowWarn] = useState(false);
  const { errors, warnings } = checkSocialComplete(soc);
  const clean = errors.length === 0 && warnings.length === 0;

  const handleClick = () => {
    if (errors.length > 0) { setShowBlock(true); return; }
    if (warnings.length > 0) { setShowWarn(true); return; }
    onNext();
  };

  return (
    <>
      <div className="mt-6" data-testid="soc-completion-status">
        {clean && (
          <div className="rounded-xl border border-[#B94B05]/40 bg-[#B94B05]/10 p-4 flex items-start gap-3" data-testid="soc-status-clean">
            <span className="text-[#B94B05] text-lg font-bold">✓</span>
            <div>
              <div className="font-['Outfit'] font-bold text-[#B94B05] text-sm">{lang === "ar" ? "القسم الاجتماعي مكتمل ومتّسق" : "Social section complete & coherent"}</div>
              <div className="text-xs text-slate-300 mt-0.5">{lang === "ar" ? "جميع مُدخلات العدد وLTI وساعات العمل متوفرة. تم حساب LTIR. يمكن المتابعة." : "All headcount, LTI and working-hour inputs are present. LTIR is computed. Safe to proceed."}</div>
            </div>
          </div>
        )}
        {!clean && errors.length > 0 && (
          <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-4" data-testid="soc-status-errors">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-red-400 text-lg font-bold">⚠</span>
              <div className="font-['Outfit'] font-bold text-red-300 text-sm">Fix these before continuing ({errors.length})</div>
            </div>
            <ul className="text-xs text-slate-200 space-y-1 pl-6 list-disc">
              {errors.map((m, i) => <li key={i} data-testid={`soc-error-${i}`}>{m}</li>)}
            </ul>
          </div>
        )}
        {!clean && errors.length === 0 && warnings.length > 0 && (
          <div className="rounded-xl border border-[#F59E0B]/30 bg-[#F59E0B]/5 p-4" data-testid="soc-status-warnings">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[#F59E0B] text-lg font-bold">⚠</span>
              <div className="font-['Outfit'] font-bold text-[#F59E0B] text-sm">{warnings.length} coherence note(s), please review</div>
            </div>
            <ul className="text-xs text-slate-300 space-y-1 pl-6 list-disc">
              {warnings.map((m, i) => <li key={i} data-testid={`soc-warn-${i}`}>{m}</li>)}
            </ul>
          </div>
        )}
      </div>

      <div className="flex justify-between items-center mt-6 mb-4">
        <button onClick={onBack} className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm font-['Outfit'] font-medium" data-testid="nav-back">
          <ArrowLeft size={16} /> Back
        </button>
        <button onClick={handleClick} disabled={saving}
          className={`font-['Outfit'] font-semibold py-2.5 px-5 rounded-lg transition-all flex items-center gap-2 disabled:opacity-50 text-sm text-white ${errors.length > 0 ? "bg-red-500/80 hover:bg-red-500" : "bg-[#B94B05] hover:bg-[#8B3804]"}`}
          data-testid="nav-next">
          {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <>{errors.length > 0 ? "Review issues" : "Next"} <ArrowRight size={16} /></>}
        </button>
      </div>

      {showBlock && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="soc-block-modal">
          <div className="bg-[#111216] border border-red-500/40 rounded-2xl max-w-lg w-full p-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-red-400 text-2xl">⚠</span>
              <h3 className="font-['Outfit'] font-bold text-white text-lg">{lang === "ar" ? "القسم الاجتماعي غير مكتمل" : "Social section incomplete"}</h3>
            </div>
            <p className="text-sm text-slate-400 mb-4">A credible ESG report cannot be generated without the following. Please fix and try again:</p>
            <ul className="text-sm text-slate-200 space-y-2 mb-5 pl-5 list-disc">
              {errors.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
            <button type="button" onClick={() => setShowBlock(false)}
              className="w-full bg-[#B94B05] hover:bg-[#8B3804] text-white font-['Outfit'] font-semibold py-2.5 rounded-lg transition-all"
              data-testid="soc-block-ok">
              OK, take me back
            </button>
          </div>
        </div>
      )}

      {showWarn && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="soc-warn-modal">
          <div className="bg-[#111216] border border-[#F59E0B]/40 rounded-2xl max-w-lg w-full p-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[#F59E0B] text-2xl">⚠</span>
              <h3 className="font-['Outfit'] font-bold text-white text-lg">Coherence notes ({warnings.length})</h3>
            </div>
            <p className="text-sm text-slate-400 mb-4">These are not blockers, but the report will be stronger if you address them:</p>
            <ul className="text-sm text-slate-200 space-y-2 mb-5 pl-5 list-disc max-h-64 overflow-y-auto">
              {warnings.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
            <div className="flex gap-3">
              <button type="button" onClick={() => setShowWarn(false)}
                className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-100 font-['Outfit'] font-semibold py-2.5 rounded-lg transition-all"
                data-testid="soc-warn-back">
                Let me fix them
              </button>
              <button type="button" onClick={() => { setShowWarn(false); onNext(); }}
                className="flex-1 bg-[#B94B05] hover:bg-[#8B3804] text-white font-['Outfit'] font-semibold py-2.5 rounded-lg transition-all"
                data-testid="soc-warn-continue">
                Continue anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}








function CalcBox({ title, children }) {
  return (
    <div className="mt-3 bg-[#0B0C10] border border-slate-700 rounded-lg p-4">
      <div className="font-['Outfit'] text-[10px] font-bold tracking-[0.08em] uppercase text-[#01AF9B] mb-3">{title}</div>
      {children}
    </div>
  );
}

function CalcField({ label, children }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[9px] font-['Outfit'] font-semibold uppercase text-slate-500 tracking-wide">{label}</label>
      {children}
    </div>
  );
}

function CalcInput({ value, onChange, placeholder }) {
  return (
    <input type="number" min="0" value={value} onChange={e => onChange(e.target.value)}
      className="w-full bg-[#111216] border border-slate-700 rounded-md text-slate-100 text-xs px-2.5 py-2 focus:ring-1 focus:ring-[#01AF9B] outline-none"
      placeholder={placeholder} />
  );
}

function NavButtons({ onBack, onNext, saving, lang = "en" }) {
  return (
    <div className="flex justify-between items-center mt-6 mb-4">
      {onBack ? (
        <button onClick={onBack} className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm font-['Outfit'] font-medium" data-testid="nav-back">
          <ArrowLeft size={16} /> {lang === "ar" ? "رجوع" : "Back"}
        </button>
      ) : <div />}
      {onNext && (
        <button onClick={onNext} disabled={saving}
          className="bg-[#01AF9B] hover:bg-[#018F7E] text-white font-['Outfit'] font-semibold py-2.5 px-5 rounded-lg transition-all flex items-center gap-2 disabled:opacity-50 text-sm"
          data-testid="nav-next">
          {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <>{lang === "ar" ? "التالي" : "Next"} <ArrowRight size={16} /></>}
        </button>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// CoherenceBadge, SERVER-DRIVEN pre-flight input reconciliation.
//
// Feb 2026 rewrite: previously this component ran only a client-side
// subset of the coherence rules, so the review page could say
// "ready to generate" while the backend still had objections. Now we
// call the authoritative POST /api/reports/{id}/validate endpoint and
// render whatever the server tells us. No divergence possible.
//
// Behaviour:
//   1. On mount + whenever env/soc/gov change (debounced 400ms), we
//      save the report and hit /validate. While that's in flight we
//      show a subtle spinner so the user never sees a stale green
//      badge next to inputs they just changed.
//   2. severity === "error"  → blocks generation (parent listens on
//      the "coherence:blocked" window event).
//   3. severity === "warning" → shows as advisory, non-blocking.
//   4. If /validate returns 0 issues, we show the green "ready to
//      generate" badge, but ONLY after a real server response, never
//      just because the client math added up.
// ═══════════════════════════════════════════════════════════════════
function CoherenceBadge({ env, soc, gov, reportId, saveReport, onJumpToStep }) {
  const { lang } = useLang();
  const [state, setState] = React.useState({
    loading: true,
    checked: false,
    hardErrors: [],
    softWarnings: [],
    error: null,
  });

  // Keep the latest saveReport in a ref so the debounced effect can call it
  // without listing it in the deps array, the parent recreates the function
  // on every render, and listing it here would trigger an infinite validate
  // loop (which is exactly the "flashing 'Checking your inputs…'" bug the
  // user reported after the last deploy).
  const saveReportRef = React.useRef(saveReport);
  React.useEffect(() => {
    saveReportRef.current = saveReport;
  }, [saveReport]);

  // Debounced re-validation whenever inputs change. We save first (so
  // the server sees exactly what the user just typed) then validate.
  React.useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      if (cancelled) return;
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        let rid = reportId;
        const doSave = saveReportRef.current;
        if (doSave) {
          const saved = await doSave({ silent: true });
          // saveReport may return the new id if the report was just created
          rid = (saved && saved._id) || rid;
        }
        if (!rid) {
          if (!cancelled) {
            setState({
              loading: false,
              checked: true,
              hardErrors: [],
              softWarnings: [],
              error: null,
            });
          }
          return;
        }
        const res = await api.validateReport(rid);
        if (cancelled) return;
        const issues = res?.issues || [];
        // Map server field -> which wizard step the user should jump to.
        const stepForField = (field = "") => {
          const f = String(field).toLowerCase();
          if (
            f.includes("scope") || f.includes("energy") || f.includes("water") ||
            f.includes("waste") || f.includes("landfill") || f.includes("emission") ||
            f.includes("ghg") || f.includes("renewable") || f.includes("diesel") ||
            f.includes("electricity") || f.includes("environment")
          ) return 1;
          if (
            f.includes("employee") || f.includes("headcount") || f.includes("hire") ||
            f.includes("depart") || f.includes("turnover") || f.includes("gender") ||
            f.includes("female") || f.includes("male") || f.includes("training") ||
            f.includes("ltir") || f.includes("injury") || f.includes("parental") ||
            f.includes("social") || f.includes("csr")
          ) return 2;
          if (
            f.includes("board") || f.includes("director") || f.includes("govern") ||
            f.includes("compliance") || f.includes("committee") || f.includes("abac")
          ) return 3;
          return 0;
        };
        const hardErrors = issues
          .filter((i) => i.severity === "error")
          .map((i) => ({
            field: i.field || "Field",
            detail: i.message || "",
            step: stepForField(i.field || i.message),
          }));
        const softWarnings = issues
          .filter((i) => i.severity === "warning")
          .map((i) => ({
            field: i.field || "Field",
            detail: i.message || "",
            step: stepForField(i.field || i.message),
          }));
        setState({
          loading: false,
          checked: true,
          hardErrors,
          softWarnings,
          error: null,
        });
      } catch (e) {
        if (cancelled) return;
        console.warn("CoherenceBadge /validate failed:", e);
        setState({
          loading: false,
          checked: true,
          hardErrors: [],
          softWarnings: [],
          error: "Could not reach the server to check your inputs. Generation will re-check when you click the button.",
        });
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // Intentionally NOT including `saveReport` here, we use a ref
    // instead (see saveReportRef above). Including it would cause the
    // effect to re-fire on every parent render (the parent recreates
    // the function each render), producing the perpetual "Checking
    // your inputs…" spinner the user reported.
  }, [
    // Stringifying is fine here, the entire form is at most a few kB.
    JSON.stringify(env),
    JSON.stringify(soc),
    JSON.stringify(gov),
    reportId,
  ]);

  const { loading, checked, hardErrors, softWarnings, error } = state;

  // Broadcast the blocking status so the parent's Generate button can
  // disable itself. Blocking on `loading` too, we never want the user
  // to hit Generate while we still don't know the answer.
  React.useEffect(() => {
    const evt = new CustomEvent("coherence:blocked", {
      detail: { blocked: loading || hardErrors.length > 0 },
    });
    window.dispatchEvent(evt);
  }, [loading, hardErrors.length]);

  const stepName = (n) => (n === 1 ? "Environmental" : n === 2 ? "Social" : n === 3 ? "Governance" : "Company");

  // Loading state, subtle spinner, never a green "all good".
  if (loading || !checked) {
    return (
      <div className="mb-5 flex items-center justify-center gap-2 rounded-lg border border-slate-700 bg-slate-800/30 px-4 py-3"
        data-testid="coherence-badge-loading">
        <div className="w-3.5 h-3.5 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
        <span className="text-slate-300 text-sm font-['Outfit'] font-semibold">
          Checking your inputs…
        </span>
      </div>
    );
  }

  // Server unreachable, still let them try Generate (the server-side
  // gate on /generate remains the source of truth).
  if (error) {
    return (
      <div className="mb-5 flex items-start gap-2 rounded-lg border border-slate-600 bg-slate-800/40 px-4 py-3"
        data-testid="coherence-badge-network-error">
        <span className="text-slate-400 text-lg leading-none">?</span>
        <span className="text-slate-300 text-xs font-['Outfit']">{error}</span>
      </div>
    );
  }

  if (hardErrors.length === 0 && softWarnings.length === 0) {
    return (
      <div className="mb-5 flex items-center justify-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-4 py-3"
        data-testid="coherence-badge-ok">
        <span className="text-emerald-400 text-lg leading-none">✓</span>
        <span className="text-emerald-300 text-sm font-['Outfit'] font-semibold">
          {lang === "ar" ? "اجتاز فحص اتساق المدخلات، جاهز للإنشاء" : "Input coherence check passed, ready to generate"}
        </span>
      </div>
    );
  }

  return (
    <div className="mb-5 rounded-lg border text-left overflow-hidden"
      style={{ borderColor: hardErrors.length ? "rgba(239,68,68,0.35)" : "rgba(245,158,11,0.35)",
               background: hardErrors.length ? "rgba(239,68,68,0.05)" : "rgba(245,158,11,0.05)" }}
      data-testid={hardErrors.length ? "coherence-badge-error" : "coherence-badge-warning"}>
      <div className="px-4 py-3 border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
        <div className="flex items-center gap-2">
          <span className="text-lg leading-none" style={{ color: hardErrors.length ? "#f87171" : "#fbbf24" }}>
            {hardErrors.length ? "⚠" : "ℹ"}
          </span>
          <span className="text-sm font-['Outfit'] font-bold" style={{ color: hardErrors.length ? "#fca5a5" : "#fcd34d" }}>
            {hardErrors.length > 0
              ? `${hardErrors.length} input contradiction${hardErrors.length > 1 ? "s" : ""} must be fixed before generating`
              : `${softWarnings.length} advisory notice${softWarnings.length > 1 ? "s" : ""} (non-blocking)`}
          </span>
        </div>
      </div>
      <ul className="px-4 py-3 space-y-2">
        {hardErrors.map((e, i) => (
          <li key={`h${i}`} className="flex items-start gap-2 text-xs" data-testid={`coherence-err-${i}`}>
            <span className="text-red-400 mt-0.5">•</span>
            <div className="flex-1">
              <span className="text-red-200 font-semibold">{e.field}:</span>{" "}
              <span className="text-slate-300">{e.detail}</span>
              {e.step > 0 && (
                <button type="button" onClick={() => onJumpToStep && onJumpToStep(e.step)}
                  className="ml-2 text-red-300 underline hover:text-red-100 font-['Outfit'] font-semibold"
                  data-testid={`coherence-jump-${i}`}>
                  Fix in {stepName(e.step)} →
                </button>
              )}
            </div>
          </li>
        ))}
        {softWarnings.map((w, i) => (
          <li key={`w${i}`} className="flex items-start gap-2 text-xs" data-testid={`coherence-warn-${i}`}>
            <span className="text-amber-400 mt-0.5">•</span>
            <div className="flex-1">
              <span className="text-amber-200 font-semibold">{w.field}:</span>{" "}
              <span className="text-slate-400">{w.detail}</span>
              {w.step > 0 && (
                <button type="button" onClick={() => onJumpToStep && onJumpToStep(w.step)}
                  className="ml-2 text-amber-300 underline hover:text-amber-100 font-['Outfit'] font-semibold">
                  Review in {stepName(w.step)} →
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ScoreRow({ label, score, color }) {
  return (
    <div className="mb-3">
      <div className="flex justify-between text-sm mb-1.5">
        <span className="text-slate-400">{label}</span>
        <span className="font-['Outfit'] font-bold" style={{ color }}>{score}%</span>
      </div>
      <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${score}%`, background: `linear-gradient(90deg, ${color}, ${color}88)` }} />
      </div>
    </div>
  );
}

// Compact live meter shown on every ReportBuilder step so users always see
// how sparse or rich each pillar is while they fill the form. Uses the same
// calcScore() helper as the final "Ready to Generate" summary, so numbers
// stay consistent from step 0 through the submit gate.
function CoverageChip({ label, score, color }) {
  return (
    <div className="flex items-center gap-1.5" data-testid={`live-meter-${label.toLowerCase()}`}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
      <span className="text-slate-400">{label}</span>
      <span className="font-['Outfit'] font-bold" style={{ color }}>{score}%</span>
    </div>
  );
}

function LiveCoverageMeter({ env, soc, gov, lang = "en", t = (k) => k }) {
  const eScore = calcScore(env, 'e');
  const sScore = calcScore(soc, 's');
  const gScore = calcScore(gov, 'g');
  const overall = Math.round((eScore + sScore + gScore) / 3);
  const thin = [eScore, sScore, gScore].filter(v => v < 40).length;
  const overallColor = overall >= 70 ? '#01AF9B' : overall >= 40 ? '#F59E0B' : '#EF4444';
  return (
    <div
      className="bg-[#111216] border border-slate-800/60 rounded-xl px-4 py-3 mb-5 flex items-center flex-wrap gap-x-5 gap-y-2"
      data-testid="live-coverage-meter"
      title={t('richness_tooltip')}
    >
      <div className="flex items-center gap-2">
        <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{t('report_richness')}</span>
        <span className="font-['Outfit'] font-bold text-lg leading-none" style={{ color: overallColor }}>{formatPct(overall, lang)}</span>
      </div>
      <div className="h-4 w-px bg-slate-800" />
      <div className="flex items-center gap-4 text-xs">
        <CoverageChip label="E" score={eScore} color="#01AF9B" lang={lang} />
        <CoverageChip label="S" score={sScore} color="#B94B05" lang={lang} />
        <CoverageChip label="G" score={gScore} color="#5B7BB0" lang={lang} />
      </div>
      {thin > 0 && (
        <span className="text-[11px] text-orange-400/90 ms-auto">
          {formatNum(thin, lang)} {thin > 1 ? t('pillars_below_40_many') : t('pillars_below_40_one')}{t('add_more_data')}
        </span>
      )}
    </div>
  );
}

// Smart nudges — up to 3 unfilled high-impact fields per pillar that drops
// below 70%. Clicking a chip jumps to the owning step and scrolls to the
// field's testId, giving users a direct "coach" without hunting through the
// form. Bilingual (EN / AR) via the field map below.
const NUDGE_MAP = {
  e: {
    step: 1, color: "#01AF9B",
    pillarEN: "Environment", pillarAR: "البيئة",
    priority: [
      { f: "electricity_kwh",   testId: "input-electricity-kwh",   en: "Grid electricity (kWh)",       ar: "كهرباء الشبكة (ك.و.س)" },
      { f: "water",             testId: "input-water",             en: "Water consumption",             ar: "استهلاك المياه" },
      { f: "waste",             testId: "input-waste",             en: "Total waste",                   ar: "إجمالي النفايات" },
      { f: "renewable_pct",     testId: "input-renewable-electricity-claimed", en: "Renewable share",   ar: "نسبة الطاقة المتجدّدة" },
      { f: "reduction_target",  testId: "input-target",            en: "Reduction Target",              ar: "هدف التخفيض" },
      { f: "climate_risk",      testId: "input-climate",           en: "Climate Risk Assessment",       ar: "تقييم مخاطر المناخ" },
      { f: "initiatives",       testId: "input-initiatives",       en: "Environmental Initiatives",     ar: "المبادرات البيئية" },
    ],
  },
  s: {
    step: 2, color: "#B94B05",
    pillarEN: "Social", pillarAR: "الاجتماعي",
    priority: [
      { f: "total_employees",       testId: "input-employees",            en: "Total Employees",             ar: "إجمالي الموظفين" },
      { f: "female_pct",            testId: "input-female-pct",           en: "Female Employees %",          ar: "٪ الموظفات" },
      { f: "training_hours",        testId: "input-training",             en: "Training Hours per Employee", ar: "ساعات التدريب لكل موظف" },
      { f: "new_hires",             testId: "input-new-hires-total",      en: "New Hires",                   ar: "التوظيف الجديد" },
      { f: "departures",            testId: "input-departures-total",     en: "Departures",                  ar: "المغادرون" },
      { f: "csr_spend",             testId: "input-csr",                  en: "Community Projects",          ar: "المشاريع المجتمعية" },
    ],
  },
  g: {
    step: 3, color: "#5B7BB0",
    pillarEN: "Governance", pillarAR: "الحوكمة",
    priority: [
      { f: "board_size",       testId: "input-board-size",      en: "Board Size",             ar: "حجم المجلس" },
    ],
  },
};
function SmartNudges({ env, soc, gov, lang = "en", t = (k) => k, setStep }) {
  const data = { e: env, s: soc, g: gov };
  const empty = (v) => v === undefined || v === null || v === "" || v === 0 || v === "0";
  const nudges = [];
  for (const key of ["e", "s", "g"]) {
    const score = calcScore(data[key], key);
    if (score >= 70) continue;
    const missing = NUDGE_MAP[key].priority.filter((p) => empty(data[key][p.f])).slice(0, 3);
    if (!missing.length) continue;
    nudges.push({ key, cfg: NUDGE_MAP[key], score, missing });
  }
  if (!nudges.length) return null;

  const jumpTo = (step, testId) => {
    setStep(step);
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="${testId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        const input = (el.tagName === "INPUT" || el.tagName === "TEXTAREA") ? el : el.querySelector("input, textarea");
        input && input.focus && input.focus();
      }
    }, 250);
  };

  return (
    <div
      className="bg-gradient-to-br from-[#01AF9B]/5 via-[#111216] to-[#111216] border border-[#01AF9B]/20 rounded-xl px-4 py-3 mb-5"
      data-testid="smart-nudges"
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[#01AF9B] text-sm">✨</span>
        <span className="font-['Outfit'] font-bold text-sm text-slate-200">{t('nudge_title')}</span>
      </div>
      <div className="flex flex-col gap-2.5">
        {nudges.map((n) => (
          <div key={n.key} className="flex items-start gap-2 flex-wrap">
            <span className="text-[11px] text-slate-400 mt-1.5 shrink-0">
              {t('nudge_fill_these')} <strong style={{ color: n.cfg.color }}>{lang === "ar" ? n.cfg.pillarAR : n.cfg.pillarEN}</strong> :
            </span>
            {n.missing.map((m) => (
              <button
                key={m.f}
                type="button"
                onClick={() => jumpTo(n.cfg.step, m.testId)}
                data-testid={`nudge-${n.key}-${m.f}`}
                className="px-2.5 py-1 rounded-full text-[11px] font-['Outfit'] font-semibold border transition-all"
                style={{ borderColor: `${n.cfg.color}66`, background: `${n.cfg.color}18`, color: n.cfg.color }}
                onMouseEnter={(e) => { e.currentTarget.style.background = `${n.cfg.color}33`; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = `${n.cfg.color}18`; }}
              >
                {lang === "ar" ? m.ar : m.en} →
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}


function calcScore(data, type) {
  const eFields = ['scope1', 'scope2', 'energy', 'renewable_pct', 'water', 'waste', 'reduction_target', 'climate_risk', 'initiatives'];
  const sFields = ['total_employees', 'female_pct', 'local_pct', 'training_hours', 'injury_rate', 'csr_spend', 'supply_chain', 'wellbeing', 'hr_policies',
    'senior_mgmt_male', 'senior_mgmt_female', 'age_under30', 'new_hires_male', 'turnover_rate',
    'performance_review_pct', 'total_working_hours', 'human_rights_policy', 'child_labor_policy', 'grievance_mechanism'];
  const gFields = ['board_size', 'board_female_pct', 'independent_pct', 'anti_corruption', 'whistleblower', 'data_protection', 'code_of_conduct', 'esg_committee', 'improvement_plan',
    'board_members', 'ceo_chair_separation', 'board_committees', 'board_meeting_frequency',
    'abac_training_pct', 'compliance_incidents', 'supplier_code_of_conduct', 'conflicts_of_interest_policy'];
  const fields = type === 'e' ? eFields : type === 's' ? sFields : gFields;
  const filled = fields.filter(f => data[f] && data[f] !== "" && data[f] !== 0).length;
  return Math.round((filled / fields.length) * 100);
}

function LocalPctHint({ pct, total, lang }) {
  const p = parseFloat(pct), te = parseFloat(total);
  if (!(p > 0 && p <= 100 && te > 0)) return null;
  const ppl = te * p / 100;
  if (Math.abs(ppl - Math.round(ppl)) <= 0.01) return null;
  const n = Math.max(1, Math.round(ppl));
  const fixed = (n / te * 100).toFixed(1).replace(/\.0$/, "");
  return (
    <div className="text-[10px] text-[#B45309] mt-1" data-testid="local-pct-hint">
      {lang === "ar"
        ? `${p}% من ${te} موظفاً = ${ppl.toFixed(1)} شخص. سيعرض التقرير ${n} من ${te} (${fixed}%).`
        : `${p}% of ${te} employees = ${ppl.toFixed(1)} people. The report will show ${n} of ${te} (${fixed}%).`}
    </div>
  );
}
